import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Table, Spin, Alert, Button, Space, Typography, Tag, Layout, Menu, Tooltip, Modal, message, Switch, Select } from 'antd';
import { LineChartOutlined, MinusOutlined, ProfileOutlined, SettingOutlined } from '@ant-design/icons'; // Added SettingOutlined
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useAuth } from '../context/AuthContext';
import MomentDatePicker from './MomentDatePicker.tsx';
// Import local utils
import { getCategoryForItem, categoryDisplayOrder, flowsheetItemCategories } from '../utils/flowsheetCategories';
import { labKeyToDisplayNameMap } from '../utils/labParameterMappings';
import { standardizedToDisplayName, isStandardizedLabParameter, displayNameToStandardized, getCategoryFromStandardizedName } from '../utils/labParameterStandardization';
import { buildParameterRegistry } from '../utils/parameterSearchUtils';
import { getParameterDisplayName } from '../utils/parameterDisplayNames';
import { physicalExamSectionsFull } from '../utils/physicalExamParametersFull';
import { biometricCategories, getAllBiometricParameters } from '../utils/biometricParameters';
import { lifestyleCategories, getAllLifestyleParameters, healthLikertScaleOptions, activityDaysOptions, incidentalWalkingTimeOptions, sedentaryHoursOptions, sittingBreakDaysOptions } from '../utils/lifestyleCategories';
import { formatLifestyleValueForDisplay, isLifestyleParameter, getDisplayOptionsForScreenSize } from '../utils/lifestyleValueDisplay';

import { imagingParameters } from '../utils/imagingParameters';
import '../utils/checkYBTParameters'; // Import YBT parameter checker for console access
import '../utils/addSampleYBTData'; // Import YBT sample data utility for testing
import '../utils/debugVietnameseYBT'; // Import Vietnamese YBT debug utility
import '../utils/testYBTFix'; // Import YBT fix tester
import { getLabValueStatusWithFlag, getLabValueStyle } from '../utils/labRangeUtils';
import useHealthSnapshotData from '../hooks/useHealthSnapshotData';
// Import Component Forms
import FlowsheetDataManagementPanel from './FlowsheetDataManagementPanel'; // Added
import LabPdfImportModal from './LabPdfImportModal';
import CarePlanningDataModal from './CarePlanningDataModal'; // Import the new modal
import ParameterSearchInput from './ParameterSearchInput'; // Import search component
import EnhancedAddDataModal from './EnhancedAddDataModal'; // Import enhanced add data modal
import TrendModal from './TrendModal'; // Import the new TrendModal component
import VitalsAddModal from './VitalsAddModal'; // Import vitals add modal
import BalanceAddModal from './BalanceAddModal';
import BiomeasurementAddModal from './BiomeasurementAddModal'; // Import balance add modal
import CognitionAddModal from './CognitionAddModal'; // Import cognition add modal
import EnduranceAddModal from './EnduranceAddModal'; // Import endurance add modal
import FlexibilityAddModal from './FlexibilityAddModal'; // Import flexibility add modal
import PowerAddModal from './PowerAddModal'; // Import power add modal
import PulmonaryAddModal from './PulmonaryAddModal'; // Import pulmonary add modal
import StrengthAddModal from './StrengthAddModal'; // Import strength add modal
import VisionAddModal from './VisionAddModal'; // Import vision add modal
import ImagingPdfUpload from './ImagingPdfUpload'; // Import imaging PDF upload component
import LifestyleParameterDetailModal from './LifestyleParameterDetailModal'; // Import lifestyle parameter detail modal

const { SubMenu } = Menu;
const { RangePicker } = MomentDatePicker;
const { Sider, Content } = Layout;
const { Text } = Typography;
const { Option } = Select;

// Helper to format date/time for table display
const formatDateTime = (dateString) => {
    return dateString ? moment(dateString).format('YYYY-MM-DD HH:mm') : 'N/A';
};

// Define constant sets derived from imports
const biometricSubCategoryKeys = new Set(biometricCategories.map(cat => cat.key));
const lifestyleSubCategoryKeys = new Set(lifestyleCategories.map(cat => cat.key));

// DEPRECATED: Replaced with centralized getParameterDisplayName utility
// This mapping is no longer used - all display names now come from parameterDisplayNames.js
// which reads from parameters.md as the single source of truth
const knownLabSubCategoryTitles = new Set([
    'CBC', 'Hepatology', 'Renal', 'Electrolytes', 'Glucose', 'Lipid', 'Thyroid', /* 'Urinalysis', */ 'Coagulation', 'Cardiac', 'Protein', 'Vitamins', 'Inflammation', 'Urine',
]);
const physicalExamSubCategoriesSet = new Set(['PE Summaries', ...physicalExamSectionsFull.map(s => s.title).filter(t => t)]);

// Define Balance category display order - deviation percentages and performance ratio first
const balanceDisplayOrder = [
  'YBT Composite Deviation',
  'Y-Balance Test Performance Ratio',
  'Y-Balance Test - Anterior Deviation',
  'Y-Balance Test - Posteromedial Deviation',
  'Y-Balance Test - Posterolateral Deviation',
  'Y-Balance Test - Right Composite Reach',
  'Y-Balance Test - Left Composite Reach',
  'Y Balance Test - Left Anterior Average',
  'Y Balance Test - Right Anterior Average',
  'Y Balance Test - Left Posteromedial Average',
  'Y Balance Test - Right Posteromedial Average',
  'Y Balance Test - Left Posterolateral Average',
  'Y Balance Test - Right Posterolateral Average'
];

// --- START: Calculation Helper Functions (moved from backend) ---
const calculateBMI = (weightKg, heightCm) => {
    if (weightKg === null || weightKg === undefined || heightCm === null || heightCm === undefined || isNaN(parseFloat(weightKg)) || isNaN(parseFloat(heightCm)) || parseFloat(heightCm) <= 0) return null;
    const heightM = parseFloat(heightCm) / 100;
    return parseFloat(weightKg) / (heightM * heightM);
};

const calculateREE = (weightKg, heightCm, ageYears, sex) => {
    if (weightKg === null || isNaN(parseFloat(weightKg)) || heightCm === null || isNaN(parseFloat(heightCm)) || ageYears === null || isNaN(parseInt(ageYears)) || !sex || (sex.toLowerCase() !== 'male' && sex.toLowerCase() !== 'female')) return null;
    const weight = parseFloat(weightKg);
    const height = parseFloat(heightCm);
    const age = parseInt(ageYears);
    if (sex.toLowerCase() === 'male') return (10 * weight) + (6.25 * height) - (5 * age) + 5;
    else return (10 * weight) + (6.25 * height) - (5 * age) - 161;
};

const likertDaysMap = { "never": 0, "rarely": 1.5, "sometimes": 3.5, "often": 5.5, "always": 7 };
const incidentalMinutesMap = { "<15": 7.5, "15-29": 22, "30-44": 37, "45-59": 52, ">60": 75 };

const calculateTEE = (ree, moderateActivityDaysResponse, vigorousActivityDaysResponse, incidentalWalkingTimeResponse, sedentaryHoursResponse) => {
    if (ree === null || isNaN(parseFloat(ree)) || moderateActivityDaysResponse === null || moderateActivityDaysResponse === undefined || vigorousActivityDaysResponse === null || vigorousActivityDaysResponse === undefined || incidentalWalkingTimeResponse === null || incidentalWalkingTimeResponse === undefined || sedentaryHoursResponse === null || sedentaryHoursResponse === undefined) return null;
    const modDays = likertDaysMap[moderateActivityDaysResponse] !== undefined ? likertDaysMap[moderateActivityDaysResponse] : 0;
    const vigDays = likertDaysMap[vigorousActivityDaysResponse] !== undefined ? likertDaysMap[vigorousActivityDaysResponse] : 0;
    const incMinDay = incidentalMinutesMap[incidentalWalkingTimeResponse] !== undefined ? incidentalMinutesMap[incidentalWalkingTimeResponse] : 0;
    const sedentaryHoursValue = parseFloat(sedentaryHoursResponse);
    const modMin = modDays * 30;
    const vigMin = vigDays * 30 * 2; 
    const incMin = incMinDay * 7; 
    const totalModEquivMin = modMin + vigMin + incMin;
    let pal = 1.20; 
    if (totalModEquivMin >= 150 && totalModEquivMin <= 300) pal = 1.375; 
    else if (totalModEquivMin > 300 && totalModEquivMin <= 450) pal = 1.55; 
    else if (totalModEquivMin > 450) pal = 1.725; 
    if (!isNaN(sedentaryHoursValue)) {
        if (sedentaryHoursValue > 8) pal -= 0.1; 
        else if (sedentaryHoursValue < 4) pal += 0.1; 
        pal = Math.max(1.20, pal); 
    }
    return parseFloat(ree) * pal;
};

const calculateMAP = (sbp, dbp) => {
    if (sbp === null || sbp === undefined || dbp === null || dbp === undefined || isNaN(parseFloat(sbp)) || isNaN(parseFloat(dbp))) return null;
    const sbpNum = parseFloat(sbp); // Ensure conversion to number
    const dbpNum = parseFloat(dbp); // Ensure conversion to number
    return (2 * dbpNum + sbpNum) / 3;
};

const calculatePP = (sbp, dbp) => {
    if (sbp === null || sbp === undefined || dbp === null || dbp === undefined || isNaN(parseFloat(sbp)) || isNaN(parseFloat(dbp))) return null;
    const sbpNum = parseFloat(sbp); // Ensure conversion to number
    const dbpNum = parseFloat(dbp); // Ensure conversion to number
    return sbpNum - dbpNum;
};

const calculateSIRSScore = (temperature, heartRate, respiratoryRate, wbc) => {
    let score = 0;
    let vitalCriteriaMet = 0; 
    let wbcConsidered = false;

    if (temperature !== null && temperature !== undefined && !isNaN(parseFloat(temperature))) {
        if (parseFloat(temperature) > 38 || parseFloat(temperature) < 36) score++;
        vitalCriteriaMet++;
    }
    if (heartRate !== null && heartRate !== undefined && !isNaN(parseFloat(heartRate))) {
        if (parseFloat(heartRate) > 90) score++;
        vitalCriteriaMet++;
    }
    if (respiratoryRate !== null && respiratoryRate !== undefined && !isNaN(parseFloat(respiratoryRate))) {
        if (parseFloat(respiratoryRate) > 20) score++;
        vitalCriteriaMet++;
    }
    if (wbc !== null && wbc !== undefined && !isNaN(parseFloat(wbc))) {
        if (parseFloat(wbc) > 12 || parseFloat(wbc) < 4) {
            score++;
        }
        wbcConsidered = true; 
    }
    
    // Return null if not enough primary vitals are present, or if only WBC is present without other vitals.
    if (vitalCriteriaMet < 2 && !(vitalCriteriaMet === 1 && wbcConsidered)) {
        return null; 
    }
    return score;
};

const calculateVO2max = (hrMax, hrRest) => {
    if (hrMax === null || hrMax === undefined || hrRest === null || hrRest === undefined || isNaN(parseFloat(hrMax)) || isNaN(parseFloat(hrRest)) || parseFloat(hrRest) <= 0) {
        return null;
    }
    const hrMaxNum = parseFloat(hrMax);
    const hrRestNum = parseFloat(hrRest);
    return 15 * (hrMaxNum / hrRestNum);
};

const calculateBUNCreatinineRatio = (bun, creatinine) => {
    if (bun === null || bun === undefined || creatinine === null || creatinine === undefined || isNaN(parseFloat(bun)) || isNaN(parseFloat(creatinine)) || parseFloat(creatinine) <= 0) {
        return null;
    }
    const bunNum = parseFloat(bun);
    const creatinineNum = parseFloat(creatinine);
    return bunNum / creatinineNum;
};

const calculateWaistHipRatio = (waist, hip) => {
    if (waist === null || waist === undefined || hip === null || hip === undefined || isNaN(parseFloat(waist)) || isNaN(parseFloat(hip)) || parseFloat(hip) <= 0) {
        return null;
    }
    const waistNum = parseFloat(waist);
    const hipNum = parseFloat(hip);
    return waistNum / hipNum;
};

const calculateNonHDLCholesterol = (totalCholesterol, hdl) => {
    if (totalCholesterol === null || totalCholesterol === undefined || hdl === null || hdl === undefined || isNaN(parseFloat(totalCholesterol)) || isNaN(parseFloat(hdl))) {
        return null;
    }
    const totalCholesterolNum = parseFloat(totalCholesterol);
    const hdlNum = parseFloat(hdl);
    return totalCholesterolNum - hdlNum;
};

// --- END: Calculation Helper Functions ---

// Display order constants - CBC organized by cell type: White Blood Cells → Red Blood Cells → Platelets
// Using exact display names as they appear in the flowsheet
const cbcDisplayOrder = [
    // White Blood Cells section
    'White Blood Cell Count',
    'Neutrophils',  // absolute count
    'Neutrophils',  // percentage (same name, will be handled by units)
    'Lymphocytes',  // absolute count
    'Lymphocytes',  // percentage (same name, will be handled by units)
    'Monocytes',    // absolute count
    'Monocytes',    // percentage (same name, will be handled by units)
    'Eosinophils',  // absolute count
    'Eosinophils',  // percentage (same name, will be handled by units)
    'Basophils',    // absolute count
    'Basophils',    // percentage (same name, will be handled by units)
    // Red Blood Cells section
    'Red Blood Cell Count',
    'Hemoglobin',
    'Hematocrit',
    'Mean Corpuscular Volume',
    'Mean Corpuscular Hemoglobin',
    'Mean Corpuscular Hemoglobin Concentration',
    'Red Cell Distribution Width',
    'Red Cell Distribution Width-SD',
    // Platelets section
    'Platelet Count',
    'Mean Platelet Volume',
    'Platelet Distribution Width',
    'Plateletcrit',
    'Platelet Large Cell Ratio',
    // Additional calculated parameters
    'Neutrophil-to-Lymphocyte Ratio',
];
const hepatologyDisplayOrder = [ 'AST', 'ALT', 'Alkaline Phosphatase', 'Bilirubin, Total', 'Bilirubin, Direct', 'Albumin', 'Total Protein', ];
const renalDisplayOrder = [ 'BUN', 'Creatinine', 'eGFR', 'BUN/Creatinine Ratio', ];
const electrolytesDisplayOrder = [ 'Sodium', 'Potassium', 'Chloride', 'Bicarbonate-CO2', 'Calcium', ];
const glucoseDisplayOrder = [ 'Glucose (Serum)', 'Glucose', 'HbA1C', ];
const lipidDisplayOrder = [
  'Total Cholesterol',
  'High-Density Lipoprotein Cholesterol', // HDL
  'Low-Density Lipoprotein Cholesterol', // LDL
  'Triglycerides', // TG
  'Non-HDL Cholesterol',
  'Very Low-Density Lipoprotein Cholesterol', // VLDL
  'Total Cholesterol/HDL Ratio', // The Ratios
  'Triglyceride/HDL Ratio',
  'Total Cholesterol/Glucose Ratio',
  'Apolipoprotein B', // ApoB
  'Atherogenic Index'
];
const thyroidDisplayOrder = [ 'TSH', 'Free T4', 'Free T3', 'Thyroid Peroxidase Ab (TPO)', 'Thyroglobulin Ab', ];
const urineDisplayOrder = [
  'Specific Gravity', 'pH', 'Nitrite', 'Protein', 'Glucose (Urine)', 'Ketones', 'Urobilinogen',
  'Bilirubin', 'Erythrocyte', 'Leukocyte', 'Microalbumin', 'Creatinine (Urine)', 'UACR'
];
const coagulationDisplayOrder = [ 'PT', 'INR', 'PTT', 'Fibrinogen', 'D-dimer', 'Thrombin Time', ];
const cardiacDisplayOrder = [ 'Troponin I', 'CK-MB', 'BNP', ];
const inflammationDisplayOrder = [ 'Uric Acid', 'hsCRP', 'ESR', 'hsCRP / Albumin Ratio', ];
const proteinDisplayOrder = [ 'Protein Total', 'Albumin', 'Prealbumin', 'Albumin / Globulin Ratio', ];
const vitaminsDisplayOrder = [ 'Vitamin D, 25-Hydroxy', 'Vitamin B12', 'Folate', 'Iron', 'TIBC', 'Transferrin Saturation', 'Ferritin', ];
const labDisplayOrderMap = { 'CBC': cbcDisplayOrder, 'Hepatology': hepatologyDisplayOrder, 'Renal': renalDisplayOrder, 'Electrolytes': electrolytesDisplayOrder, 'Glucose': glucoseDisplayOrder, 'Lipid': lipidDisplayOrder, 'Thyroid': thyroidDisplayOrder, 'Urine': urineDisplayOrder, 'Coagulation': coagulationDisplayOrder, 'Cardiac': cardiacDisplayOrder, 'Inflammation': inflammationDisplayOrder, 'Protein': proteinDisplayOrder, 'Vitamins': vitaminsDisplayOrder, };
const vitalsDisplayOrder = [ 'vitals_temp_c', 'vitals_sbp', 'vitals_dbp', 'vitals_rhr', 'vitals_rr', 'vitals_map', 'vitals_pp', 'vitals_spo2', 'vitals_sirs', 'vitals_hrv', 'vitals_vo2max', 'vitals_pain', 'vitals_weight', 'vitals_height', 'vitals_bmi', ];
const movementDisplayOrder = [ 'moderate_activity_days', 'vigorous_activity_days', 'strength_training_days', 'flexibility_stretching_days', 'balance_practice_days', 'incidental_walking_time', 'sedentary_hours', 'sitting_break_days', 'red_flag_sob', 'red_flag_chest_pain', 'red_flag_palpitations', 'red_flag_dizziness', 'red_flag_pain', 'red_flag_imbalance', ];
const biomeasurementDisplayOrder = [ 'weight', 'height', 'waistCircumference', 'hipCircumference', 'neckCircumference', 'weight_inbody', 'skeletalMuscleMass', 'bodyFatMass', 'percentBodyFat', 'waistHipRatio', 'visceralFatLevel', 'softLeanMass', 'upperLowerBodyBalance', 'bodyMassIndex_inbody', 'inbodyScore', 'bodyTypeCoordinate', 'ree', 'tee', ];
const nutritionDisplayOrder = [ 'Lifestyle_protein_intake', 'Lifestyle_fruit_intake', 'Lifestyle_vegetables_intake', 'Lifestyle_refined_carbs_intake', 'Lifestyle_fiber_intake', 'Lifestyle_seeds_nuts_intake', 'Lifestyle_sodium_intake', 'Lifestyle_sugar_intake', 'Lifestyle_fried_food_intake', 'Lifestyle_healthy_oil_use', 'Lifestyle_ultraprocessed_intake', 'Lifestyle_water_intake', 'Lifestyle_alcohol_intake', 'Lifestyle_weight_loss_concern', 'Lifestyle_weight_gain_concern', 'Lifestyle_appetite_lower_normal', 'Lifestyle_appetite_higher_normal', 'Lifestyle_skip_meals_hungry', 'Lifestyle_full_small_amount', 'Lifestyle_difficulty_chewing_swallowing', 'Lifestyle_problems_taste_smell', 'Lifestyle_worry_food_access', 'Lifestyle_bored_eating', 'Lifestyle_busy_eating', 'Lifestyle_late_eating', 'Lifestyle_binge_eating', 'Lifestyle_guilty_eating', 'Lifestyle_rushed_eating', 'Lifestyle_distracted_eating', 'Lifestyle_fad_diets', ];


const PatientFlowsheet = ({ patientId, initialFilterKey }) => {
  const { user } = useAuth(); // Get user from real AuthContext
  const { t } = useTranslation();

  const [flowsheetData, setFlowsheetData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [renderError, setRenderError] = useState(null);
  const [dateRange, setDateRange] = useState([moment().subtract(1, 'year'), moment()]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('Vitals');
  const [selectedTimestamp, setSelectedTimestamp] = useState(moment());
  const [addDataMode, setAddDataMode] = useState(null);
  const tableContainerRef = useRef(null);
  const [isExamModalVisible, setIsExamModalVisible] = useState(false);
  const [examModalContent, setExamModalContent] = useState('');
  const [examModalTitle, setExamModalTitle] = useState(t('categories.physicalExam', 'Physical Exam') + ' ' + t('common.summary', 'Summary'));
  const [hideEmptyColumns, setHideEmptyColumns] = useState(false);
  const [patientDemographics, setPatientDemographics] = useState(null);
  const [isLabPdfImportModalVisible, setIsLabPdfImportModalVisible] = useState(false);
  const [isManagementPanelVisible, setIsManagementPanelVisible] = useState(false); // Added state for management panel
  const [isCarePlanningModalVisible, setIsCarePlanningModalVisible] = useState(false); // State for the new modal
  const [highlightedParameterKey, setHighlightedParameterKey] = useState(null); // State for search highlighting
  const [isEnhancedAddDataModalVisible, setIsEnhancedAddDataModalVisible] = useState(false); // State for enhanced add data modal
  const [isVitalsAddModalVisible, setIsVitalsAddModalVisible] = useState(false); // State for vitals add modal
  const [isBalanceAddModalVisible, setIsBalanceAddModalVisible] = useState(false); // State for balance add modal
  const [isBiomeasurementAddModalVisible, setIsBiomeasurementAddModalVisible] = useState(false); // State for biomeasurement add modal
  const [isCognitionAddModalVisible, setIsCognitionAddModalVisible] = useState(false); // State for cognition add modal
  const [isEnduranceAddModalVisible, setIsEnduranceAddModalVisible] = useState(false); // State for endurance add modal
  const [isFlexibilityAddModalVisible, setIsFlexibilityAddModalVisible] = useState(false); // State for flexibility add modal
  const [isPowerAddModalVisible, setIsPowerAddModalVisible] = useState(false); // State for power add modal
  const [isPulmonaryAddModalVisible, setIsPulmonaryAddModalVisible] = useState(false); // State for pulmonary add modal
  const [isStrengthAddModalVisible, setIsStrengthAddModalVisible] = useState(false); // State for strength add modal
  const [isVisionAddModalVisible, setIsVisionAddModalVisible] = useState(false); // State for vision add modal
  const [isImagingPdfUploadVisible, setIsImagingPdfUploadVisible] = useState(false); // State for imaging PDF upload modal
  const [scoringConfigs, setScoringConfigs] = useState(null);
  const [clinicalRanges, setClinicalRanges] = useState(null);
  const { snapshotData, loading: snapshotLoading, error: snapshotError } = useHealthSnapshotData(patientId);
  const [isTrendModalVisible, setIsTrendModalVisible] = useState(false);
  const [selectedParameterData, setSelectedParameterData] = useState(null);
  const [isLifestyleDetailModalVisible, setIsLifestyleDetailModalVisible] = useState(false);
  const [lifestyleDetailData, setLifestyleDetailData] = useState(null);

  const parameterRegistry = useMemo(() => buildParameterRegistry(), [t]);

  // Handler for clicking on lifestyle parameter timestamps
  const handleLifestyleParameterClick = useCallback((parameterKey, value, timestamp, notes) => {
    console.log('Lifestyle parameter clicked:', { parameterKey, value, timestamp, notes });
    setLifestyleDetailData({
      parameterKey,
      value,
      timestamp,
      notes
    });
    setIsLifestyleDetailModalVisible(true);
    console.log('Modal visibility set to true');
  }, []);

  // Handler for clicking on timestamp column header to view all lifestyle parameters
  const handleTimestampColumnClick = useCallback((timestamp, tableData) => {
    // Collect all lifestyle parameters for this timestamp
    const lifestyleParams = [];

    if (tableData && Array.isArray(tableData)) {
      tableData.forEach(record => {
        // Check if this is a lifestyle parameter
        if (isLifestyleParameter(record.key)) {
          // Get the value for this timestamp
          const cellData = record[timestamp];
          if (cellData && (cellData.value !== null && cellData.value !== undefined)) {
            lifestyleParams.push({
              parameterKey: record.key,
              value: cellData.value,
              notes: cellData.notes
            });
          }
        }
      });
    }

    // Only show modal if there are lifestyle parameters
    if (lifestyleParams.length > 0) {
      setLifestyleDetailData({
        timestamp,
        parameters: lifestyleParams
      });
      setIsLifestyleDetailModalVisible(true);
    }
  }, []);

  // Helper function to convert display name to internal key
  const getInternalKey = useCallback((displayName) => {
    const displayToInternalMap = {
      // Vietnamese to English
      'Kết quả xét nghiệm': 'Lab Results',
      'Dấu hiệu sinh tồn': 'Vitals',
      'Khám thể chất (Đầy đủ)': 'Physical Exam',
      'Khám thể chất': 'Physical Exam',
      'Sinh trắc học': 'Biometrics',
      'Chẩn đoán hình ảnh': 'Imaging',
      'Lối sống': 'Lifestyle',
      'Tất cả danh mục': 'All',
      // Lab subcategories Vietnamese to English
      'Công thức máu': 'CBC',
      'Lipid máu': 'Lipid',
      'Đường huyết': 'Glucose',
      'Gan mật': 'Hepatology',
      'Thận': 'Renal',
      'Điện giải': 'Electrolytes',
      'Tuyến giáp': 'Thyroid',
      'Nước tiểu': 'Urine',
      'Đông máu': 'Coagulation',
      'Tim mạch': 'Cardiac',
      'Protein': 'Protein',
      'Vitamin': 'Vitamins',
      'Viêm': 'Inflammation',
      // Biometric subcategories Vietnamese to English
      'Nhận thức': 'Cognition',
      'Thị lực': 'Vision',
      'Phổi': 'Pulmonary',
      'Đo sinh trắc': 'Biomeasurement',
      'Độ linh hoạt': 'Flexibility',
      'Thăng bằng': 'Balance',
      'Sức mạnh': 'Strength',
      'Sức bền': 'Endurance',
      'Sức mạnh bùng nổ': 'Power'
    };
    return displayToInternalMap[displayName] || displayName;
  }, []);

  // Helper function to translate Vietnamese category names to English for filtering
  const getEnglishCategoryName = useCallback((categoryName) => {
    const vietnameseToEnglishMap = {
      // Main categories
      'Kết quả xét nghiệm': 'Lab Results',
      'Dấu hiệu sinh tồn': 'Vitals',
      'Khám thể chất (Đầy đủ)': 'Physical Exam',
      'Khám thể chất': 'Physical Exam',
      'Sinh trắc học': 'Biometrics',
      'Chẩn đoán hình ảnh': 'Imaging',
      'Lối sống': 'Lifestyle',
      'Lối sống - Dinh dưỡng': 'Lifestyle',
      'Lối sống - Vận động': 'Lifestyle',
      'Lối sống - Phục hồi': 'Lifestyle',
      'Lối sống - Cảm xúc': 'Lifestyle',
      'Lối sống - Môi trường': 'Lifestyle',
      'Tất cả danh mục': 'All',
      // Lab subcategories
      'Công thức máu': 'CBC',
      'Lipid máu': 'Lipid',
      'Đường huyết': 'Glucose',
      'Gan mật': 'Hepatology',
      'Thận': 'Renal',
      'Điện giải': 'Electrolytes',
      'Tuyến giáp': 'Thyroid',
      'Nước tiểu': 'Urine',
      'Đông máu': 'Coagulation',
      'Tim mạch': 'Cardiac',
      'Protein': 'Protein',
      'Vitamin': 'Vitamins',
      'Viêm': 'Inflammation',
      // Biometric subcategories
      'Nhận thức': 'Cognition',
      'Thị lực': 'Vision',
      'Phổi': 'Pulmonary',
      'Đo sinh trắc': 'Biomeasurement',
      'Độ linh hoạt': 'Flexibility',
      'Thăng bằng': 'Balance',
      'Sức mạnh': 'Strength',
      'Sức bền': 'Endurance',
      'Sức mạnh bùng nổ': 'Power'
    };
    return vietnameseToEnglishMap[categoryName] || categoryName;
  }, []);

  // Helper function to check if a category name is a known lab subcategory (English or Vietnamese)
  const isKnownLabSubcategory = useCallback((categoryName) => {
    const englishName = getEnglishCategoryName(categoryName);
    return knownLabSubCategoryTitles.has(englishName) || knownLabSubCategoryTitles.has(categoryName);
  }, [getEnglishCategoryName]);

  // Helper function to check if a category name is a known biometric subcategory (English or Vietnamese)
  const isKnownBiometricSubcategory = useCallback((categoryName) => {
    const englishName = getEnglishCategoryName(categoryName);
    return biometricSubCategoryKeys.has(englishName) || biometricSubCategoryKeys.has(categoryName) ||
           biometricCategories.some(cat => cat.title === categoryName || cat.title === englishName ||
                                          cat.key === categoryName || cat.key === englishName);
  }, [getEnglishCategoryName]);

  // Get the English category name for the current filter
  const englishCategoryFilter = useMemo(() => 
    getEnglishCategoryName(selectedCategoryFilter), 
    [selectedCategoryFilter, getEnglishCategoryName]
  );

  // Monitor language changes for debugging
  useEffect(() => {
    if (i18n.language === 'vi') {
    }
  }, [i18n.language]);

  useEffect(() => {
    const fetchScoringConfigs = async () => {
      try {
        const [
          scoringVitalsRes,
          scoringLabsRes,
          scoringBiometricsRes,
          scoringPhysicalExamRes,
          scoringLifestyleRes,
          scoringImagingRes,
          clinicalRangesRes,
        ] = await Promise.all([
          axios.get(`${window.location.origin}/configs/scoring_config_vitals.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_labs.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_biometrics.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_physical_exam.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_lifestyle.json`),
          axios.get(`${window.location.origin}/configs/scoring_config_imaging.json`),
          axios.get(`${window.location.origin}/configs/clinical_reference_ranges.json`),
        ]);

        setClinicalRanges(clinicalRangesRes.data);

        const allScoringParams = [
          ...(scoringVitalsRes.data?.parameters || []),
          ...(scoringLabsRes.data?.parameters || []),
          ...(scoringBiometricsRes.data?.parameters || []),
          ...(scoringPhysicalExamRes.data?.parameters || []),
          ...(scoringLifestyleRes.data?.parameters || []),
          ...(scoringImagingRes.data?.parameters || []),
        ];
        
        const configMap = new Map();
        allScoringParams.forEach(param => {
          if (param && param.key) {
            configMap.set(param.key, param);
          }
        });
        setScoringConfigs(configMap);
      } catch (err) {
        console.error("Failed to load scoring or clinical range configs", err);
        setError("Failed to load parameter configurations.");
      }
    };

    fetchScoringConfigs();
  }, []);

  useEffect(() => {
    const fetchPatientDemographics = async () => {
      if (patientId) {
        try {
          const response = await axios.get(`/patients/${patientId}`); 
          if (response.data && response.data.data && response.data.data.patient) {
            const { birthDate, sex } = response.data.data.patient;
            let age = null;
            if (birthDate) {
              age = moment().diff(moment(birthDate), 'years');
            }
            setPatientDemographics({ age, sex });
          } else {
            setPatientDemographics(null);
          }
        } catch (err) {
          console.error("Error fetching patient demographics:", err);
          setPatientDemographics(null); 
        }
      }
    };
    fetchPatientDemographics();
  }, [patientId]);




  const fetchFlowsheetData = useCallback(async (range) => {
    if (!patientId) {
      console.error("No patientId provided to PatientFlowsheet component");
      return;
    }
    setLoading(true);
    setError(null);
    const params = {
      startDate: range[0].startOf('day').toISOString(),
      endDate: range[1].endOf('day').toISOString(),
    };
    if (selectedCategoryFilter && selectedCategoryFilter !== 'All') {
      // Convert translated category names to English for backend compatibility
      const categoryTranslationMap = {
        // Main categories (Vietnamese to English)
        'Kết quả xét nghiệm': 'Lab Results',
        'Dấu hiệu sinh tồn': 'Vitals',
        'Khám thể chất (Đầy đủ)': 'Physical Exam',
        'Khám thể chất': 'Physical Exam',
        'Sinh trắc học': 'Biometrics',
        'Chẩn đoán hình ảnh': 'Imaging',
        'Lối sống': 'Lifestyle',
        'Lối sống - Dinh dưỡng': 'Lifestyle',
        'Lối sống - Vận động': 'Lifestyle',
        'Lối sống - Phục hồi': 'Lifestyle',
        'Lối sống - Cảm xúc': 'Lifestyle',
        'Lối sống - Môi trường': 'Lifestyle',
        'Khảo sát': 'Surveys',
        'Tất cả danh mục': 'All',
        // Main categories (English to English - for when UI is in English)
        'Lab Results': 'Lab Results',
        'Vitals': 'Vitals',
        'Physical Exam': 'Physical Exam',
        'Biometrics': 'Biometrics',
        'Imaging': 'Imaging',
        'Lifestyle': 'Lifestyle',
        'Surveys': 'Surveys',
        'All': 'All',
        // Lab subcategories (Vietnamese to English)
        'Công thức máu': 'CBC',
        'Lipid máu': 'Lipid',
        'Đường huyết': 'Glucose',
        'Gan mật': 'Hepatology',
        'Thận': 'Renal',
        'Điện giải': 'Electrolytes',
        'Tuyến giáp': 'Thyroid',
        'Nước tiểu': 'Urine',
        'Phân tích nước tiểu': 'Urine',
        'Đông máu': 'Coagulation',
        'Tim mạch': 'Cardiac',
        'Protein': 'Protein',
        'Vitamin': 'Vitamins',
        'Viêm': 'Inflammation',
        // Lab subcategories (English to English - for when UI is in English)
        'CBC': 'CBC',
        'Lipid': 'Lipid',
        'Glucose': 'Glucose',
        'Hepatology': 'Hepatology',
        'Renal': 'Renal',
        'Electrolytes': 'Electrolytes',
        'Thyroid': 'Thyroid',
        'Urine': 'Urine',
        'Coagulation': 'Coagulation',
        'Cardiac': 'Cardiac',
        'Vitamins': 'Vitamins',
        'Inflammation': 'Inflammation',
        // Biometric subcategories (Vietnamese to English)
        'Nhận thức': 'Cognition',
        'Thị lực': 'Vision',
        'Phổi': 'Pulmonary',
        'Đo sinh trắc': 'Biomeasurement',
        'Độ linh hoạt': 'Flexibility',
        'Thăng bằng': 'Balance',
        'Sức mạnh': 'Strength',
        'Sức bền': 'Endurance',
        'Sức mạnh bùng nổ': 'Power',
        // Biometric subcategories (English to English - for when UI is in English)
        'Cognition': 'Cognition',
        'Vision': 'Vision',
        'Pulmonary': 'Pulmonary',
        'Biomeasurement': 'Biomeasurement',
        'Flexibility': 'Flexibility',
        'Balance': 'Balance',
        'Strength': 'Strength',
        'Endurance': 'Endurance',
        'Power': 'Power'
      };
      
      const englishCategoryName = categoryTranslationMap[selectedCategoryFilter];
      if (englishCategoryName) {
        params.category = englishCategoryName;
      } else {
        // If no translation found, log a warning and don't set category filter
        // This prevents sending Vietnamese labels to the backend
        console.warn(`No translation found for category filter: "${selectedCategoryFilter}"`);
      }
    }
    try {
      const url = `/patients/${patientId}/flowsheet/data`;

      const response = await axios.get(url, { params });
      
      // Debug lab data specifically when using Vietnamese
      if (i18n.language === 'vi' && (selectedCategoryFilter?.includes('máu') || selectedCategoryFilter?.includes('Tim mạch') || selectedCategoryFilter === 'Cardiac')) {
        const labTypeFilter = selectedCategoryFilter?.includes('máu') ? 'cbc' : 'cardiac';
        const labItemsFromBackend = response.data?.data?.flowsheetData?.filter(item => item.name?.includes(labTypeFilter));
      }

      if (!response.data.data.flowsheetData || response.data.data.flowsheetData.length === 0) {

        setFlowsheetData([]);
        setLoading(false);
        return;
      }


      const parametersToExclude = [
          "Sun exposure < 15min/day?", "Environment Score", "Nutrition Score",
          "PhysicalActivity Score", "Sleep Score", "Appetite",
      ];
      const filteredData = response.data.data.flowsheetData.filter(item => !parametersToExclude.includes(item.name));

      const processedData = filteredData.map((item) => {
        // Force fix lab category assignment at source for all lab types
        if (item.name && item.name.startsWith('labs_')) {
          item.category = 'Lab Results';
        }
        
        return {
          ...item,
          flag: item.type === 'Lab' ? item.flag : undefined,
          parameterKey: `${item.type}_${item.name}`
        };
      });



      setFlowsheetData(processedData);
      
    } catch (err) {
      console.error("[Vietnamese Debug] Error fetching flowsheet data:", {
        error: err.message,
        currentLanguage: i18n.language,
        errorDetails: err.response?.data
      });
      setError(err.response?.data?.message || 'Failed to fetch flowsheet data.');
      setFlowsheetData([]);
    } finally {
      setLoading(false);
    }
  }, [patientId, selectedCategoryFilter]);

  useEffect(() => {
    fetchFlowsheetData(dateRange);
  }, [fetchFlowsheetData, dateRange]);
  
  // Monitor language changes and force data refresh for parameter display names
  useEffect(() => {
    if (i18n.language === 'vi' && flowsheetData.length > 0) {
      // Force component re-render by triggering a state update
      // This ensures parameter display names are updated with Vietnamese translations
      setFlowsheetData([...flowsheetData]);
    }
  }, [i18n.language]);

  // Legacy parameter mapping removed - all data should now use standardized parameter names from parameters.md

  // Helper function to calculate parameter counts for categories
  const calculateParameterCounts = useCallback((parameterRows, parameterRegistry) => {
    const countsWithData = new Map();
    const totalAvailable = new Map();

    // Vietnamese to English category mapping for counting
    const vietnameseToEnglishCategories = {
      // Main categories (Vietnamese to English)
      'Kết quả xét nghiệm': 'Lab Results',
      'Dấu hiệu sinh tồn': 'Vitals',
      'Khám thể chất (Đầy đủ)': 'Physical Exam',
      'Khám thể chất': 'Physical Exam',
      'Sinh trắc học': 'Biometrics',
      'Chẩn đoán hình ảnh': 'Imaging',
      'Lối sống': 'Lifestyle',
      'Lối sống - Dinh dưỡng': 'Lifestyle',
      'Lối sống - Vận động': 'Lifestyle',
      'Lối sống - Phục hồi': 'Lifestyle',
      'Lối sống - Cảm xúc': 'Lifestyle',
      'Lối sống - Môi trường': 'Lifestyle',
      'Tất cả danh mục': 'All',
      // Main categories (English to English - for when UI is in English)
      'Lab Results': 'Lab Results',
      'Vitals': 'Vitals',
      'Physical Exam': 'Physical Exam',
      'Biometrics': 'Biometrics',
      'Imaging': 'Imaging',
      'Lifestyle': 'Lifestyle',
      'All': 'All',
      // Lab subcategories (Vietnamese to English)
      'Công thức máu': 'CBC',
      'Lipid máu': 'Lipid',
      'Đường huyết': 'Glucose',
      'Gan mật': 'Hepatology',
      'Thận': 'Renal',
      'Điện giải': 'Electrolytes',
      'Tuyến giáp': 'Thyroid',
      'Nước tiểu': 'Urine',
      'Phân tích nước tiểu': 'Urine',
      'Đông máu': 'Coagulation',
      'Tim mạch': 'Cardiac',
      'Protein': 'Protein',
      'Vitamin': 'Vitamins',
      'Viêm': 'Inflammation',
      // Lab subcategories (English to English - for when UI is in English)
      'CBC': 'CBC',
      'Lipid': 'Lipid',
      'Glucose': 'Glucose',
      'Hepatology': 'Hepatology',
      'Renal': 'Renal',
      'Electrolytes': 'Electrolytes',
      'Thyroid': 'Thyroid',
      'Urine': 'Urine',
      'Coagulation': 'Coagulation',
      'Cardiac': 'Cardiac',
      'Vitamins': 'Vitamins',
      'Inflammation': 'Inflammation',
      // Biometric subcategories (Vietnamese to English)
      'Nhận thức': 'Cognition',
      'Thị lực': 'Vision',
      'Phổi': 'Pulmonary',
      'Đo sinh trắc': 'Biomeasurement',
      'Độ linh hoạt': 'Flexibility',
      'Thăng bằng': 'Balance',
      'Sức mạnh': 'Strength',
      'Sức bền': 'Endurance',
      'Sức mạnh bùng nổ': 'Power',
      // Biometric subcategories (English to English - for when UI is in English)
      'Cognition': 'Cognition',
      'Vision': 'Vision',
      'Pulmonary': 'Pulmonary',
      'Biomeasurement': 'Biomeasurement',
      'Flexibility': 'Flexibility',
      'Balance': 'Balance',
      'Strength': 'Strength',
      'Endurance': 'Endurance',
      'Power': 'Power'
    };

    // Initialize counts for all main categories (both English and Vietnamese)
    categoryDisplayOrder.forEach(category => {
      countsWithData.set(category, 0);
      totalAvailable.set(category, 0);
      
      // Also initialize for Vietnamese equivalents
      for (const [vietnamese, english] of Object.entries(vietnameseToEnglishCategories)) {
        if (english === category) {
          countsWithData.set(vietnamese, 0);
          totalAvailable.set(vietnamese, 0);
        }
      }
    });

    // Count total available parameters by category and subcategory from registry
    parameterRegistry.forEach(param => {
      const { category, subcategory } = param;
      if (category) {
        totalAvailable.set(category, (totalAvailable.get(category) || 0) + 1);
        
        // Also count for Vietnamese equivalent
        for (const [vietnamese, english] of Object.entries(vietnameseToEnglishCategories)) {
          if (english === category) {
            totalAvailable.set(vietnamese, (totalAvailable.get(vietnamese) || 0) + 1);
          }
        }
      }
      if (subcategory) {
        totalAvailable.set(subcategory, (totalAvailable.get(subcategory) || 0) + 1);
        
        // Also count for Vietnamese equivalent
        for (const [vietnamese, english] of Object.entries(vietnameseToEnglishCategories)) {
          if (english === subcategory) {
            totalAvailable.set(vietnamese, (totalAvailable.get(vietnamese) || 0) + 1);
          }
        }
      }
    });

    // Count parameters with data by category and subcategory
    parameterRows.forEach(param => {
      const category = param.category;
      const subcategory = param.subcategory;


      // Count for main category
      if (category) {
        countsWithData.set(category, (countsWithData.get(category) || 0) + 1);
        
        // Also count for Vietnamese equivalent
        for (const [vietnamese, english] of Object.entries(vietnameseToEnglishCategories)) {
          if (english === category) {
            countsWithData.set(vietnamese, (countsWithData.get(vietnamese) || 0) + 1);
          }
        }

        // Count for subcategory
        if (subcategory) {
          countsWithData.set(subcategory, (countsWithData.get(subcategory) || 0) + 1);
          
          // Also count for Vietnamese equivalent
          for (const [vietnamese, english] of Object.entries(vietnameseToEnglishCategories)) {
            if (english === subcategory) {
              countsWithData.set(vietnamese, (countsWithData.get(vietnamese) || 0) + 1);
            }
          }
        }
      }
    });


    // Special handling for 'All' category
    const totalCountWithData = parameterRows.length;
    const totalAvailableCount = parameterRegistry.length;
    countsWithData.set('All', totalCountWithData);
    totalAvailable.set('All', totalAvailableCount);
    countsWithData.set('Tất cả danh mục', totalCountWithData);
    totalAvailable.set('Tất cả danh mục', totalAvailableCount);

    return { countsWithData, totalAvailable };
  }, []);

  const transformedData = useMemo(() => {
    try {
        // Build category structure from parameter registry even if scoringConfigs is not loaded yet
        // This ensures categories are always available for the sidebar navigation
        const categoryStructure = {};

        parameterRegistry.forEach(param => {
            // Build category structure for the side menu
            if (!categoryStructure[param.category]) {
                categoryStructure[param.category] = {
                    key: param.category,
                    title: param.category,
                    children: new Map(),
                };
            }
            if (param.subcategory) {
                // For imaging subcategories, use "Imaging_" prefix for the key to match filtering logic
                let subcategoryKey = param.subcategory;
                if (param.category === 'Imaging') {
                    // Convert display names to filter keys for imaging
                    const imagingDisplayToFilterMap = {
                        'Spleen US': 'Imaging_SpleenUS',
                        'Liver US': 'Imaging_LiverUS',
                        'Carotid US': 'Imaging_CarotidUS',
                        'EKG': 'Imaging_EKG',
                        'Bladder US': 'Imaging_BladderUS',
                        'Chest XR': 'Imaging_ChestXR',
                        'Heart US': 'Imaging_HeartUS',
                        'Gallbladder US': 'Imaging_GallbladderUS',
                        'Kidney US': 'Imaging_KidneyUS',
                        'Thyroid US': 'Imaging_ThyroidUS'
                    };
                    subcategoryKey = imagingDisplayToFilterMap[param.subcategory] || `Imaging_${param.subcategory.replace(/\s+/g, '')}`;
                }

                categoryStructure[param.category].children.set(param.subcategory, {
                    key: subcategoryKey,
                    title: param.subcategory,
                });
            }
        });

        // Always add expected subcategories to categoryStructure for sidebar navigation
        // Vitals category (should be a main category, not have subcategories)
        if (!categoryStructure['Vitals']) {
            categoryStructure['Vitals'] = {
                key: 'Vitals',
                title: 'Vitals',
                children: new Map(),
            };
        }

        // Physical Exam subcategories
        if (!categoryStructure['Physical Exam']) {
            categoryStructure['Physical Exam'] = {
                key: 'Physical Exam',
                title: 'Physical Exam',
                children: new Map(),
            };
        }

        // Add all expected Physical Exam subcategories
        const expectedPESubcategories = [
            'PE Summaries',
            'General',
            'Cardiovascular',
            'Chest/Respiratory',
            'Abdomen',
            'Extremities',
            'Neurological',
            'HEENT - Eyes',
            'HEENT - Ears',
            'HEENT - Mouth/Throat',
            'HEENT - Nose/Sinus',
            'Lymph Nodes',
            'Skin'
        ];

        expectedPESubcategories.forEach(subcatName => {
            if (!categoryStructure['Physical Exam'].children.has(subcatName)) {
                categoryStructure['Physical Exam'].children.set(subcatName, {
                    key: subcatName,
                    title: subcatName,
                });
            }
        });

        // Lab Results subcategories
        if (!categoryStructure['Lab Results']) {
            categoryStructure['Lab Results'] = {
                key: 'Lab Results',
                title: 'Lab Results',
                children: new Map(),
            };
        }

        const expectedLabSubcategories = [
            'CBC',
            'Lipid',
            'Glucose',
            'Hepatology',
            'Renal',
            'Electrolytes',
            'Thyroid',
            'Vitamins',
            'Protein',
            'Inflammation',
            'Urine',
            'Coagulation',
            'Cardiac'
          ];

        expectedLabSubcategories.forEach(subcatName => {
            if (!categoryStructure['Lab Results'].children.has(subcatName)) {
                categoryStructure['Lab Results'].children.set(subcatName, {
                    key: subcatName,
                    title: subcatName,
                });
            }
        });

        // Lifestyle subcategories
        if (!categoryStructure['Lifestyle']) {
            categoryStructure['Lifestyle'] = {
                key: 'Lifestyle',
                title: 'Lifestyle',
                children: new Map(),
            };
        }

        const expectedLifestyleSubcategories = [
            'Nutrition',
            'Movement',
            'Recovery',
            'Emotion',
            'Environment'
        ];

        expectedLifestyleSubcategories.forEach(subcatName => {
            if (!categoryStructure['Lifestyle'].children.has(subcatName)) {
                categoryStructure['Lifestyle'].children.set(subcatName, {
                    key: subcatName,
                    title: subcatName,
                });
            }
        });

        // Biometrics subcategories
        if (!categoryStructure['Biometrics']) {
            categoryStructure['Biometrics'] = {
                key: 'Biometrics',
                title: 'Biometrics',
                children: new Map(),
            };
        }

        const expectedBiometricsSubcategories = [
            'Cognition',
            'Vision',
            'Pulmonary',
            'Biomeasurement',
            'Flexibility',
            'Balance',
            'Strength',
            'Endurance',
            'Power'
        ];

        expectedBiometricsSubcategories.forEach(subcatName => {
            if (!categoryStructure['Biometrics'].children.has(subcatName)) {
                categoryStructure['Biometrics'].children.set(subcatName, {
                    key: subcatName,
                    title: subcatName,
                });
            }
        });

        // Imaging subcategories
        if (!categoryStructure['Imaging']) {
            categoryStructure['Imaging'] = {
                key: 'Imaging',
                title: 'Imaging',
                children: new Map(),
            };
        }

        const expectedImagingSubcategories = [
            'EKG',
            'Chest XR',
            'Heart US',
            'Bladder US',
            'Spleen US',
            'Liver US',
            'Carotid US',
            'Gallbladder US',
            'Kidney US',
            'Thyroid US'
        ];

        expectedImagingSubcategories.forEach(subcatName => {
            if (!categoryStructure['Imaging'].children.has(subcatName)) {
                // For imaging subcategories, use "Imaging_" prefix for the key to match filtering logic
                const imagingDisplayToFilterMap = {
                    'Spleen US': 'Imaging_SpleenUS',
                    'Liver US': 'Imaging_LiverUS',
                    'Carotid US': 'Imaging_CarotidUS',
                    'EKG': 'Imaging_EKG',
                    'Bladder US': 'Imaging_BladderUS',
                    'Chest XR': 'Imaging_ChestXR',
                    'Heart US': 'Imaging_HeartUS',
                    'Gallbladder US': 'Imaging_GallbladderUS',
                    'Kidney US': 'Imaging_KidneyUS',
                    'Thyroid US': 'Imaging_ThyroidUS'
                };
                const subcategoryKey = imagingDisplayToFilterMap[subcatName] || `Imaging_${subcatName.replace(/\s+/g, '')}`;

                categoryStructure['Imaging'].children.set(subcatName, {
                    key: subcategoryKey,
                    title: subcatName,
                });
            }
        });

        // Build static hierarchical categories for sidebar navigation with translations
        const staticHierarchicalCategories = categoryDisplayOrder
            .map(catName => {
                const cat = categoryStructure[catName];
                if (!cat) {
                    // Special handling for Lab Results - ensure it appears even if no lab parameters are loaded yet
                    if (catName === 'Lab Results') {
                        return {
                            key: 'Lab Results',
                            title: t('categories.labResults', 'Lab Results'),
                            children: []
                        };
                    }
                    // Special handling for Imaging - ensure it appears even if no imaging parameters are loaded yet
                    if (catName === 'Imaging') {
                        return {
                            key: 'Imaging',
                            title: t('categories.imaging', 'Imaging'),
                            children: []
                        };
                    }
                    // Special handling for Surveys - ensure it appears even if no survey data is loaded yet
                    if (catName === 'Surveys') {
                        return {
                            key: 'Surveys',
                            title: t('categories.surveys', 'Surveys'),
                            children: []
                        };
                    }
                    return null;
                }
                const children = Array.from(cat.children.values());
                // Special sorting for PE
                if (catName === 'Physical Exam') {
                    children.sort((a, b) => {
                        // PE Summaries should always come first
                        if (a.title === 'PE Summaries') return -1;
                        if (b.title === 'PE Summaries') return 1;

                        // Then sort other PE subcategories by their order in physicalExamSectionsFull
                        const indexA = physicalExamSectionsFull.findIndex(s => s.title === a.title);
                        const indexB = physicalExamSectionsFull.findIndex(s => s.title === b.title);
                        return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
                    });
                } else {
                    children.sort((a, b) => a.title.localeCompare(b.title));
                }
                // Get translated category title
                const getCategoryTitle = (categoryKey) => {
                    switch(categoryKey) {
                        case 'Vitals': return t('categories.vitals', 'Vitals');
                        case 'Physical Exam': return t('categories.physicalExam', 'Physical Exam');
                        case 'Biometrics': return t('categories.biometrics', 'Biometrics');
                        case 'Lab Results': return t('categories.labResults', 'Lab Results');
                        case 'Imaging': return t('categories.imaging', 'Imaging');
                        case 'Lifestyle': return t('categories.lifestyle', 'Lifestyle');
                        case 'Surveys': return t('categories.surveys', 'Surveys');
                        default: return categoryKey;
                    }
                };

                // Get translated subcategory title
                const getSubcategoryTitle = (subcategoryKey, parentCategoryKey) => {
                    // Lab subcategories
                    if (parentCategoryKey === 'Lab Results') {
                        switch(subcategoryKey) {
                            case 'CBC': return t('categories.cbc', 'CBC');
                            case 'Lipid': return t('categories.lipid', 'Lipid');
                            case 'Glucose': return t('categories.glucose', 'Glucose');
                            case 'Hepatology': return t('categories.hepatology', 'Hepatology');
                            case 'Renal': return t('categories.renal', 'Renal');
                            case 'Electrolytes': return t('categories.electrolytes', 'Electrolytes');
                            case 'Thyroid': return t('categories.thyroid', 'Thyroid');
                            case 'Cardiac': return t('categories.cardiac', 'Cardiac');
                            case 'Coagulation': return t('categories.coagulation', 'Coagulation');
                            case 'Inflammation': return t('categories.inflammation', 'Inflammation');
                            case 'Urine': return t('categories.urine', 'Urine');
                            case 'Vitamins': return t('categories.vitamins', 'Vitamins');
                            default: return subcategoryKey;
                        }
                    }

                    // Physical Exam subcategories
                    if (parentCategoryKey === 'Physical Exam') {
                        switch(subcategoryKey) {
                            case 'PE Summaries': return t('categories.peSummaries', 'PE Summaries');
                            case 'General': return t('categories.general', 'General');
                            case 'Cardiovascular': return t('categories.cardiovascular', 'Cardiovascular');
                            case 'Chest/Respiratory': return t('categories.chestRespiratory', 'Chest/Respiratory');
                            case 'Abdomen': return t('categories.abdomen', 'Abdomen');
                            case 'Extremities': return t('categories.extremities', 'Extremities');
                            case 'Neurological': return t('categories.neurological', 'Neurological');
                            case 'HEENT - Eyes': return t('categories.heentEyes', 'HEENT - Eyes');
                            case 'HEENT - Ears': return t('categories.heentEars', 'HEENT - Ears');
                            case 'HEENT - Mouth/Throat': return t('categories.heentMouth', 'HEENT - Mouth/Throat');
                            case 'HEENT - Nose/Sinus': return t('categories.heentNose', 'HEENT - Nose/Sinus');
                            case 'Lymph Nodes': return t('categories.lymphNodes', 'Lymph Nodes');
                            case 'Skin': return t('categories.skin', 'Skin');
                            default: return subcategoryKey;
                        }
                    }

                    // Lifestyle subcategories
                    if (parentCategoryKey === 'Lifestyle') {
                        switch(subcategoryKey) {
                            case 'Nutrition': return t('categories.nutrition', 'Nutrition');
                            case 'Movement': return t('categories.movement', 'Movement');
                            case 'Recovery': return t('categories.recovery', 'Recovery');
                            case 'Emotion': return t('categories.emotion', 'Emotion');
                            case 'Environment': return t('categories.environment', 'Environment');
                            default: return subcategoryKey;
                        }
                    }

                    // Biometrics subcategories
                    if (parentCategoryKey === 'Biometrics') {
                        switch(subcategoryKey) {
                            case 'Cognition': return t('categories.cognition', 'Cognition');
                            case 'Vision': return t('categories.vision', 'Vision');
                            case 'Pulmonary': return t('categories.pulmonary', 'Pulmonary');
                            case 'Biomeasurement': return t('categories.biomeasurement', 'Biomeasurement');
                            case 'Flexibility': return t('categories.flexibility', 'Flexibility');
                            case 'Balance': return t('categories.balance', 'Balance');
                            case 'Strength': return t('categories.strength', 'Strength');
                            case 'Endurance': return t('categories.endurance', 'Endurance');
                            case 'Power': return t('categories.power', 'Power');
                            default: return subcategoryKey;
                        }
                    }

                    // Imaging subcategories
                    if (parentCategoryKey === 'Imaging') {
                        switch(subcategoryKey) {
                            case 'EKG': return t('categories.ekg', 'EKG');
                            case 'Chest XR': return t('categories.chestXr', 'Chest XR');
                            case 'Heart US': return t('categories.heartUs', 'Heart US');
                            case 'Bladder US': return t('categories.bladderUs', 'Bladder US');
                            case 'Spleen US': return t('categories.spleenUs', 'Spleen US');
                            case 'Liver US': return t('categories.liverUs', 'Liver US');
                            case 'Carotid US': return t('categories.carotidUs', 'Carotid US');
                            case 'Gallbladder US': return t('categories.gallbladderUs', 'Gallbladder US');
                            case 'Kidney US': return t('categories.kidneyUs', 'Kidney US');
                            case 'Thyroid US': return t('categories.thyroidUs', 'Thyroid US');
                            default: return subcategoryKey;
                        }
                    }

                    // Default fallback
                    return subcategoryKey;
                };

                // Translate subcategory titles
                const translatedChildren = children.map(child => ({
                    ...child,
                    title: getSubcategoryTitle(child.title, cat.key)
                }));

                return { ...cat, title: getCategoryTitle(cat.key), children: translatedChildren };
            })
            .filter(Boolean);

        staticHierarchicalCategories.push({ key: 'All', title: t('patientChart.flowsheet.allCategories', 'All') });

        // If scoringConfigs is not loaded yet, return categories only for sidebar navigation
        if (!scoringConfigs) {
          return {
            uniqueTimestamps: [],
            parameterRows: [],
            categories: staticHierarchicalCategories,
            parameterCounts: new Map(),
            totalAvailableCounts: new Map()
          };
        }

        // Centralized Parameter Definition from Registry
        const parametersMap = new Map();

        // Always create Physical Exam Summary parameters FIRST so they are included in category structure
        parametersMap.set('physicalExamSummarySimple', {
            key: 'physicalExamSummarySimple',
            parameterName: t('categories.physicalExam', 'Physical Exam') + ' ' + t('common.summary', 'Summary') + ' (Simple)',
            type: 'Physical Exam Summary',
            category: t('categories.physicalExam', 'Physical Exam'),
            subcategory: 'PE Summaries',
            referenceRange: null, // Initialize reference range field
            valuesByTimestamp: {},
            notesByTimestamp: {},
            flagsByTimestamp: {},
            trendData: []
        });

        // Initialize YBT limb length parameters EARLY so they can receive backend data
        const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
        const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';

        parametersMap.set('biometrics_balance_ybt_right_limb_length', {
            key: 'biometrics_balance_ybt_right_limb_length',
            parameterName: 'YBT Right Limb Length',
            type: 'Biometric',
            units: 'cm',
            category: 'Biometrics',
            subcategory: subcategoryDisplayName,
            referenceRange: null,
            valuesByTimestamp: {},
            notesByTimestamp: {},
            flagsByTimestamp: {},
            trendData: []
        });

        parametersMap.set('biometrics_balance_ybt_left_limb_length', {
            key: 'biometrics_balance_ybt_left_limb_length',
            parameterName: 'YBT Left Limb Length',
            type: 'Biometric',
            units: 'cm',
            category: 'Biometrics',
            subcategory: subcategoryDisplayName,
            referenceRange: null,
            valuesByTimestamp: {},
            notesByTimestamp: {},
            flagsByTimestamp: {},
            trendData: []
        });
        parametersMap.set('physicalExamSummaryFull', {
            key: 'physicalExamSummaryFull',
            parameterName: t('categories.physicalExam', 'Physical Exam') + ' ' + t('common.summary', 'Summary') + ' (Full)',
            type: 'Physical Exam Summary',
            category: t('categories.physicalExam', 'Physical Exam'),
            subcategory: 'PE Summaries',
            referenceRange: null, // Initialize reference range field
            valuesByTimestamp: {},
            notesByTimestamp: {},
            flagsByTimestamp: {},
            trendData: []
        });

        parameterRegistry.forEach(param => {
            const config = scoringConfigs.get(param.key);
            const unit = config?.sd?.unit || param.units || '';

            // Get the appropriate display name based on current language
            let displayName = param.displayName;
            try {
                // Force re-evaluation of parameter display name with current language
                displayName = getParameterDisplayName(param.key);
            } catch (error) {
                // Fallback to the original display name from registry
                displayName = param.displayName;
            }

            // Ensure proper category assignment based on parameter key
            let properCategory = param.category;
            let properSubcategory = param.subcategory;

            // Override category based on standardized parameter key to ensure correct categorization
            if (param.key.startsWith('vitals_')) {
                properCategory = 'Vitals';
                properSubcategory = 'Vitals';
            } else if (param.key.startsWith('labs_')) {
                properCategory = 'Lab Results';
                // Keep the existing subcategory for labs
            } else if (param.key.startsWith('pe_')) {
                properCategory = 'Physical Exam';
                // Keep the existing subcategory for PE
            } else if (param.key.startsWith('biometrics_')) {
                properCategory = 'Biometrics';
                // Keep the existing subcategory for biometrics
            } else if (param.key.startsWith('lifestyle_')) {
                properCategory = 'Lifestyle';
                // Keep the existing subcategory for lifestyle
            } else if (param.key.startsWith('imaging_')) {
                properCategory = 'Imaging';
                // Keep the existing subcategory for imaging
            }

            parametersMap.set(param.key, {
                key: param.key,
                parameterName: displayName,
                type: param.type,
                units: unit,
                category: properCategory,
                subcategory: properSubcategory,
                referenceRange: null, // Initialize reference range field
                valuesByTimestamp: {},
                notesByTimestamp: {},
                flagsByTimestamp: {},
                trendData: [],
            });
        });

        // Build category structure for the side menu from ALL parameters in parametersMap
        parametersMap.forEach((param) => {

            if (!categoryStructure[param.category]) {
                categoryStructure[param.category] = {
                    key: param.category,
                    title: param.category,
                    children: new Map(),
                };
            }
            if (param.subcategory) {
                // For imaging subcategories, use "Imaging_" prefix for the key to match filtering logic
                let subcategoryKey = param.subcategory;
                if (param.category === 'Imaging') {
                    // Convert display names to filter keys for imaging
                    const imagingDisplayToFilterMap = {
                        'Spleen US': 'Imaging_SpleenUS',
                        'Liver US': 'Imaging_LiverUS',
                        'Carotid US': 'Imaging_CarotidUS',
                        'EKG': 'Imaging_EKG',
                        'Bladder US': 'Imaging_BladderUS',
                        'Chest XR': 'Imaging_ChestXR',
                        'Heart US': 'Imaging_HeartUS',
                        'Gallbladder US': 'Imaging_GallbladderUS',
                        'Kidney US': 'Imaging_KidneyUS',
                        'Thyroid US': 'Imaging_ThyroidUS',
                        'Other Imaging': 'Imaging_OtherImaging'
                    };
                    subcategoryKey = imagingDisplayToFilterMap[param.subcategory] || `Imaging_${param.subcategory.replace(/\s+/g, '')}`;
                }
                // For lifestyle subcategories, use the raw key from lifestyleCategories to match filtering logic
                else if (param.category === 'Lifestyle') {
                    // Find the lifestyle category that matches this subcategory display name
                    const lifestyleCategory = lifestyleCategories.find(cat => cat.title === param.subcategory);
                    subcategoryKey = lifestyleCategory ? lifestyleCategory.key : param.subcategory.toLowerCase();
                }
                // For biometric subcategories, use the raw key from biometricCategories to match filtering logic
                else if (param.category === 'Biometrics') {
                    // Find the biometric category that matches this subcategory display name
                    const biometricCategory = biometricCategories.find(cat => cat.title === param.subcategory);
                    subcategoryKey = biometricCategory ? biometricCategory.key : param.subcategory.toLowerCase();
                }

                categoryStructure[param.category].children.set(param.subcategory, {
                    key: subcategoryKey,
                    title: param.subcategory,
                });
            }
        });







        let uniqueTimestamps = [];
        if (flowsheetData && flowsheetData.length > 0) {
            uniqueTimestamps = [...new Set(flowsheetData.map(item => item.date))].sort((a, b) => moment(a).unix() - moment(b).unix());

            const findStandardizedKey = (item) => {
                const originalName = item.name || '';



                // Handle Physical Exam Summary entries specially
                if (item.type === 'PhysicalExamSummary') {
                    return 'physicalExamSummarySimple';
                }
                if (item.type === 'Physical Exam Summary Full') {
                    return 'physicalExamSummaryFull';
                }

                // Handle Survey entries specially
                if (item.type === 'Survey') {
                    // For survey entries, use the name as the key since they're already descriptive
                    // This allows them to be displayed in the flowsheet
                    return `survey_${originalName.replace(/\s+/g, '_').toLowerCase()}`;
                }

                // First attempt direct match in registry - for data that already uses standardized parameter names
                if (parametersMap.has(originalName)) {
                    return originalName;
                }

                // Vitals name standardization for display names that need conversion
                if (item.type === 'Vital') {
                    // Create reverse mapping from display names to standardized keys
                    // Include all possible variations that might come from backend
                    const vitalsDisplayToStandardizedMap = {
                        // Standard display names
                        'Temperature': 'vitals_temp_c',
                        'Systolic Blood Pressure': 'vitals_sbp',
                        'Diastolic Blood Pressure': 'vitals_dbp',
                        'Resting Heart Rate': 'vitals_rhr',
                        'Heart Rate': 'vitals_rhr', // Alternative name
                        'Respiratory Rate': 'vitals_rr',
                        'Mean Arterial Pressure': 'vitals_map',
                        'Mean Arterial Pressure (MAP)': 'vitals_map', // Alternative name
                        'Pulse Pressure': 'vitals_pp',
                        'Pulse Pressure (PP)': 'vitals_pp', // Alternative name
                        'Oxygen Saturation': 'vitals_spo2',
                        'SIRS Score': 'vitals_sirs',
                        'Heart Rate Variability': 'vitals_hrv',
                        'VO₂ Max': 'vitals_vo2max',
                        'VO2 Max': 'vitals_vo2max', // Alternative name
                        'Pain Scale': 'vitals_pain',
                        'Pain Level': 'vitals_pain', // Alternative name
                        'Weight': 'vitals_weight',
                        'Height': 'vitals_height',
                        'Body Mass Index': 'vitals_bmi',
                        'BMI': 'vitals_bmi', // Alternative name
                        // Legacy database names (camelCase format from database)
                        'temperature': 'vitals_temp_c',
                        'heartRate': 'vitals_rhr',
                        'respiratoryRate': 'vitals_rr',
                        'bloodPressureSystolic': 'vitals_sbp',
                        'bloodPressureDiastolic': 'vitals_dbp',
                        'oxygenSaturation': 'vitals_spo2',
                        'weight': 'vitals_weight',
                        'height': 'vitals_height'
                    };

                    const standardizedVitalKey = vitalsDisplayToStandardizedMap[originalName];
                    if (standardizedVitalKey && parametersMap.has(standardizedVitalKey)) {
                        return standardizedVitalKey;
                    }
                }

                // Lab name standardization for display names that need conversion
                if (item.type === 'Lab') {
                    // First check if it's already a standardized key
                    if (originalName.startsWith('labs_') && parametersMap.has(originalName)) {
                        return originalName;
                    }

                    // Then try display name to standardized conversion
                    const standardizedLabKey = displayNameToStandardized(originalName);
                    if (parametersMap.has(standardizedLabKey)) {
                        return standardizedLabKey;
                    }
                }

                // Physical Exam name standardization for display names that need conversion
                if (item.type === 'Physical Exam Detail') {
                    // First check if it's already a standardized key
                    if (originalName.startsWith('pe_') && parametersMap.has(originalName)) {
                        return originalName;
                    }
                    // Create reverse mapping from display names to standardized keys for common PE parameters
                    const physicalExamDisplayToStandardizedMap = {
                        'Alopecia': 'pe_integumentary_alopecia',
                        'Scalp lesions': 'pe_integumentary_scalp_lesions',
                        'Arm lesions': 'pe_integumentary_arm_lesions',
                        'Leg lesions': 'pe_integumentary_leg_lesions',
                        'Finger nails': 'pe_integumentary_finger_nails',
                        'Toe nails': 'pe_integumentary_toe_nails',
                        'Jaundice': 'pe_integumentary_jaundice',
                        'Pallor': 'pe_integumentary_pallor',
                        'Bruising': 'pe_integumentary_bruising',
                        'Surgical scar': 'pe_integumentary_surgical_scar',
                        'Caput medusa': 'pe_integumentary_caput_medusa',
                        'Skin turgor': 'pe_integumentary_skin_turgor',
                        'Conjunctiva': 'pe_heent_eyes_conjunctiva',
                        'Sclera': 'pe_heent_eyes_sclera',
                        'Pupils': 'pe_heent_eyes_pupils',
                        'Visual acuity': 'pe_heent_eyes_visual_acuity',
                        'Extraocular movements': 'pe_heent_eyes_extraocular_movements',
                        'Fundoscopy': 'pe_heent_eyes_fundoscopy',
                        'Ear canals': 'pe_heent_ears_ear_canals',
                        'Tympanic membranes': 'pe_heent_ears_tympanic_membranes',
                        'Weber test': 'pe_heent_ears_weber_test',
                        'Rinne test': 'pe_heent_ears_rinne_test',
                        'Nasal mucosa': 'pe_heent_nose_nasal_mucosa',
                        'Nasal septum': 'pe_heent_nose_nasal_septum',
                        'Sinuses': 'pe_heent_nose_sinuses',
                        'Lips': 'pe_heent_mouth_lips',
                        'Teeth': 'pe_heent_mouth_teeth',
                        'Gums': 'pe_heent_mouth_gums',
                        'Tongue': 'pe_heent_mouth_tongue',
                        'Throat': 'pe_heent_mouth_throat',
                        'Tonsils': 'pe_heent_mouth_tonsils',
                        'Thyroid': 'pe_heent_neck_thyroid',
                        'Lymph nodes': 'pe_heent_neck_lymph_nodes',
                        'Carotid pulses': 'pe_heent_neck_carotid_pulses',
                        'JVD': 'pe_heent_neck_jvd',
                        'Heart sounds': 'pe_cardio_heart_sounds',
                        'Heart rhythm': 'pe_cardio_heart_rhythm',
                        'Murmurs': 'pe_cardio_murmurs',
                        'Pedal edema': 'pe_cardio_pedal_edema',
                        'Peripheral pulses': 'pe_cardio_peripheral_pulses',
                        'Capillary refill': 'pe_cardio_capillary_refill',
                        'Chest expansion': 'pe_resp_chest_expansion',
                        'Percussion': 'pe_resp_percussion',
                        'Breath sounds': 'pe_resp_breath_sounds',
                        'Crackles': 'pe_resp_crackles',
                        'Wheezes': 'pe_resp_wheezes',
                        'Bowel sounds': 'pe_gi_bowel_sounds',
                        'Abdominal tenderness': 'pe_gi_abdominal_tenderness',
                        'Hepatomegaly': 'pe_gi_hepatomegaly',
                        'Splenomegaly': 'pe_gi_splenomegaly',
                        'Masses': 'pe_gi_masses',
                        'Cervical lordosis': 'pe_msk_cervical_lordosis',
                        'Thoracic kyphosis': 'pe_msk_thoracic_kyphosis',
                        'Lumbar lordosis': 'pe_msk_lumbar_lordosis',
                        'Visible scoliosis': 'pe_msk_visible_scoliosis',
                        'Cervical tenderness': 'pe_msk_cervical_tenderness',
                        'Thoracic tenderness': 'pe_msk_thoracic_tenderness',
                        'Lumbar tenderness': 'pe_msk_lumbar_tenderness',
                        'Mental status': 'pe_neuro_mental_status',
                        'Cranial nerves': 'pe_neuro_cranial_nerves',
                        'Motor strength': 'pe_neuro_motor_strength',
                        'Reflexes': 'pe_neuro_reflexes',
                        'Sensation': 'pe_neuro_sensation',
                        'Coordination': 'pe_neuro_coordination',
                        'Gait': 'pe_neuro_gait',
                        'Patellar reflex': 'pe_neuro_patellar_reflex'
                    };

                    const standardizedPEKey = physicalExamDisplayToStandardizedMap[originalName];
                    if (standardizedPEKey && parametersMap.has(standardizedPEKey)) {
                        return standardizedPEKey;
                    }
                }

                // Biometric name standardization for display names that need conversion
                if (item.type === 'Biometric') {


                    // First check if it's already a standardized key
                    if (originalName.startsWith('biometrics_') && parametersMap.has(originalName)) {
                        return originalName;
                    }



                    // Special handling for YBT limb length parameters that are dynamically created
                    if (originalName === 'biometrics_balance_ybt_right_limb_length' ||
                        originalName === 'biometrics_balance_ybt_left_limb_length') {
                        return originalName;
                    }

                    // Create reverse mapping from display names to standardized keys for biometric parameters
                    const biometricDisplayToStandardizedMap = {
                        'TMT-A Time': 'biometrics_cognition_tmtA_time',
                        'TMT-A Errors': 'biometrics_cognition_tmtA_errors',
                        'TMT-B Time': 'biometrics_cognition_tmtB_time',
                        'TMT-B Errors': 'biometrics_cognition_tmtB_errors',
                        'Word Recall - Immediate Trial 1': 'biometrics_cognition_wordRecall_immediate1',
                        'Word Recall - Immediate Trial 2': 'biometrics_cognition_wordRecall_immediate2',
                        'Word Recall - Delayed': 'biometrics_cognition_wordRecall_delayed',
                        'Visual Acuity - Right Eye': 'biometrics_vision_visual_acuity_right',
                        'Visual Acuity - Left Eye': 'biometrics_vision_visual_acuity_left',
                        'Color Vision': 'biometrics_vision_color_vision',
                        'YBT Anterior - Right': 'biometrics_balance_ybt_anterior_right',
                        'YBT Anterior - Left': 'biometrics_balance_ybt_anterior_left',
                        'YBT Posteromedial - Right': 'biometrics_balance_ybt_posteromedial_right',
                        'YBT Posteromedial - Left': 'biometrics_balance_ybt_posteromedial_left',
                        'YBT Posterolateral - Right': 'biometrics_balance_ybt_posterolateral_right',
                        'YBT Posterolateral - Left': 'biometrics_balance_ybt_posterolateral_left',
                        'YBT Right Limb Length': 'biometrics_balance_ybt_right_limb_length',
                        'YBT Left Limb Length': 'biometrics_balance_ybt_left_limb_length',
                        'Y-Balance Test - Right Limb Length': 'biometrics_balance_ybt_right_limb_length',
                        'Y-Balance Test - Left Limb Length': 'biometrics_balance_ybt_left_limb_length',
                        'Y-Balance Test - Right Composite Reach': 'biometrics_balance_ybt_right_composite_reach',
                        'Y-Balance Test - Left Composite Reach': 'biometrics_balance_ybt_left_composite_reach',
                        'Y-Balance Test - Composite Deviation': 'biometrics_balance_ybt_composite_deviation',
                        'Y-Balance Test - Anterior Deviation': 'biometrics_balance_ybt_anterior_deviation',
                        'Y-Balance Test - Posteromedial Deviation': 'biometrics_balance_ybt_posteromedial_deviation',
                        'Y-Balance Test - Posterolateral Deviation': 'biometrics_balance_ybt_posterolateral_deviation',
                        'Y-Balance Test Performance Ratio': 'biometrics_balance_ybt_performance_ratio',
                        'Body Fat Percentage': 'biometrics_body_composition_body_fat_percentage',
                        'Muscle Mass': 'biometrics_body_composition_muscle_mass',
                        'Bone Density': 'biometrics_body_composition_bone_density',
                        'Visceral Fat': 'biometrics_body_composition_visceral_fat',
                        'Waist Circumference': 'biometrics_body_composition_waist_circumference',
                        'Hip Circumference': 'biometrics_body_composition_hip_circumference'
                    };

                    const standardizedBiometricKey = biometricDisplayToStandardizedMap[originalName];
                    if (standardizedBiometricKey && parametersMap.has(standardizedBiometricKey)) {
                        return standardizedBiometricKey;
                    }
                }

                // Lifestyle name standardization for display names that need conversion
                if (item.type === 'Questionnaire' || item.type === 'BooleanCheckbox' || item.type === 'BooleanCheckboxOption') {
                    // First check if it's already a standardized key
                    if (originalName.startsWith('lifestyle_')) {
                        if (parametersMap.has(originalName)) {
                            return originalName;
                        }
                    }

                    // Create reverse mapping from display names to standardized keys for lifestyle parameters
                    const lifestyleDisplayToStandardizedMap = {
                        'Protein intake': 'lifestyle_nutrition_protein_intake',
                        'Refined carbs intake': 'lifestyle_nutrition_refined_carbs_intake',
                        'Fiber intake': 'lifestyle_nutrition_fiber_intake',
                        'Sugar intake': 'lifestyle_nutrition_sugar_intake',
                        'Fried food intake': 'lifestyle_nutrition_fried_food_intake',
                        'Healthy oil use': 'lifestyle_nutrition_healthy_oil_use',
                        'Fruit intake': 'lifestyle_nutrition_fruit_intake',
                        'Vegetables intake': 'lifestyle_nutrition_vegetables_intake',
                        'Seeds nuts intake': 'lifestyle_nutrition_seeds_nuts_intake',
                        'Alcohol intake': 'lifestyle_nutrition_alcohol_intake',
                        'Caffeine intake': 'lifestyle_nutrition_caffeine_intake',
                        'Water intake': 'lifestyle_nutrition_water_intake',
                        'Exercise frequency': 'lifestyle_movement_exercise_frequency',
                        'Exercise intensity': 'lifestyle_movement_exercise_intensity',
                        'Exercise duration': 'lifestyle_movement_exercise_duration',
                        'Walking frequency': 'lifestyle_movement_walking_frequency',
                        'Sitting hours': 'lifestyle_movement_sitting_hours',
                        'Sleep duration': 'lifestyle_recovery_sleep_duration',
                        'Sleep quality': 'lifestyle_recovery_sleep_quality',
                        'Sleep consistency': 'lifestyle_recovery_sleep_consistency',
                        'Stress level': 'lifestyle_emotion_stress_level',
                        'Mood': 'lifestyle_emotion_mood',
                        'Social support': 'lifestyle_emotion_social_support',
                        'Air quality': 'lifestyle_environment_air_quality',
                        'Noise level': 'lifestyle_environment_noise_level',
                        'Chemical exposure': 'lifestyle_environment_chemical_exposure',
                        'Morning outdoor light days weekly': 'lifestyle_environment_morning_outdoor_light_days_weekly'
                    };

                    const standardizedLifestyleKey = lifestyleDisplayToStandardizedMap[originalName];
                    if (standardizedLifestyleKey && parametersMap.has(standardizedLifestyleKey)) {
                        return standardizedLifestyleKey;
                    }
                }

                // Imaging name standardization for display names that need conversion
                if (item.type === 'ImagingDetail') {
                    // First check if it's already a standardized key
                    if (originalName.startsWith('imaging_') && parametersMap.has(originalName)) {
                        return originalName;
                    }

                    // Create reverse mapping from display names to standardized keys for imaging parameters
                    const imagingDisplayToStandardizedMap = {
                        'Craniocaudal length': 'imaging_spleen_us_craniocaudal_length',
                        'Capsular edge nodularity': 'imaging_spleen_us_capsular_edge_nodularity',
                        'Parenchymal echogenicity': 'imaging_spleen_us_parenchymal_echogenicity',
                        'Largest lesion diameter': 'imaging_spleen_us_largest_lesion_diameter',
                        'Splenic vein diameter': 'imaging_spleen_us_splenic_vein_diameter',
                        'PSDR': 'imaging_spleen_us_psdr',
                        'Heart Rate': 'imaging_ekg_heart_rate',
                        'PR Interval': 'imaging_ekg_pr_interval',
                        'QRS Duration': 'imaging_ekg_qrs_duration',
                        'QT Interval': 'imaging_ekg_qt_interval',
                        'QTc': 'imaging_ekg_qtc',
                        'Axis': 'imaging_ekg_axis',
                        'Rhythm': 'imaging_ekg_rhythm',
                        'Interpretation': 'imaging_ekg_interpretation'
                    };

                    const standardizedImagingKey = imagingDisplayToStandardizedMap[originalName];
                    if (standardizedImagingKey && parametersMap.has(standardizedImagingKey)) {
                        return standardizedImagingKey;
                    }
                }

                // Log unmapped parameters for debugging - these should be fixed at the data source
                console.warn(`[PatientFlowsheet] Unmapped parameter found - should be using standardized name:`, {
                    name: originalName,
                    type: item.type,
                    category: item.category,
                    expectedFormat: item.type === 'Vital' ? 'vitals_*' :
                                   item.type === 'Biometric' ? 'biometrics_*' :
                                   item.type === 'Physical Exam Detail' ? 'pe_*' :
                                   item.type === 'Physical Exam Summary Full' ? 'physicalExamSummaryFull' :
                                   item.type === 'PhysicalExamSummary' ? 'physicalExamSummarySimple' :
                                   item.type === 'Questionnaire' ? 'lifestyle_*' :
                                   item.type === 'ImagingDetail' ? 'imaging_*' :
                                   item.type === 'Lab' ? 'labs_*' : 'unknown'
                });

                return null;
            };











            flowsheetData.forEach(item => {
                if (!item || !item.date || !item.type) return;



                const standardizedKey = findStandardizedKey(item);



                if (standardizedKey) {
                    let paramDataToUpdate = parametersMap.get(standardizedKey);

                    // Handle Survey entries specially - create parameter entry on the fly
                    if (!paramDataToUpdate && standardizedKey.startsWith('survey_')) {
                        paramDataToUpdate = {
                            key: standardizedKey,
                            parameterName: item.name, // Use the original name as display name
                            displayName: item.name, // Also set displayName for compatibility
                            category: 'Surveys',
                            subcategory: 'Surveys',
                            type: item.type,
                            units: '', // Surveys don't have units
                            referenceRange: null, // Surveys don't have reference ranges
                            valuesByTimestamp: {},
                            notesByTimestamp: {},
                            flagsByTimestamp: {},
                            trendData: [], // Initialize trendData array
                            originalItemName: item.name
                        };
                        parametersMap.set(standardizedKey, paramDataToUpdate);
                    }

                    if (paramDataToUpdate) {
                        // Validate data before processing to prevent phantom timestamps and wrong values
                        if (!item.date || !moment(item.date).isValid()) {
                            console.warn(`[PatientFlowsheet] Skipping item with invalid date:`, item);
                            return null;
                        }

                        // Additional validation for lab values to prevent display of suspicious values
                        if (item.type === 'Lab' && item.value !== null && item.value !== undefined) {
                            const numericValue = parseFloat(item.value);

                            if (!isNaN(numericValue)) {
                                // Validate HDL/LDL values to prevent display of unconverted mmol/L values
                                if (standardizedKey === 'labs_lipid_hdl' && numericValue < 15) {
                                    console.warn(`[PatientFlowsheet] Skipping suspicious HDL value: ${numericValue} mg/dL (likely unconverted mmol/L)`);
                                    return null;
                                }

                                if (standardizedKey === 'labs_lipid_ldl' && numericValue < 50) {
                                    console.warn(`[PatientFlowsheet] Skipping suspicious LDL value: ${numericValue} mg/dL (likely unconverted mmol/L)`);
                                    return null;
                                }
                            }
                        }

                        // Update values, notes, flags etc.
                        let renderableValue = item.value;
                        if (typeof item.value === 'object' && item.value !== null) {
                            try { renderableValue = JSON.stringify(item.value); } catch (e) { renderableValue = '[Object]'; }
                        } else if (Array.isArray(item.value)) {
                            renderableValue = `[Array with ${item.value.length} items]`;
                        }

                        // Fix visual acuity values: convert numeric values to Snellen format for vision parameters
                        if (standardizedKey && standardizedKey.includes('biometrics_vision_visualAcuity')) {
                            // Only convert if it's a number and not already in Snellen format
                            if (typeof renderableValue === 'number') {
                                const valueStr = renderableValue.toString();

                                // Handle 4-digit numbers like 2020, 2025, 2040
                                if (valueStr.length === 4 && valueStr.startsWith('20')) {
                                    const denominator = valueStr.substring(2);
                                    const denominatorNum = parseInt(denominator, 10);
                                    if (denominatorNum > 0 && denominatorNum < 1000) {
                                        renderableValue = `20/${denominator}`;
                                    }
                                }
                                // Handle simple numeric values - these represent the denominator only (e.g., 20 means 20/20, 40 means 20/40)
                                // Extended range to handle edge cases like 6, 7 that might represent 20/60, 20/70
                                else if (valueStr.length <= 3 && !isNaN(renderableValue) && renderableValue >= 1 && renderableValue <= 400) {
                                    // Special handling for single digits that might represent tens (6 -> 60, 7 -> 70)
                                    let denominator = renderableValue;
                                    if (renderableValue < 10 && renderableValue >= 1) {
                                        denominator = renderableValue * 10; // 6 -> 60, 7 -> 70
                                    }
                                    renderableValue = `20/${denominator}`;
                                }
                            }
                        }

                        paramDataToUpdate.valuesByTimestamp[item.date] = renderableValue;
                        if (item.notes) paramDataToUpdate.notesByTimestamp[item.date] = item.notes;

                        // Ensure proper category assignment based on standardized key
                        if (standardizedKey === 'physicalExamSummarySimple' || standardizedKey === 'physicalExamSummaryFull') {
                            paramDataToUpdate.category = 'Physical Exam';
                            paramDataToUpdate.subcategory = 'PE Summaries';
                        } else if (standardizedKey.startsWith('vitals_')) {
                            paramDataToUpdate.category = 'Vitals';
                            paramDataToUpdate.subcategory = 'Vitals';
                        } else if (standardizedKey.startsWith('labs_')) {
                            paramDataToUpdate.category = 'Lab Results';
                            // Set subcategory based on lab type
                            if (standardizedKey.includes('_cbc_')) {
                                paramDataToUpdate.subcategory = 'CBC';
                            } else if (standardizedKey.includes('_lipid_')) {
                                paramDataToUpdate.subcategory = 'Lipid';
                            } else if (standardizedKey.includes('_glucose_')) {
                                paramDataToUpdate.subcategory = 'Glucose';
                            } else if (standardizedKey.includes('_hepatology_')) {
                                paramDataToUpdate.subcategory = 'Hepatology';
                            } else if (standardizedKey.includes('_renal_')) {
                                paramDataToUpdate.subcategory = 'Renal';
                            } else if (standardizedKey.includes('_electrolytes_')) {
                                paramDataToUpdate.subcategory = 'Electrolytes';
                            } else if (standardizedKey.includes('_thyroid_')) {
                                paramDataToUpdate.subcategory = 'Thyroid';
                            } else if (standardizedKey.includes('_cardiac_')) {
                                paramDataToUpdate.subcategory = 'Cardiac';
                            } else if (standardizedKey.includes('_coagulation_')) {
                                paramDataToUpdate.subcategory = 'Coagulation';
                            } else if (standardizedKey.includes('_inflammation_')) {
                                paramDataToUpdate.subcategory = 'Inflammation';
                            } else if (standardizedKey.includes('_urine_')) {
                                paramDataToUpdate.subcategory = 'Urine';
                            } else if (standardizedKey.includes('_vitamins_')) {
                                paramDataToUpdate.subcategory = 'Vitamins';
                            }
                            // Keep existing subcategory for other labs
                        } else if (standardizedKey.startsWith('pe_')) {
                            paramDataToUpdate.category = 'Physical Exam';
                            // Keep existing subcategory for PE
                        } else if (standardizedKey.startsWith('biometrics_')) {
                            paramDataToUpdate.category = 'Biometrics';
                            // Keep existing subcategory for biometrics
                        } else if (standardizedKey.startsWith('lifestyle_')) {
                            paramDataToUpdate.category = 'Lifestyle';
                            // Keep existing subcategory for lifestyle
                        } else if (standardizedKey.startsWith('imaging_')) {
                            paramDataToUpdate.category = 'Imaging';
                            // Keep existing subcategory for imaging
                        } else if (standardizedKey.startsWith('survey_')) {
                            paramDataToUpdate.category = 'Surveys';
                            paramDataToUpdate.subcategory = 'Surveys';
                        }

                        // Store original item name for debugging
                        paramDataToUpdate.originalItemName = item.name;

                        // Extract subcategory from backend structuredValue if available
                        // This will be overridden later for specific data types to ensure consistency
                        if (item.structuredValue && item.structuredValue.subCategory) {
                            // Convert raw subcategory keys to display names for consistency with parameter registry
                            let subcategoryValue = item.structuredValue.subCategory;

                            // For biometrics, convert raw keys like 'cognition' to display names like 'Cognition'
                            if (standardizedKey.startsWith('biometrics_')) {
                                const biometricCategory = biometricCategories.find(cat => cat.key === subcategoryValue);
                                subcategoryValue = biometricCategory ? biometricCategory.title : subcategoryValue;
                            }
                            // For lifestyle, convert raw keys like 'nutrition' to display names like 'Nutrition'
                            else if (standardizedKey.startsWith('lifestyle_')) {
                                const lifestyleCategory = lifestyleCategories.find(cat => cat.key === subcategoryValue);
                                subcategoryValue = lifestyleCategory ? lifestyleCategory.title : subcategoryValue;
                            }
                            // For imaging, the subcategory should already be in display format, but handle edge cases
                            else if (standardizedKey.startsWith('imaging_')) {
                                // If it's a raw key format like 'spleen_us', convert to display name
                                if (subcategoryValue.includes('_')) {
                                    const parts = subcategoryValue.split('_');
                                    if (parts.length >= 2) {
                                        const organOrStudy = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
                                        const modality = parts[1].toUpperCase();
                                        subcategoryValue = `${organOrStudy} ${modality}`;
                                    }
                                }
                            }

                            // Set the subcategory - this may be overridden later for specific data types
                            paramDataToUpdate.subcategory = subcategoryValue;
                        }

                        // Ensure proper category assignment based on standardized key
                        if (standardizedKey.startsWith('vitals_')) {
                            paramDataToUpdate.category = 'Vitals';
                        } else if (standardizedKey.startsWith('labs_')) {
                            // Labs should always have category = 'Lab Results'
                            paramDataToUpdate.category = 'Lab Results';
                        
                            // Set subcategory based on lab type - force override
                            if (standardizedKey.includes('_cbc_')) {
                                paramDataToUpdate.subcategory = 'CBC';
                            } else if (standardizedKey.includes('_lipid_')) {
                                paramDataToUpdate.subcategory = 'Lipid';
                            } else if (standardizedKey.includes('_glucose_')) {
                                paramDataToUpdate.subcategory = 'Glucose';
                            } else if (standardizedKey.includes('_hepatology_')) {
                                paramDataToUpdate.subcategory = 'Hepatology';
                            } else if (standardizedKey.includes('_renal_')) {
                                paramDataToUpdate.subcategory = 'Renal';
                            } else if (standardizedKey.includes('_electrolytes_')) {
                                paramDataToUpdate.subcategory = 'Electrolytes';
                            } else if (standardizedKey.includes('_thyroid_')) {
                                paramDataToUpdate.subcategory = 'Thyroid';
                            } else if (standardizedKey.includes('_cardiac_')) {
                                paramDataToUpdate.subcategory = 'Cardiac';
                            } else if (standardizedKey.includes('_coagulation_')) {
                                paramDataToUpdate.subcategory = 'Coagulation';
                            } else if (standardizedKey.includes('_inflammation_')) {
                                paramDataToUpdate.subcategory = 'Inflammation';
                            } else if (standardizedKey.includes('_urine_')) {
                                paramDataToUpdate.subcategory = 'Urine';
                            } else if (standardizedKey.includes('_vitamins_')) {
                                paramDataToUpdate.subcategory = 'Vitamins';
                            } else {
                                // The subcategory contains the specific lab category (e.g., 'CBC', 'Lipid')
                                // Get the subcategory from the authoritative parameterRegistry
                                const registryEntry = parameterRegistry.find(p => p.key === standardizedKey);
                                if (registryEntry && registryEntry.subcategory) {
                                    paramDataToUpdate.subcategory = registryEntry.subcategory;
                                } else if (item.structuredValue?.subCategory) {
                                    // Fallback to the backend-provided subcategory if not in registry
                                    paramDataToUpdate.subcategory = item.structuredValue.subCategory;
                                }
                            }
                        } else if (standardizedKey.startsWith('biometrics_')) {
                            paramDataToUpdate.category = 'Biometrics';
                            // Always use subcategory from database if available - this overrides the registry subcategory
                            if (item.structuredValue?.subCategory) {
                                const rawSubcategory = item.structuredValue.subCategory;
                                const biometricCategory = biometricCategories.find(cat => cat.key === rawSubcategory);
                                // THIS IS THE KEY CHANGE: Ensure we always assign the display title.
                                paramDataToUpdate.subcategory = biometricCategory ? biometricCategory.title : rawSubcategory;
                            }
                        } else if (standardizedKey.startsWith('pe_')) {
                            // Physical exam parameters should always have category = 'Physical Exam'
                            // The subcategory contains the section (e.g., 'Integumentary', 'Cardiovascular')
                            paramDataToUpdate.category = 'Physical Exam';

                            // Ensure subcategory is available for filtering
                            if (!paramDataToUpdate.subcategory) {
                                // First try to get from structuredValue
                                if (item.structuredValue?.subCategory) {
                                    paramDataToUpdate.subcategory = item.structuredValue.subCategory;
                                } else {
                                    // Fallback: extract subcategory from standardized parameter name
                                    // Find the section that contains this parameter
                                    for (const section of physicalExamSectionsFull) {
                                        const parameterExists = section.parameters.some(param => param.key === standardizedKey);
                                        if (parameterExists) {
                                            paramDataToUpdate.subcategory = section.title;
                                            break;
                                        }
                                    }
                                    // If still no subcategory found, use a default
                                    if (!paramDataToUpdate.subcategory) {
                                        paramDataToUpdate.subcategory = 'Physical Exam';
                                    }
                                }
                            }
                        } else if (standardizedKey.startsWith('lifestyle_')) {
                            // Lifestyle should always have category = 'Lifestyle'
                            // The subcategory contains the specific lifestyle category (e.g., 'nutrition', 'movement')
                            paramDataToUpdate.category = 'Lifestyle';
                            // Ensure subcategory is available for filtering - convert raw keys to display names
                            if (!paramDataToUpdate.subcategory && item.structuredValue?.subCategory) {
                                const rawSubcategory = item.structuredValue.subCategory;
                                const lifestyleCategory = lifestyleCategories.find(cat => cat.key === rawSubcategory);
                                paramDataToUpdate.subcategory = lifestyleCategory ? lifestyleCategory.title : rawSubcategory;
                            }
                        } else if (standardizedKey.startsWith('imaging_')) {
                            // Imaging should always have category = 'Imaging'
                            // The subcategory contains the study type (e.g., 'Chest XR', 'Bladder US')
                            paramDataToUpdate.category = 'Imaging';



                            // Ensure subcategory is available for filtering - convert raw keys to display names if needed
                            if (!paramDataToUpdate.subcategory && item.structuredValue?.subCategory) {
                                let subcategoryValue = item.structuredValue.subCategory;
                                // If it's a raw key format like 'spleen_us', convert to display name
                                if (subcategoryValue.includes('_')) {
                                    const parts = subcategoryValue.split('_');
                                    if (parts.length >= 2) {
                                        const organOrStudy = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
                                        const modality = parts[1].toUpperCase();
                                        subcategoryValue = `${organOrStudy} ${modality}`;
                                    }
                                }
                                paramDataToUpdate.subcategory = subcategoryValue;

                            }
                        }

                        if (paramDataToUpdate.type === 'Lab') {
                            paramDataToUpdate.flagsByTimestamp[item.date] = item.flag;

                            // Set reference range if available and not already set
                            if (item.referenceRange && !paramDataToUpdate.referenceRange) {
                                paramDataToUpdate.referenceRange = item.referenceRange;
                            }

                            const numericValue = parseFloat(item.value);
                            if (!isNaN(numericValue)) {
                                paramDataToUpdate.trendData.push({ date: item.date, value: numericValue });
                            }
                        }

                        // Verbose individual parameter mapping removed for performance
                    }
                } else {
                    console.warn(`Could not map backend item to a standardized key:`, item);
                }
            });
          if (patientDemographics) {
            uniqueTimestamps.forEach(ts => {
              // Use standardized parameter names for weight and height
              const weightData = parametersMap.get('vitals_weight');
              const heightData = parametersMap.get('vitals_height');
              const weightKg = weightData?.valuesByTimestamp[ts] ? parseFloat(weightData.valuesByTimestamp[ts]) : null;
              const heightCm = heightData?.valuesByTimestamp[ts] ? parseFloat(heightData.valuesByTimestamp[ts]) : null;

              if (weightKg !== null && heightCm !== null) {
                const bmi = calculateBMI(weightKg, heightCm);
                if (bmi !== null) {
                  // Use standardized parameter name for BMI
                  const bmiKey = 'vitals_bmi';
                  if (!parametersMap.has(bmiKey)) {
                    parametersMap.set(bmiKey, { key: bmiKey, parameterName: 'Body Mass Index (BMI)', type: 'Vital', units: 'kg/m²', category: 'Vitals', referenceRange: null, valuesByTimestamp: {}, notesByTimestamp: {}, flagsByTimestamp: {}, trendData: [] });
                  }
                  parametersMap.get(bmiKey).valuesByTimestamp[ts] = bmi; // bmi is already toFixed(1)
                }
              }

              if (patientDemographics) {
                const lastWeight = weightData?.valuesByTimestamp[ts] ? parseFloat(weightData.valuesByTimestamp[ts]) : (weightData?.lastValue ? parseFloat(weightData.lastValue) : null);
                const lastHeight = heightData?.valuesByTimestamp[ts] ? parseFloat(heightData.valuesByTimestamp[ts]) : (heightData?.lastValue ? parseFloat(heightData.lastValue) : null);

                if (lastWeight !== null && lastHeight !== null && patientDemographics.age !== null && patientDemographics.sex) {
                  const ree = calculateREE(lastWeight, lastHeight, patientDemographics.age, patientDemographics.sex);
                  if (ree !== null) {
                    const reeKey = 'biometrics_biomeasurement_ree';
                    if (!parametersMap.has(reeKey)) {
                      const biomeasurementCategory = biometricCategories.find(cat => cat.key === 'biomeasurement');
                      const subcategoryDisplayName = biomeasurementCategory ? biomeasurementCategory.title : 'Biomeasurement';
                      parametersMap.set(reeKey, { key: reeKey, parameterName: 'Resting Energy Expenditure (REE)', type: 'Biometric', units: 'kcal/day', category: 'Biometrics', subcategory: subcategoryDisplayName, referenceRange: null, valuesByTimestamp: {}, notesByTimestamp: {}, flagsByTimestamp: {}, trendData: [] });
                    }
                    parametersMap.get(reeKey).valuesByTimestamp[ts] = ree.toFixed(0);

                    const modActResponse = parametersMap.get('lifestyle_movement_moderate_activity_days')?.valuesByTimestamp[ts] || parametersMap.get('lifestyle_movement_moderate_activity_days')?.lastValue;
                    const vigActResponse = parametersMap.get('lifestyle_movement_vigorous_activity_days')?.valuesByTimestamp[ts] || parametersMap.get('lifestyle_movement_vigorous_activity_days')?.lastValue;
                    const incWalkResponse = parametersMap.get('lifestyle_movement_incidental_walking_time')?.valuesByTimestamp[ts] || parametersMap.get('lifestyle_movement_incidental_walking_time')?.lastValue;
                    const sedHrsResponse = parametersMap.get('lifestyle_movement_sedentary_hours')?.valuesByTimestamp[ts] || parametersMap.get('lifestyle_movement_sedentary_hours')?.lastValue;
                    const tee = calculateTEE(ree, modActResponse, vigActResponse, incWalkResponse, sedHrsResponse);
                    if (tee !== null) {
                      const teeKey = 'biometrics_biomeasurement_tee';
                      if (!parametersMap.has(teeKey)) {
                        const biomeasurementCategory = biometricCategories.find(cat => cat.key === 'biomeasurement');
                        const subcategoryDisplayName = biomeasurementCategory ? biomeasurementCategory.title : 'Biomeasurement';
                        parametersMap.set(teeKey, { key: teeKey, parameterName: 'Total Energy Expenditure (TEE)', type: 'Biometric', units: 'kcal/day', category: 'Biometrics', subcategory: subcategoryDisplayName, referenceRange: null, valuesByTimestamp: {}, notesByTimestamp: {}, flagsByTimestamp: {}, trendData: [] });
                      }
                      parametersMap.get(teeKey).valuesByTimestamp[ts] = tee.toFixed(0);
                    }
                  }
                }
              }

              // Calculate MAP and PP
              const sbpData = parametersMap.get('vitals_sbp');
              const dbpData = parametersMap.get('vitals_dbp');
              const sbpVal = sbpData?.valuesByTimestamp[ts] ? parseFloat(sbpData.valuesByTimestamp[ts]) : null;
              const dbpVal = dbpData?.valuesByTimestamp[ts] ? parseFloat(dbpData.valuesByTimestamp[ts]) : null;

              if (sbpVal !== null && dbpVal !== null) {
                const mapVal = calculateMAP(sbpVal, dbpVal);
                if (mapVal !== null && parametersMap.has('vitals_map')) {
                  parametersMap.get('vitals_map').valuesByTimestamp[ts] = mapVal.toFixed(1);
                }
                const ppVal = calculatePP(sbpVal, dbpVal);
                if (ppVal !== null && parametersMap.has('vitals_pp')) {
                  parametersMap.get('vitals_pp').valuesByTimestamp[ts] = ppVal.toFixed(0);
                }
              }

              // Calculate non-HDL Cholesterol (only if not already provided by backend)
              const nonHdlTotalCholesterolData = parametersMap.get('labs_lipid_chol_total');
              const nonHdlHdlData = parametersMap.get('labs_lipid_hdl');
              const nonHDLCholesterolKey = 'labs_lipid_non_hdl';

              if (nonHdlTotalCholesterolData && nonHdlHdlData) {
                const totalCholesterolValStr = nonHdlTotalCholesterolData.valuesByTimestamp[ts];
                const hdlValStr = nonHdlHdlData.valuesByTimestamp[ts];
                const totalCholesterolVal = (totalCholesterolValStr !== null && totalCholesterolValStr !== undefined && totalCholesterolValStr !== "") ? parseFloat(totalCholesterolValStr) : NaN;
                const hdlVal = (hdlValStr !== null && hdlValStr !== undefined && hdlValStr !== "") ? parseFloat(hdlValStr) : NaN;

                if (!isNaN(totalCholesterolVal) && !isNaN(hdlVal)) {
                  const nonHDLCholesterol = calculateNonHDLCholesterol(totalCholesterolVal, hdlVal);

                  if (parametersMap.has(nonHDLCholesterolKey)) {
                    // Only update the value, preserve existing reference range and other properties
                    const existingParam = parametersMap.get(nonHDLCholesterolKey);
                    existingParam.valuesByTimestamp[ts] = nonHDLCholesterol.toFixed(0);
                  } else {
                    // Create new parameter entry if it doesn't exist (fallback for legacy data)
                    parametersMap.set(nonHDLCholesterolKey, {
                      key: nonHDLCholesterolKey,
                      parameterName: 'Non-HDL Cholesterol',
                      type: 'Lab',
                      units: 'mg/dL',
                      category: 'Lab Results',
                      subcategory: 'Lipid',
                      referenceRange: null, // Initialize reference range field
                      valuesByTimestamp: { [ts]: nonHDLCholesterol.toFixed(0) },
                      notesByTimestamp: {},
                      flagsByTimestamp: {},
                      trendData: []
                    });
                  }
                }
              }

              // Calculate SIRS Score
              const tempData = parametersMap.get('vitals_temp_c');
              const hrData = parametersMap.get('vitals_rhr');
              const rrData = parametersMap.get('vitals_rr');
              const tempVal = tempData?.valuesByTimestamp[ts] ? parseFloat(tempData.valuesByTimestamp[ts]) : null;
              const hrVal = hrData?.valuesByTimestamp[ts] ? parseFloat(hrData.valuesByTimestamp[ts]) : null;
              const rrVal = rrData?.valuesByTimestamp[ts] ? parseFloat(rrData.valuesByTimestamp[ts]) : null;
              
              let wbcVal = null;
              // Find the most recent WBC on or before the current timestamp ts - use standardized parameter name only
              const relevantWbcEntries = flowsheetData // This is the raw data from API
                .filter(item => item.name === 'labs_cbc_wbc' && item.type === 'Lab' && moment(item.date).isSameOrBefore(moment(ts)))
                .sort((a, b) => moment(b.date).diff(moment(a.date)));
              
              if (relevantWbcEntries.length > 0) {
                 // Consider WBC if it's within a 48-hour window of the vitals timestamp
                if (moment(ts).diff(moment(relevantWbcEntries[0].date), 'hours') <= 48) {
                    wbcVal = parseFloat(relevantWbcEntries[0].value);
                }
              }

              const sirsVal = calculateSIRSScore(tempVal, hrVal, rrVal, wbcVal);
              if (sirsVal !== null && parametersMap.has('vitals_sirs')) {
                parametersMap.get('vitals_sirs').valuesByTimestamp[ts] = sirsVal;
              } else if (parametersMap.has('vitals_sirs')) { 
                 parametersMap.get('vitals_sirs').valuesByTimestamp[ts] = null; // Set to null if not calculable
              }

              // Calculate VO2 Max
              if (patientDemographics.age && parametersMap.has('vitals_rhr')) {
                const age = patientDemographics.age;
                const hrMax = 220 - age;
                const hrRestVal = hrData?.valuesByTimestamp[ts] ? parseFloat(hrData.valuesByTimestamp[ts]) : null;
                const vo2maxVal = calculateVO2max(hrMax, hrRestVal);
                if (vo2maxVal !== null && parametersMap.has('vitals_vo2max')) {
                    parametersMap.get('vitals_vo2max').valuesByTimestamp[ts] = vo2maxVal.toFixed(1);
                } else if (parametersMap.has('vitals_vo2max')) {
                    parametersMap.get('vitals_vo2max').valuesByTimestamp[ts] = null;
                }
              } else if (parametersMap.has('vitals_vo2max')) {
                parametersMap.get('vitals_vo2max').valuesByTimestamp[ts] = null;
              }

              // Calculate Y-Balance Test Averages
              const yBalanceBases = [
                'biometrics_balance_ybt_right_anterior',
                'biometrics_balance_ybt_left_anterior',
                'biometrics_balance_ybt_right_posteromedial',
                'biometrics_balance_ybt_left_posteromedial',
                'biometrics_balance_ybt_right_posterolateral',
                'biometrics_balance_ybt_left_posterolateral',
              ];

              yBalanceBases.forEach(baseKey => {
                const trial1Data = parametersMap.get(`${baseKey}_1`);
                const trial2Data = parametersMap.get(`${baseKey}_2`);

                // Ensure average parameter is initialized
                const avgKey = `${baseKey}_avg`;
                if (!parametersMap.has(avgKey)) {
                  const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
                  const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';

                  // Extract direction and side from baseKey for display name
                  const parts = baseKey.split('_');
                  const side = parts[3]; // right or left
                  const direction = parts[4]; // anterior, posteromedial, posterolateral
                  const displayName = `YBT ${side.charAt(0).toUpperCase() + side.slice(1)} ${direction.charAt(0).toUpperCase() + direction.slice(1)} Average`;

                  parametersMap.set(avgKey, {
                    key: avgKey,
                    parameterName: displayName,
                    type: 'Biometric',
                    units: 'cm',
                    category: 'Biometrics',
                    subcategory: subcategoryDisplayName,
                    referenceRange: null,
                    valuesByTimestamp: {},
                    notesByTimestamp: {},
                    flagsByTimestamp: {},
                    trendData: []
                  });
                }

                const avgData = parametersMap.get(avgKey);

                if (avgData) {
                  const trial1Value = trial1Data ? trial1Data.valuesByTimestamp[ts] : null;
                  const trial2Value = trial2Data ? trial2Data.valuesByTimestamp[ts] : null;

                  // Parse values, treating null/undefined as NaN
                  const trial1Num = (trial1Value !== null && trial1Value !== undefined) ? parseFloat(trial1Value) : NaN;
                  const trial2Num = (trial2Value !== null && trial2Value !== undefined) ? parseFloat(trial2Value) : NaN;

                  let avg = null;

                  if (!isNaN(trial1Num) && !isNaN(trial2Num)) {
                    // Both trials available - calculate average
                    avg = (trial1Num + trial2Num) / 2;
                  } else if (!isNaN(trial1Num)) {
                    // Only trial 1 available - use trial 1 value
                    avg = trial1Num;
                  } else if (!isNaN(trial2Num)) {
                    // Only trial 2 available - use trial 2 value
                    avg = trial2Num;
                  }

                  if (avg !== null && !isNaN(avg)) {
                    avgData.valuesByTimestamp[ts] = avg.toFixed(1);
                  }
                }
              });

              // Calculate Y-Balance Composite Reach and Asymmetry
              const directions = ['anterior', 'posteromedial', 'posterolateral'];
              let rightCompositeSum = 0;
              let leftCompositeSum = 0;
              let validDirections = 0;

              directions.forEach(dir => {
                const rightAvgData = parametersMap.get(`biometrics_balance_ybt_right_${dir}_avg`);
                const leftAvgData = parametersMap.get(`biometrics_balance_ybt_left_${dir}_avg`);

                // Get values for both sides (may be NaN if not present)
                const rightAvg = rightAvgData ? parseFloat(rightAvgData.valuesByTimestamp[ts]) : NaN;
                const leftAvg = leftAvgData ? parseFloat(leftAvgData.valuesByTimestamp[ts]) : NaN;

                // For composite reach calculation, only include valid measurements
                if (!isNaN(rightAvg)) {
                  rightCompositeSum += rightAvg;
                  validDirections++;
                }
                if (!isNaN(leftAvg)) {
                  leftCompositeSum += leftAvg;
                  // Only increment validDirections once per direction if we haven't already
                  if (isNaN(rightAvg)) {
                    validDirections++;
                  }
                }

                // Calculate deviation parameters - support both bilateral and unilateral measurements
                let asymmetryPct = null;
                let asymmetryCm = null;

                if (!isNaN(rightAvg) && !isNaN(leftAvg)) {
                  // Bilateral measurement - calculate traditional asymmetry
                  asymmetryCm = Math.abs(leftAvg - rightAvg);
                  asymmetryPct = (asymmetryCm / ((leftAvg + rightAvg) / 2)) * 100;
                } else if (!isNaN(rightAvg) || !isNaN(leftAvg)) {
                  // Unilateral measurement - use single-side deviation approach
                  // For single trials, we'll use 0% deviation as there's no asymmetry to measure
                  asymmetryPct = 0;
                  asymmetryCm = 0;
                }

                // Store asymmetry values if calculated
                if (asymmetryCm !== null) {
                  const asymmetryCmKey = `biometrics_balance_ybt_${dir}_asymmetry_cm`;
                  if (parametersMap.has(asymmetryCmKey)) {
                    parametersMap.get(asymmetryCmKey).valuesByTimestamp[ts] = asymmetryCm.toFixed(1);
                  }
                }

                if (asymmetryPct !== null) {
                  const asymmetryPctKey = `biometrics_balance_ybt_${dir}_deviation`;

                  // Ensure the deviation parameter is properly initialized
                  if (!parametersMap.has(asymmetryPctKey)) {
                    const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
                    const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';
                    const directionName = dir.charAt(0).toUpperCase() + dir.slice(1); // Capitalize first letter

                    parametersMap.set(asymmetryPctKey, {
                      key: asymmetryPctKey,
                      parameterName: `Y-Balance Test - ${directionName} Deviation`,
                      type: 'Biometric',
                      units: '%',
                      category: 'Biometrics',
                      subcategory: subcategoryDisplayName,
                      referenceRange: null,
                      valuesByTimestamp: {},
                      notesByTimestamp: {},
                      flagsByTimestamp: {},
                      trendData: []
                    });
                  }
                  parametersMap.get(asymmetryPctKey).valuesByTimestamp[ts] = asymmetryPct.toFixed(1);
                }
              });

              // Limb length parameters are now initialized early, so just get the keys
              const rightLimbLengthKey = 'biometrics_balance_ybt_right_limb_length';
              const leftLimbLengthKey = 'biometrics_balance_ybt_left_limb_length';

              const rightLimbLengthData = parametersMap.get(rightLimbLengthKey);
              const leftLimbLengthData = parametersMap.get(leftLimbLengthKey);



              const rightLimbLength = rightLimbLengthData ? parseFloat(rightLimbLengthData.valuesByTimestamp[ts]) : null;
              const leftLimbLength = leftLimbLengthData ? parseFloat(leftLimbLengthData.valuesByTimestamp[ts]) : null;

              // Only calculate composite reach if not already provided as direct measurements
              const rightCompositeKey = 'biometrics_balance_ybt_right_composite_reach';
              const leftCompositeKey = 'biometrics_balance_ybt_left_composite_reach';

              // Ensure composite reach parameters are properly initialized
              if (!parametersMap.has(rightCompositeKey)) {
                const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
                const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';
                parametersMap.set(rightCompositeKey, {
                  key: rightCompositeKey,
                  parameterName: 'YBT Right Composite Reach',
                  type: 'Biometric',
                  units: '%',
                  category: 'Biometrics',
                  subcategory: subcategoryDisplayName,
                  referenceRange: null,
                  valuesByTimestamp: {},
                  notesByTimestamp: {},
                  flagsByTimestamp: {},
                  trendData: []
                });
              }

              if (!parametersMap.has(leftCompositeKey)) {
                const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
                const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';
                parametersMap.set(leftCompositeKey, {
                  key: leftCompositeKey,
                  parameterName: 'YBT Left Composite Reach',
                  type: 'Biometric',
                  units: '%',
                  category: 'Biometrics',
                  subcategory: subcategoryDisplayName,
                  referenceRange: null,
                  valuesByTimestamp: {},
                  notesByTimestamp: {},
                  flagsByTimestamp: {},
                  trendData: []
                });
              }



              const hasDirectRightComposite = parametersMap.has(rightCompositeKey) &&
                parametersMap.get(rightCompositeKey).valuesByTimestamp[ts] !== undefined &&
                parametersMap.get(rightCompositeKey).valuesByTimestamp[ts] !== null &&
                parametersMap.get(rightCompositeKey).valuesByTimestamp[ts] !== '';

              const hasDirectLeftComposite = parametersMap.has(leftCompositeKey) &&
                parametersMap.get(leftCompositeKey).valuesByTimestamp[ts] !== undefined &&
                parametersMap.get(leftCompositeKey).valuesByTimestamp[ts] !== null &&
                parametersMap.get(leftCompositeKey).valuesByTimestamp[ts] !== '';



              // Only calculate if we have all 3 directions AND limb lengths AND no direct measurements
              if (validDirections === 3 && !hasDirectRightComposite && !hasDirectLeftComposite) {
                if (rightLimbLength) {
                  const rightCompositeReach = (rightCompositeSum / (3 * rightLimbLength)) * 100;
                  if (parametersMap.has(rightCompositeKey)) {
                    parametersMap.get(rightCompositeKey).valuesByTimestamp[ts] = rightCompositeReach.toFixed(1);
                  }
                }
                if (leftLimbLength) {
                  const leftCompositeReach = (leftCompositeSum / (3 * leftLimbLength)) * 100;
                  if (parametersMap.has(leftCompositeKey)) {
                    parametersMap.get(leftCompositeKey).valuesByTimestamp[ts] = leftCompositeReach.toFixed(1);
                  }
                }
              }

              // Calculate YBT Performance Ratio
              const rightCompositeData = parametersMap.get(rightCompositeKey);
              const leftCompositeData = parametersMap.get(leftCompositeKey);

              if (rightCompositeData && leftCompositeData) {
                const rightValue = parseFloat(rightCompositeData.valuesByTimestamp[ts]);
                const leftValue = parseFloat(leftCompositeData.valuesByTimestamp[ts]);

                if (!isNaN(rightValue) && !isNaN(leftValue) && rightValue >= 0 && leftValue >= 0) {
                  const asymmetry = Math.abs(rightValue - leftValue);
                  const average = (rightValue + leftValue) / 2;
                  const performanceRatio = average > 0 ? (asymmetry / average) * 100 : 0;

                  const performanceRatioKey = 'biometrics_balance_ybt_performance_ratio';
                  if (!parametersMap.has(performanceRatioKey)) {
                    const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
                    const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';
                    parametersMap.set(performanceRatioKey, {
                      key: performanceRatioKey,
                      parameterName: 'Y-Balance Test Performance Ratio',
                      type: 'Biometric',
                      units: '%',
                      category: 'Biometrics',
                      subcategory: subcategoryDisplayName,
                      referenceRange: null,
                      valuesByTimestamp: {},
                      notesByTimestamp: {},
                      flagsByTimestamp: {},
                      trendData: []
                    });
                  }
                  parametersMap.get(performanceRatioKey).valuesByTimestamp[ts] = performanceRatio.toFixed(1);
                }
              }

              // Calculate Composite Deviation - support partial data
              const anteriorDeviation = parseFloat(parametersMap.get('biometrics_balance_ybt_anterior_deviation')?.valuesByTimestamp[ts]);
              const posteromedialDeviation = parseFloat(parametersMap.get('biometrics_balance_ybt_posteromedial_deviation')?.valuesByTimestamp[ts]);
              const posterolateralDeviation = parseFloat(parametersMap.get('biometrics_balance_ybt_posterolateral_deviation')?.valuesByTimestamp[ts]);

              // Collect valid deviation values
              const validDeviations = [];
              if (!isNaN(anteriorDeviation)) validDeviations.push(anteriorDeviation);
              if (!isNaN(posteromedialDeviation)) validDeviations.push(posteromedialDeviation);
              if (!isNaN(posterolateralDeviation)) validDeviations.push(posterolateralDeviation);

              // Calculate composite deviation if at least one direction has data
              if (validDeviations.length > 0) {
                const compositeDeviation = validDeviations.reduce((sum, val) => sum + val, 0) / validDeviations.length;
                const compositeDeviationKey = 'biometrics_balance_ybt_composite_deviation';
                if (!parametersMap.has(compositeDeviationKey)) {
                    const balanceCategory = biometricCategories.find(cat => cat.key === 'balance');
                    const subcategoryDisplayName = balanceCategory ? balanceCategory.title : 'Balance';
                    parametersMap.set(compositeDeviationKey, {
                        key: compositeDeviationKey,
                        parameterName: 'YBT Composite Deviation',
                        type: 'Biometric',
                        units: '%',
                        category: 'Biometrics',
                        subcategory: subcategoryDisplayName,
                        referenceRange: null, // Initialize reference range field
                        valuesByTimestamp: {},
                        notesByTimestamp: {},
                        flagsByTimestamp: {},
                        trendData: []
                    });
                }
                parametersMap.get(compositeDeviationKey).valuesByTimestamp[ts] = compositeDeviation.toFixed(1);
              }

              // Calculate Waist-Hip Ratio
              const waistData = parametersMap.get('biometrics_biomeasurement_waistCircumference');
              const hipData = parametersMap.get('biometrics_biomeasurement_hipCircumference');
              if (waistData && hipData) {
                  const waistValStr = waistData.valuesByTimestamp[ts];
                  const hipValStr = hipData.valuesByTimestamp[ts];
                  const waistVal = (waistValStr !== null && waistValStr !== undefined && waistValStr !== "") ? parseFloat(waistValStr) : NaN;
                  const hipVal = (hipValStr !== null && hipValStr !== undefined && hipValStr !== "") ? parseFloat(hipValStr) : NaN;
                  if (!isNaN(waistVal) && !isNaN(hipVal) && hipVal !== 0) {
                      const whr = calculateWaistHipRatio(waistVal, hipVal);
                      const whrKey = 'biometrics_biomeasurement_waistHipRatio';
                      if (parametersMap.has(whrKey)) {
                          parametersMap.get(whrKey).valuesByTimestamp[ts] = whr.toFixed(2);
                      }
                  }
              }

              // Calculate BUN/Creatinine Ratio
                const bunData = parametersMap.get('labs_renal_bun');
                const creatinineData = parametersMap.get('labs_renal_creatinine');
                if (bunData && creatinineData) {
                    const bunValStr = bunData.valuesByTimestamp[ts];
                    const creatinineValStr = creatinineData.valuesByTimestamp[ts];
                    const bunVal = (bunValStr !== null && bunValStr !== undefined && bunValStr !== "") ? parseFloat(bunValStr) : NaN;
                    const creatinineVal = (creatinineValStr !== null && creatinineValStr !== undefined && creatinineValStr !== "") ? parseFloat(creatinineValStr) : NaN;
                    if (!isNaN(bunVal) && !isNaN(creatinineVal) && creatinineVal !== 0) {
                        const bunCreatinineRatio = calculateBUNCreatinineRatio(bunVal, creatinineVal);
                        const bunCreatinineRatioKey = 'labs_renal_bun_creat_ratio';
                        if (!parametersMap.has(bunCreatinineRatioKey)) {
                            parametersMap.set(bunCreatinineRatioKey, {
                                key: bunCreatinineRatioKey,
                                parameterName: 'BUN/Creatinine Ratio',
                                type: 'Lab',
                                units: '',
                                category: 'Labs',
                                subcategory: 'Renal',
                                referenceRange: null, // Initialize reference range field
                                valuesByTimestamp: {},
                                notesByTimestamp: {},
                                flagsByTimestamp: {},
                                trendData: []
                            });
                        }
                        // Only update the value, preserve existing reference range and other properties
                        const existingBunCreatParam = parametersMap.get(bunCreatinineRatioKey);
                        existingBunCreatParam.valuesByTimestamp[ts] = bunCreatinineRatio.toFixed(2);
                    } else if (parametersMap.has('labs_renal_bun_creat_ratio') && parametersMap.get('labs_renal_bun_creat_ratio').valuesByTimestamp.hasOwnProperty(ts)) {
                        parametersMap.get('labs_renal_bun_creat_ratio').valuesByTimestamp[ts] = null;
                    }
                }

              // Note: TC/GLU Ratio and Atherogenic Index are now calculated by the backend
              // The frontend no longer performs these calculations to avoid conflicts
            });
          }

          if (parametersMap.has('labs_cbc_neut') && parametersMap.has('labs_cbc_lymph')) {
            const neutrophilData = parametersMap.get('labs_cbc_neut');
            const lymphocyteData = parametersMap.get('labs_cbc_lymph');
            let nlrParamData = parametersMap.get('labs_cbc_nlr');
            if (!nlrParamData) {
                parametersMap.set('labs_cbc_nlr', { key: 'labs_cbc_nlr', parameterName: 'NLR', type: 'Lab', units: '', category: 'Lab Results', subcategory: 'CBC', referenceRange: null, valuesByTimestamp: {}, notesByTimestamp: {}, flagsByTimestamp: {}, trendData: [] });
                nlrParamData = parametersMap.get('labs_cbc_nlr'); }
            if (nlrParamData) {
                uniqueTimestamps.forEach(ts => {
                    const neutrophilValueStr = neutrophilData.valuesByTimestamp[ts];
                    const lymphocyteValueStr = lymphocyteData.valuesByTimestamp[ts];
                    const neutrophilValue = (neutrophilValueStr !== null && neutrophilValueStr !== undefined && neutrophilValueStr !== "") ? parseFloat(neutrophilValueStr) : NaN;
                    const lymphocyteValue = (lymphocyteValueStr !== null && lymphocyteValueStr !== undefined && lymphocyteValueStr !== "") ? parseFloat(lymphocyteValueStr) : NaN;
                    if (!isNaN(neutrophilValue) && !isNaN(lymphocyteValue) && lymphocyteValue !== 0) {
                        const nlrValue = neutrophilValue / lymphocyteValue;
                        nlrParamData.valuesByTimestamp[ts] = nlrValue.toFixed(2);
                    } else { if (nlrParamData.valuesByTimestamp.hasOwnProperty(ts)) { nlrParamData.valuesByTimestamp[ts] = null; } } }); } }
        }

        parametersMap.forEach((paramData) => {
            if (paramData.originalCategory === 'Lifestyle - Sleep') {
                paramData.category = 'recovery';
            } else if (paramData.originalCategory === 'Lifestyle - Environment') {
                paramData.category = 'environment';
            } else if (paramData.originalCategory === 'Endocrine') {
                if (flowsheetItemCategories[paramData.parameterName] === 'Thyroid') {
                    paramData.category = 'Thyroid';
                } else {
                    paramData.category = 'Thyroid';
                }
            }
        });
        if (!parametersMap.has('vitals_map')) { parametersMap.set('vitals_map', { key: 'vitals_map', parameterName: 'Mean Arterial Pressure (MAP)', type: 'Vital', units: 'mmHg', category: 'Vitals', referenceRange: null, valuesByTimestamp: {}, flagsByTimestamp: {}, trendData: [] }); }
        if (!parametersMap.has('vitals_pp')) { parametersMap.set('vitals_pp', { key: 'vitals_pp', parameterName: 'Pulse Pressure (PP)', type: 'Vital', units: 'mmHg', category: 'Vitals', referenceRange: null, valuesByTimestamp: {}, flagsByTimestamp: {}, trendData: [] }); }

        const flatParameterRows = [];
        if (parametersMap.has('physicalExamSummarySimple')) { flatParameterRows.push(parametersMap.get('physicalExamSummarySimple')); parametersMap.delete('physicalExamSummarySimple'); }
        if (parametersMap.has('physicalExamSummaryFull')) { flatParameterRows.push(parametersMap.get('physicalExamSummaryFull')); parametersMap.delete('physicalExamSummaryFull'); }
        parametersMap.forEach((paramData) => { if (paramData.type !== 'Header') { flatParameterRows.push(paramData); } });

        flatParameterRows.forEach(paramData => {
          paramData.lastValue = null; paramData.lastValueTimestamp = null; paramData.lastValueType = paramData.type;
          for (let i = uniqueTimestamps.length - 1; i >= 0; i--) {
            const ts = uniqueTimestamps[i];
            if (paramData.valuesByTimestamp && paramData.valuesByTimestamp.hasOwnProperty(ts)) {
              const value = paramData.valuesByTimestamp[ts];
              if (value !== null && value !== undefined && value !== '') {
                paramData.lastValue = value; paramData.lastValueTimestamp = ts;
                if (paramData.key === 'physicalExamSummarySimple' || paramData.key === 'physicalExamSummaryFull') { paramData.lastValueType = 'Physical Exam Summary'; }
                else { paramData.lastValueType = paramData.type; }
                break;
              } } } });
        let parameterRows = flatParameterRows.map(paramData => {
            const timestampData = uniqueTimestamps.reduce((acc, ts) => {
                const valueExists = paramData.valuesByTimestamp && paramData.valuesByTimestamp.hasOwnProperty(ts);
                const notesExist = paramData.notesByTimestamp && paramData.notesByTimestamp.hasOwnProperty(ts);
                const flagExists = paramData.flagsByTimestamp && paramData.flagsByTimestamp.hasOwnProperty(ts);
                acc[ts] = {
                    value: valueExists ? paramData.valuesByTimestamp[ts] : null,
                    notes: notesExist ? paramData.notesByTimestamp[ts] : null,
                    flag: flagExists ? paramData.flagsByTimestamp[ts] : null,
                };
                return acc;
            }, {});

            const row = {
              key: paramData.key,
              parameterName: paramData.parameterName,
              type: paramData.type,
              units: paramData.units,
              referenceRange: paramData.referenceRange,
              category: paramData.category,
              subcategory: paramData.subcategory, // Pass subcategory to the final row for filtering
              displaySubCategory: paramData.displaySubCategory, // Pass displaySubCategory to the final row
              trendData: paramData.trendData.sort((a, b) => moment(a.date).unix() - moment(b.date).unix()),
              lastValue: paramData.lastValue,
              lastValueTimestamp: paramData.lastValueTimestamp,
              lastValueType: paramData.lastValueType,
              flagsByTimestamp: paramData.flagsByTimestamp, // Ensure flagsByTimestamp is passed to each row
              ...timestampData,
            };

            return row;
        });





        // Apply custom sorting for CBC parameters at the data level
        parameterRows = parameterRows.sort((a, b) => {
          // Special handling for CBC parameters - check by parameter name
          const aIsCBC = cbcDisplayOrder.includes(a.parameterName);
          const bIsCBC = cbcDisplayOrder.includes(b.parameterName);

          if (aIsCBC && bIsCBC) {
            const indexA = cbcDisplayOrder.indexOf(a.parameterName);
            const indexB = cbcDisplayOrder.indexOf(b.parameterName);
            return indexA - indexB;
          }

          // If only one is CBC, prioritize it
          if (aIsCBC && !bIsCBC) return -1;
          if (!aIsCBC && bIsCBC) return 1;

          // For Balance parameters, use balanceDisplayOrder
          if (a.subcategory === 'Balance' && b.subcategory === 'Balance') {
            const indexA = balanceDisplayOrder.indexOf(a.parameterName);
            const indexB = balanceDisplayOrder.indexOf(b.parameterName);

            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
          }

          // For Lipid parameters specifically (subcategory = 'Lipid'), use lipidDisplayOrder
          if (a.type === 'Lab' && a.subcategory === 'Lipid' && b.type === 'Lab' && b.subcategory === 'Lipid') {
            const indexA = lipidDisplayOrder.indexOf(a.parameterName);
            const indexB = lipidDisplayOrder.indexOf(b.parameterName);

            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
          }

          // For other lab parameters, use labDisplayOrderMap
          if (a.category === b.category && labDisplayOrderMap[a.category]) {
            const orderArray = labDisplayOrderMap[a.category];
            const indexA = orderArray.indexOf(a.parameterName);
            const indexB = orderArray.indexOf(b.parameterName);

            // Debug: Log other lab parameter sorting
            if (a.category === 'Lab Results' && a.subcategory !== 'Lipid') {
            }

            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
          }

          // For vitals, use vitalsDisplayOrder
          if (a.category === 'Vitals' && b.category === 'Vitals') {
            const indexA = vitalsDisplayOrder.indexOf(a.key);
            const indexB = vitalsDisplayOrder.indexOf(b.key);

            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
          }

          // For Survey parameters, use surveyDisplayOrder
          if (a.category === 'Surveys' && b.category === 'Surveys') {
            const surveyDisplayOrder = [
              'Life Changes (6 months)',
              'New Diagnosis (6 months)',
              'Hospitalization (6 months)',
              'Current Medications',
              'Health Improvement'
            ];

            const indexA = surveyDisplayOrder.indexOf(a.parameterName);
            const indexB = surveyDisplayOrder.indexOf(b.parameterName);

            if (indexA !== -1 && indexB !== -1) {
              return indexA - indexB;
            }
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
          }

          // Default alphabetical sorting
          return a.parameterName.localeCompare(b.parameterName);
        });
        // Calculate parameter counts for all categories (memoized for performance)
        const parameterCountsResult = calculateParameterCounts(parameterRows, parameterRegistry);
        const parameterCounts = parameterCountsResult.countsWithData;
        const totalAvailableCounts = parameterCountsResult.totalAvailable;




        return { uniqueTimestamps, parameterRows, categories: staticHierarchicalCategories, parameterCounts, totalAvailableCounts };
    } catch (error) {
      console.error("Error in transformedData calculation:", error);
      return { uniqueTimestamps: [], parameterRows: [], categories: [], parameterCounts: new Map(), totalAvailableCounts: new Map() };
    }
  }, [flowsheetData, selectedCategoryFilter, patientDemographics, parameterRegistry, calculateParameterCounts, t]);

  // Enhanced useEffect for handling initialFilterKey with improved parameter matching
  useEffect(() => {
    if (initialFilterKey && transformedData.parameterRows.length > 0) {
        // Searching for parameter with key: ${initialFilterKey}

        // Enhanced parameter matching logic to handle all categories consistently
        let parameter = null;

        // 1. First try exact match (for standardized keys)
        parameter = transformedData.parameterRows.find(p => p.key === initialFilterKey);

        // 2. If no exact match, try suffix matching (for legacy compatibility)
        if (!parameter) {
            parameter = transformedData.parameterRows.find(p => p.key.endsWith(`_${initialFilterKey}`));
        }

        // 3. If still no match, try prefix matching for composite keys
        if (!parameter) {
            parameter = transformedData.parameterRows.find(p =>
                p.key.includes(initialFilterKey) ||
                initialFilterKey.includes(p.key.split('_').pop())
            );
        }

        // 4. For biometrics, physical exam, and lifestyle - try category-specific matching
        if (!parameter) {
            // Try matching against original item names or backend keys
            parameter = transformedData.parameterRows.find(p =>
                p.originalItemName === initialFilterKey ||
                (p.key.includes('biometrics_') && p.key.split('_').pop() === initialFilterKey) ||
                (p.key.includes('pe_') && p.key.split('_').pop() === initialFilterKey) ||
                (p.key.includes('lifestyle_') && p.key.split('_').pop() === initialFilterKey)
            );
        }

        if (parameter) {


            // Set the category to ensure the parameter is visible
            setSelectedCategoryFilter(parameter.category || 'All');

            // Set the highlight key to the actual found parameter key for consistent highlighting
            setHighlightedParameterKey(parameter.key);
        } else {
            console.warn(`[PatientFlowsheet] Parameter not found for initialFilterKey: ${initialFilterKey}`);
            // Still try to highlight with the original key in case it works
            setHighlightedParameterKey(initialFilterKey);
        }
    }
  }, [initialFilterKey, transformedData.parameterRows]);

  const filteredTableData = useMemo(() => {
    let rowsToDisplay = transformedData.parameterRows;
    
    // Debug lab data before filtering for all Vietnamese subcategories
    if (i18n.language === 'vi' && (selectedCategoryFilter?.includes('máu') || selectedCategoryFilter === 'CBC' ||
        selectedCategoryFilter?.includes('Lipid') || selectedCategoryFilter?.includes('Đường huyết') ||
        selectedCategoryFilter?.includes('Gan mật') || selectedCategoryFilter?.includes('Thận') ||
        selectedCategoryFilter?.includes('Điện giải') || selectedCategoryFilter?.includes('Tuyến giáp') ||
        selectedCategoryFilter?.includes('Tim mạch') || selectedCategoryFilter?.includes('Đông máu') ||
        selectedCategoryFilter?.includes('Viêm') || selectedCategoryFilter?.includes('Nước tiểu') ||
        selectedCategoryFilter?.includes('Vitamin') || selectedCategoryFilter === 'Cardiac' ||
        selectedCategoryFilter === 'Coagulation' || selectedCategoryFilter === 'Inflammation' ||
        selectedCategoryFilter === 'Urine' || selectedCategoryFilter === 'Vitamins')) {
      const labType = selectedCategoryFilter;
      const keyPattern = labType === 'CBC' || labType?.includes('máu') ? 'cbc' :
                        labType?.includes('Lipid') ? 'lipid' :
                        labType?.includes('Đường huyết') ? 'glucose' :
                        labType?.includes('Gan mật') ? 'hepatology' :
                        labType?.includes('Thận') ? 'renal' :
                        labType?.includes('Điện giải') ? 'electrolytes' :
                        labType?.includes('Tuyến giáp') ? 'thyroid' :
                        labType?.includes('Tim mạch') || labType === 'Cardiac' ? 'cardiac' :
                        labType?.includes('Đông máu') || labType === 'Coagulation' ? 'coagulation' :
                        labType?.includes('Viêm') || labType === 'Inflammation' ? 'inflammation' :
                        labType?.includes('Nước tiểu') || labType === 'Urine' ? 'urine' :
                        labType?.includes('Vitamin') || labType === 'Vitamins' ? 'vitamins' : 'unknown';
      
      const labRows = rowsToDisplay.filter(row => row.key?.includes(keyPattern));
    }
    
    // Debug lab data in filteredTableData for all Vietnamese subcategories
    if (i18n.language === 'vi' && (selectedCategoryFilter?.includes('máu') || selectedCategoryFilter === 'CBC' ||
        selectedCategoryFilter?.includes('Lipid') || selectedCategoryFilter?.includes('Đường huyết') ||
        selectedCategoryFilter?.includes('Gan mật') || selectedCategoryFilter?.includes('Thận') ||
        selectedCategoryFilter?.includes('Điện giải') || selectedCategoryFilter?.includes('Tuyến giáp') ||
        selectedCategoryFilter?.includes('Tim mạch') || selectedCategoryFilter?.includes('Đông máu') ||
        selectedCategoryFilter?.includes('Viêm') || selectedCategoryFilter?.includes('Nước tiểu') ||
        selectedCategoryFilter?.includes('Vitamin') || selectedCategoryFilter === 'Cardiac' ||
        selectedCategoryFilter === 'Coagulation' || selectedCategoryFilter === 'Inflammation' ||
        selectedCategoryFilter === 'Urine' || selectedCategoryFilter === 'Vitamins')) {
      const labType = selectedCategoryFilter;
      const keyPattern = labType === 'CBC' || labType?.includes('máu') ? 'cbc' :
                        labType?.includes('Lipid') ? 'lipid' :
                        labType?.includes('Đường huyết') ? 'glucose' :
                        labType?.includes('Gan mật') ? 'hepatology' :
                        labType?.includes('Thận') ? 'renal' :
                        labType?.includes('Điện giải') ? 'electrolytes' :
                        labType?.includes('Tuyến giáp') ? 'thyroid' :
                        labType?.includes('Tim mạch') || labType === 'Cardiac' ? 'cardiac' :
                        labType?.includes('Đông máu') || labType === 'Coagulation' ? 'coagulation' :
                        labType?.includes('Viêm') || labType === 'Inflammation' ? 'inflammation' :
                        labType?.includes('Nước tiểu') || labType === 'Urine' ? 'urine' :
                        labType?.includes('Vitamin') || labType === 'Vitamins' ? 'vitamins' : 'unknown';
      
      const labRows = rowsToDisplay.filter(row => row.key?.includes(keyPattern));
    }



    // Apply CBC, Balance, and Lipid sorting again at the filtered data level to ensure it's preserved
    rowsToDisplay = rowsToDisplay.sort((a, b) => {
      // Special handling for CBC parameters
      const aIsCBC = cbcDisplayOrder.includes(a.parameterName);
      const bIsCBC = cbcDisplayOrder.includes(b.parameterName);

      if (aIsCBC && bIsCBC) {
        const indexA = cbcDisplayOrder.indexOf(a.parameterName);
        const indexB = cbcDisplayOrder.indexOf(b.parameterName);
        return indexA - indexB;
      }

      // If only one is CBC, prioritize it
      if (aIsCBC && !bIsCBC) return -1;
      if (!aIsCBC && bIsCBC) return 1;

      // Special handling for Balance parameters
      const aIsBalance = a.subcategory === 'Balance' && balanceDisplayOrder.includes(a.parameterName);
      const bIsBalance = b.subcategory === 'Balance' && balanceDisplayOrder.includes(b.parameterName);

      if (aIsBalance && bIsBalance) {
        const indexA = balanceDisplayOrder.indexOf(a.parameterName);
        const indexB = balanceDisplayOrder.indexOf(b.parameterName);
        return indexA - indexB;
      }

      // If only one is Balance, prioritize it
      if (aIsBalance && !bIsBalance) return -1;
      if (!aIsBalance && bIsBalance) return 1;

      // Special handling for Lipid parameters (check subcategory since category is "Lab Results")
      const aIsLipid = a.type === 'Lab' && a.subcategory === 'Lipid' && lipidDisplayOrder.includes(a.parameterName);
      const bIsLipid = b.type === 'Lab' && b.subcategory === 'Lipid' && lipidDisplayOrder.includes(b.parameterName);

      if (aIsLipid && bIsLipid) {
        const indexA = lipidDisplayOrder.indexOf(a.parameterName);
        const indexB = lipidDisplayOrder.indexOf(b.parameterName);
        return indexA - indexB;
      }

      // If only one is Lipid, prioritize it
      if (aIsLipid && !bIsLipid) return -1;
      if (!aIsLipid && bIsLipid) return 1;

      // Special handling for Survey parameters - sort by question order
      const surveyDisplayOrder = [
        'Life Changes (6 months)',
        'New Diagnosis (6 months)',
        'Hospitalization (6 months)',
        'Current Medications',
        'Health Improvement'
      ];

      const aIsSurvey = a.category === 'Surveys' && surveyDisplayOrder.includes(a.parameterName);
      const bIsSurvey = b.category === 'Surveys' && surveyDisplayOrder.includes(b.parameterName);

      if (aIsSurvey && bIsSurvey) {
        const indexA = surveyDisplayOrder.indexOf(a.parameterName);
        const indexB = surveyDisplayOrder.indexOf(b.parameterName);
        return indexA - indexB;
      }

      // If only one is Survey, prioritize it
      if (aIsSurvey && !bIsSurvey) return -1;
      if (!aIsSurvey && bIsSurvey) return 1;

      // For other parameters, maintain existing order
      return 0;
    });



    if (selectedCategoryFilter !== 'All') {

        if (englishCategoryFilter === 'Biometrics' || selectedCategoryFilter === 'Biometrics') {
            // Filter for all biometric parameters using standardized naming
            rowsToDisplay = rowsToDisplay.filter(row => {
                const isBiometric = row.category === 'Biometrics' ||
                                  row.type === 'Biometric' ||
                                  biometricSubCategoryKeys.has(row.category) ||
                                  (row.key && row.key.startsWith('biometrics_'));
                return isBiometric;
            });
        } else if (isKnownBiometricSubcategory(selectedCategoryFilter)) {
            // Handle specific biometric subcategories (cognition, strength, etc.)
            // For biometric data, the main category is always 'Biometrics' and the subcategory contains the specific type

            // Find the biometric category by either raw key or display name
            let biometricCategory = biometricCategories.find(cat => cat.key === selectedCategoryFilter);
            if (!biometricCategory) {
                biometricCategory = biometricCategories.find(cat => cat.title === selectedCategoryFilter);
            }
            if (!biometricCategory) {
                biometricCategory = biometricCategories.find(cat => cat.key === englishCategoryFilter);
            }
            if (!biometricCategory) {
                biometricCategory = biometricCategories.find(cat => cat.title === englishCategoryFilter);
            }

            const displayName = biometricCategory ? biometricCategory.title : selectedCategoryFilter;
            const rawKey = biometricCategory ? biometricCategory.key : selectedCategoryFilter;

            rowsToDisplay = rowsToDisplay.filter(row => {
                const isBiometricParameter = row.type === 'Biometric' ||
                                           row.category === 'Biometrics' ||
                                           (row.key && row.key.startsWith('biometrics_'));

                if (!isBiometricParameter) {
                    return false;
                }

                // The logic now becomes much cleaner because the data is standardized.
                // We primarily check if the row's subcategory matches the filter's display name.
                const englishFilterName = getEnglishCategoryName(selectedCategoryFilter);
                return row.subcategory === displayName || row.subcategory === selectedCategoryFilter || row.subcategory === englishFilterName;
            });
        } else if (englishCategoryFilter === 'Physical Exam' || selectedCategoryFilter === 'Physical Exam') {
            // Filter for all physical exam parameters using standardized naming
            rowsToDisplay = rowsToDisplay.filter(row => {
                const isPhysicalExam = row.category === 'Physical Exam' ||
                                     physicalExamSubCategoriesSet.has(row.category) ||
                                     row.type === 'Physical Exam Detail' ||
                                     row.type === 'Physical Exam Summary' ||
                                     (row.key && row.key.startsWith('pe_'));
                return isPhysicalExam;
            });
        } else if (physicalExamSubCategoriesSet.has(selectedCategoryFilter) || ['Chest', 'HEENT - Mouth', 'HEENT - Nose', 'Lymph'].includes(selectedCategoryFilter)) {
            // Handle specific physical exam subcategories with mapping for abbreviated names
            const filterNameToSubcategoryMap = {
                'Chest': 'Chest/Respiratory',
                'HEENT - Mouth': 'HEENT - Mouth/Throat',
                'HEENT - Nose': 'HEENT - Nose/Sinus',
                'Lymph': 'Lymph Nodes'
            };

            const englishFilterName = getEnglishCategoryName(selectedCategoryFilter);
            const targetSubcategory = filterNameToSubcategoryMap[selectedCategoryFilter] || 
                                     filterNameToSubcategoryMap[englishFilterName] || 
                                     selectedCategoryFilter;

            rowsToDisplay = rowsToDisplay.filter(row => {
                // Check if this is a Physical Exam parameter
                const isPhysicalExamParam = row.category === 'Physical Exam' ||
                                          row.type === 'Physical Exam Detail' ||
                                          row.type === 'Physical Exam Summary' ||
                                          (row.key && row.key.startsWith('pe_'));

                if (!isPhysicalExamParam) {
                    return false;
                }

                // Check both the abbreviated name and the full section title
                const matchesAbbreviated = row.subcategory === selectedCategoryFilter;
                const matchesFullTitle = row.subcategory === targetSubcategory;
                const matchesEnglish = row.subcategory === englishFilterName;

                const matches = matchesAbbreviated || matchesFullTitle || matchesEnglish;

                return matches;
            });
        } else if (englishCategoryFilter === 'Lab Results' || selectedCategoryFilter === 'Lab Results') {
            // Filter for all lab parameters using standardized naming
            rowsToDisplay = rowsToDisplay.filter(row => {
                const isLab = row.type === 'Lab' ||
                            row.category === 'Lab Results' ||
                            knownLabSubCategoryTitles.has(row.category) ||
                            (row.key && row.key.startsWith('labs_'));
                return isLab;
            });
        } else if (isKnownLabSubcategory(selectedCategoryFilter)) {
            // Handle specific lab subcategories (CBC, Lipid, etc.)
            rowsToDisplay = rowsToDisplay.filter(row => {
                // A row is a match if its main category is "Lab Results" AND its subcategory matches the selected filter.
                // This is the most direct and reliable way to filter, now that subcategories are correctly assigned.
                const isLab = row.category === 'Lab Results';
                const englishSubcategoryFilter = getEnglishCategoryName(selectedCategoryFilter);
                const englishRowSubcategory = getEnglishCategoryName(row.subcategory);
                const subcategoryMatches = row.subcategory === selectedCategoryFilter || 
                                         row.subcategory === englishSubcategoryFilter ||
                                         englishRowSubcategory === selectedCategoryFilter ||
                                         englishRowSubcategory === englishSubcategoryFilter;


                return isLab && subcategoryMatches;
            });




        } else if (englishCategoryFilter === 'Lifestyle' || selectedCategoryFilter === 'Lifestyle') {
            // Filter for all lifestyle parameters using standardized naming
            rowsToDisplay = rowsToDisplay.filter(row => {
                // Primary condition: any parameter with a key starting with 'lifestyle_'
                const hasLifestyleKey = row.key && row.key.startsWith('lifestyle_');

                // Secondary conditions: category-based matching
                const hasLifestyleCategory = row.category === 'Lifestyle' ||
                                           lifestyleSubCategoryKeys.has(row.category) ||
                                           ['nutrition', 'movement', 'recovery', 'emotion', 'environment'].includes(row.category);

                // Tertiary conditions: type-based matching for lifestyle-related types
                const hasLifestyleType = row.type === 'Questionnaire' ||
                                       row.type === 'BooleanCheckbox' ||
                                       row.type === 'BooleanCheckboxOption' ||
                                       row.type === 'Score';

                // A parameter is considered lifestyle if it meets ANY of these conditions
                const isLifestyle = hasLifestyleKey || hasLifestyleCategory || hasLifestyleType;



                return isLifestyle;
            });
        } else if (lifestyleSubCategoryKeys.has(selectedCategoryFilter) || 
                   lifestyleSubCategoryKeys.has(englishCategoryFilter) ||
                   ['nutrition', 'movement', 'recovery', 'emotion', 'environment'].includes(selectedCategoryFilter.toLowerCase()) ||
                   ['nutrition', 'movement', 'recovery', 'emotion', 'environment'].includes(englishCategoryFilter.toLowerCase())) {
            // Handle specific lifestyle subcategories
            // selectedCategoryFilter could be either raw key (e.g., 'nutrition') or display name (e.g., 'Nutrition')
            let lifestyleCategory = lifestyleCategories.find(cat => cat.key === selectedCategoryFilter);
            if (!lifestyleCategory) {
                lifestyleCategory = lifestyleCategories.find(cat => cat.title === selectedCategoryFilter);
            }
            if (!lifestyleCategory) {
                lifestyleCategory = lifestyleCategories.find(cat => cat.key === englishCategoryFilter);
            }
            if (!lifestyleCategory) {
                lifestyleCategory = lifestyleCategories.find(cat => cat.title === englishCategoryFilter);
            }

            const displayName = lifestyleCategory ? lifestyleCategory.title : selectedCategoryFilter;
            const rawKey = lifestyleCategory ? lifestyleCategory.key : selectedCategoryFilter.toLowerCase();

            rowsToDisplay = rowsToDisplay.filter(row => {
                // Primary condition: parameter key contains the subcategory (using raw key)
                const keyMatchesSubcategory = row.key && row.key.includes(`lifestyle_${rawKey}_`);

                // Secondary condition: category or subcategory matches
                const englishFilterName = getEnglishCategoryName(selectedCategoryFilter);
                const categoryMatchesSubcategory = row.category === selectedCategoryFilter ||
                                                  row.category === englishFilterName ||
                                                  row.category === rawKey ||
                                                  row.subcategory === selectedCategoryFilter ||
                                                  row.subcategory === englishFilterName ||
                                                  row.subcategory === displayName ||
                                                  row.subcategory === rawKey;

                // Tertiary condition: check if it's a lifestyle parameter at all
                const isLifestyleParameter = row.category === 'Lifestyle' ||
                                           lifestyleSubCategoryKeys.has(row.category) ||
                                           row.type === 'Questionnaire' ||
                                           row.type === 'BooleanCheckbox' ||
                                           row.type === 'BooleanCheckboxOption' ||
                                           row.type === 'Score' ||
                                           (row.key && row.key.startsWith('lifestyle_'));

                // A parameter matches if it has the right key pattern OR (is lifestyle AND matches category)
                const matchesSubcategory = keyMatchesSubcategory || (isLifestyleParameter && categoryMatchesSubcategory);



                return matchesSubcategory;
            });
        } else if (englishCategoryFilter === 'Imaging' || selectedCategoryFilter === 'Imaging') {
            // Filter for all imaging parameters using standardized naming
            rowsToDisplay = rowsToDisplay.filter(row => {
                const isImaging = row.category === 'Imaging' ||
                                (row.key && row.key.startsWith('imaging_'));
                return isImaging;
            });
        } else if (selectedCategoryFilter.startsWith('Imaging_')) {
            // Handle specific imaging study types
            const studyKey = selectedCategoryFilter.replace('Imaging_', '');

            // Create mapping from frontend filter keys to stored subcategory display names
            const imagingFilterToSubcategoryMap = {
                'SpleenUS': 'Spleen US',
                'LiverUS': 'Liver US',
                'CarotidUS': 'Carotid US',
                'EKG': 'EKG',
                'BladderUS': 'Bladder US',
                'ChestXR': 'Chest XR',
                'HeartUS': 'Heart US',
                'GallbladderUS': 'Gallbladder US',
                'KidneyUS': 'Kidney US',
                'ThyroidUS': 'Thyroid US'
            };

            const targetSubcategory = imagingFilterToSubcategoryMap[studyKey] || studyKey;

            rowsToDisplay = rowsToDisplay.filter(row => {
                // Create the correct key pattern for imaging parameters
                // Handle special cases for proper parameter name matching
                let keyPattern;
                if (studyKey === 'ThyroidUS') {
                    keyPattern = 'thyroid_us';
                } else if (studyKey === 'CarotidUS') {
                    keyPattern = 'carotid_us';
                } else if (studyKey === 'BladderUS') {
                    keyPattern = 'bladder_us';
                } else if (studyKey === 'HeartUS') {
                    keyPattern = 'heart_us';
                } else if (studyKey === 'LiverUS') {
                    keyPattern = 'liver_us';
                } else if (studyKey === 'SpleenUS') {
                    keyPattern = 'spleen_us';
                } else if (studyKey === 'KidneyUS') {
                    keyPattern = 'kidney_us';
                } else if (studyKey === 'GallbladderUS') {
                    keyPattern = 'gallbladder_us';
                } else if (studyKey === 'ChestXR') {
                    keyPattern = 'chest_xr';
                } else {
                    // Fallback: Convert studyKey like 'ChestXR' to 'chest_xr' for pattern matching
                    keyPattern = studyKey.toLowerCase().replace(/([a-z])([A-Z])/g, '$1_$2');
                }

                const matchesImagingStudy = (row.category === 'Imaging' && row.displaySubCategory === studyKey) ||
                                          (row.category === 'Imaging' && row.subcategory === studyKey) ||
                                          (row.category === 'Imaging' && row.subcategory === targetSubcategory) ||
                                          (row.key && row.key.includes(`imaging_${keyPattern}_`));

                return matchesImagingStudy;
            });
        } else if (englishCategoryFilter === 'Vitals' || selectedCategoryFilter === 'Vitals') {
            // Filter for all vitals parameters using standardized naming
            rowsToDisplay = rowsToDisplay.filter(row => {
                const isVital = row.category === 'Vitals' ||
                              row.type === 'Vital' ||
                              (row.key && row.key.startsWith('vitals_'));
                return isVital;
            });
        } else if (['nutrition', 'movement', 'recovery', 'emotion', 'environment', 'Nutrition', 'Movement', 'Recovery', 'Emotion', 'Environment'].includes(selectedCategoryFilter)) {
            // Handle legacy lifestyle subcategories that were remapped by line 1121
            // These are lifestyle parameters that got their category changed from 'Lifestyle' to raw subcategory names
            rowsToDisplay = rowsToDisplay.filter(row => {
                // Primary condition: parameter key contains the subcategory
                const keyMatchesSubcategory = row.key && row.key.includes(`lifestyle_${selectedCategoryFilter}_`);

                // Secondary condition: category matches directly
                const categoryMatchesSubcategory = row.category === selectedCategoryFilter;

                // Tertiary condition: check if it's a lifestyle parameter with matching subcategory
                const isLifestyleWithMatchingSubcategory = (
                    (row.category === 'Lifestyle' || (row.key && row.key.startsWith('lifestyle_'))) &&
                    (row.subcategory === selectedCategoryFilter ||
                     row.subcategory === selectedCategoryFilter.charAt(0).toUpperCase() + selectedCategoryFilter.slice(1))
                );

                // A parameter matches if ANY of these conditions are true
                const matchesSubcategory = keyMatchesSubcategory || categoryMatchesSubcategory || isLifestyleWithMatchingSubcategory;



                return matchesSubcategory;
            });
        } else if (englishCategoryFilter === 'Surveys' || selectedCategoryFilter === 'Surveys') {
            // Filter for all survey parameters
            rowsToDisplay = rowsToDisplay.filter(row => {
                const isSurvey = row.category === 'Surveys' ||
                               row.type === 'Survey' ||
                               (row.key && row.key.startsWith('survey_'));
                return isSurvey;
            });
        } else {
            // Fallback for any other category
            rowsToDisplay = rowsToDisplay.filter(row => row.category === selectedCategoryFilter);
        }


    }
    const peOrderMap = new Map(); let peIndex = 0; physicalExamSectionsFull.forEach(section => { section.parameters.forEach(param => { if (param.label) { peOrderMap.set(param.key, peIndex++); } }); });
    const categoryOrderMap = new Map(); let overallIndex = 0; categoryDisplayOrder.forEach(catName => { if (catName === 'Physical Exam') { categoryOrderMap.set('Physical Exam', overallIndex++); const peSubCats = ['PE Summaries', ...physicalExamSectionsFull.map(s => s.title).filter(t => t)]; peSubCats.sort((a, b) => { if (a === 'PE Summaries') return -1; if (b === 'PE Summaries') return 1; const indexA = physicalExamSectionsFull.findIndex(s => s.title === a); const indexB = physicalExamSectionsFull.findIndex(s => s.title === b); return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB); }); peSubCats.forEach(subCat => categoryOrderMap.set(subCat, overallIndex++)); } else if (catName === 'Lab Results') { categoryOrderMap.set('Lab Results', overallIndex++); const presentLabCats = [...new Set(rowsToDisplay.filter(r => r.type === 'Lab').map(r => r.category))].sort(); presentLabCats.forEach(subCat => categoryOrderMap.set(subCat, overallIndex++)); } else if (catName === 'Lifestyle') { categoryOrderMap.set('Lifestyle', overallIndex++); const lifestyleChildrenKeys = lifestyleCategories.map(cat => cat.key); lifestyleChildrenKeys.forEach(subCatKey => categoryOrderMap.set(subCatKey, overallIndex++)); } else if (catName === 'Biometrics') { categoryOrderMap.set('Biometrics', overallIndex++); const biometricChildrenKeys = biometricCategories.map(cat => cat.key); biometricChildrenKeys.forEach(subCatKey => categoryOrderMap.set(subCatKey, overallIndex++)); } else { categoryOrderMap.set(catName, overallIndex++); } });


    rowsToDisplay.sort((a, b) => {
      const categoryIndexA = categoryOrderMap.get(a.category) ?? Infinity;
      const categoryIndexB = categoryOrderMap.get(b.category) ?? Infinity;
      if (categoryIndexA !== categoryIndexB) {
        return categoryIndexA - categoryIndexB;
      }

      if (physicalExamSubCategoriesSet.has(a.category)) {
        if (a.category === 'PE Summaries') {
          if (a.key === 'physicalExamSummarySimple') return -1;
          if (b.key === 'physicalExamSummarySimple') return 1;
          return 0;
        }
        if (a.type === 'Physical Exam Detail' && b.type === 'Physical Exam Detail') {
          const indexA = peOrderMap.get(a.key) ?? Infinity;
          const indexB = peOrderMap.get(b.key) ?? Infinity;
          return indexA - indexB;
        }
      }

      if (a.category === 'Vitals') {
        const indexA = vitalsDisplayOrder.indexOf(a.key);
        const indexB = vitalsDisplayOrder.indexOf(b.key);
        if (indexA === -1 && indexB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      if (a.category === 'balance' || (a.subcategory === 'Balance' && a.category === 'Biometrics')) {
        const indexA = balanceDisplayOrder.indexOf(a.parameterName);
        const indexB = balanceDisplayOrder.indexOf(b.parameterName);
        if (indexA === -1 && indexB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      if (a.category === 'movement') {
        const indexA = movementDisplayOrder.indexOf(a.key.replace('Lifestyle_', ''));
        const indexB = movementDisplayOrder.indexOf(b.key.replace('Lifestyle_', ''));
        if (indexA === -1 && indexB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      if (a.category === 'biomeasurement') {
        const indexA = biomeasurementDisplayOrder.indexOf(a.key.replace('biometrics_biomeasurement_', ''));
        const indexB = biomeasurementDisplayOrder.indexOf(b.key.replace('biometrics_biomeasurement_', ''));
        if (indexA === -1 && indexB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      if (a.category === 'nutrition') {
        const indexA = nutritionDisplayOrder.indexOf(a.key);
        const indexB = nutritionDisplayOrder.indexOf(b.key);
        if (indexA === -1 && indexB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      // Special handling for Lipid parameters first (check subcategory since category is "Lab Results")
      if (a.type === 'Lab' && a.subcategory === 'Lipid' && b.type === 'Lab' && b.subcategory === 'Lipid') {
        const indexA = lipidDisplayOrder.indexOf(a.parameterName);
        const indexB = lipidDisplayOrder.indexOf(b.parameterName);

        if (indexA === -1 && indexB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      }

      // Lab parameter sorting for non-Lipid parameters
      if (a.type === 'Lab' && labDisplayOrderMap[a.category]) {
        const orderA = labDisplayOrderMap[a.category].indexOf(a.parameterName);
        const orderB = labDisplayOrderMap[a.category].indexOf(b.parameterName);

        if (orderA === -1 && orderB === -1) return a.parameterName.localeCompare(b.parameterName);
        if (orderA === -1) return 1;
        if (orderB === -1) return -1;
        return orderA - orderB;
      } else {
        const nameA = a.parameterName;
        const nameB = b.parameterName;
        if (typeof nameA === 'string' && typeof nameB === 'string') {
          return nameA.localeCompare(nameB);
        } else if (typeof nameA === 'string') {
          return -1;
        } else if (typeof nameB === 'string') {
          return 1;
        } else {
          return 0;
        }
      }
    });

    let finalRowsWithCbcHeaders = [];
    const isCbcView = selectedCategoryFilter === 'CBC' || selectedCategoryFilter === 'Công thức máu' ||
                      (englishCategoryFilter === 'Lab Results' && rowsToDisplay.some(r => r.subcategory === 'CBC' || r.category === 'CBC')) ||
                      (selectedCategoryFilter === 'Lab Results' && rowsToDisplay.some(r => r.subcategory === 'CBC' || r.category === 'CBC')) ||
                      selectedCategoryFilter === 'All';

    if (isCbcView && rowsToDisplay.some(r => r.subcategory === 'CBC' || r.category === 'CBC')) {
      let wbcHeaderAdded = false; let rbcHeaderAdded = false; let plateletHeaderAdded = false;
      const wbcMarker = 'White Blood Cell Count'; const rbcMarker = 'Red Blood Cell Count'; const plateletMarker = 'Platelet Count';
      const emptyTimestampData = transformedData.uniqueTimestamps.reduce((acc, ts) => { acc[ts] = { value: null, notes: null, flag: null }; return acc; }, {});

      for (const row of rowsToDisplay) {
        if (row.subcategory === 'CBC' || row.category === 'CBC') {
          // White Blood Cells section: WBC through Basophil %
          if (!wbcHeaderAdded && cbcDisplayOrder.slice(
            cbcDisplayOrder.indexOf(wbcMarker),
            cbcDisplayOrder.indexOf(rbcMarker)
          ).includes(row.parameterName)) {
            if (row.parameterName === wbcMarker || !finalRowsWithCbcHeaders.find(r => r.key === 'HEADER_WBC')) {
              finalRowsWithCbcHeaders.push({
                key: 'HEADER_WBC',
                parameterName: t('medicalTerms.wbc', 'White Blood Cells') + ' & Differential',
                type: 'CBCheader',
                category: 'CBC',
                ...emptyTimestampData
              });
              wbcHeaderAdded = true;
            }
          }
          // Red Blood Cells section: RBC through RDW-SD
          else if (!rbcHeaderAdded && cbcDisplayOrder.slice(
            cbcDisplayOrder.indexOf(rbcMarker),
            cbcDisplayOrder.indexOf(plateletMarker)
          ).includes(row.parameterName)) {
            if (row.parameterName === rbcMarker || !finalRowsWithCbcHeaders.find(r => r.key === 'HEADER_RBC')) {
              finalRowsWithCbcHeaders.push({
                key: 'HEADER_RBC',
                parameterName: t('medicalTerms.rbc', 'Red Blood Cells'),
                type: 'CBCheader',
                category: 'CBC',
                ...emptyTimestampData
              });
              rbcHeaderAdded = true;
            }
          }
          // Platelets section: PLT and onwards
          else if (!plateletHeaderAdded && cbcDisplayOrder.slice(cbcDisplayOrder.indexOf(plateletMarker)).includes(row.parameterName)) {
            if (row.parameterName === plateletMarker || !finalRowsWithCbcHeaders.find(r => r.key === 'HEADER_PLATELETS')) {
              finalRowsWithCbcHeaders.push({
                key: 'HEADER_PLATELETS',
                parameterName: t('medicalTerms.platelets', 'Platelets'),
                type: 'CBCheader',
                category: 'CBC',
                ...emptyTimestampData
              });
              plateletHeaderAdded = true;
            }
          }
        }
        finalRowsWithCbcHeaders.push(row);
      }

      const uniqueKeys = new Set();
      finalRowsWithCbcHeaders = finalRowsWithCbcHeaders.filter(row => {
        if (row.type === 'CBCheader') {
          if (uniqueKeys.has(row.key)) {
            return false;
          }
          uniqueKeys.add(row.key);
        }
        return true;
      });
    } else {
      finalRowsWithCbcHeaders = rowsToDisplay;
    }
    
    // Debug final filtered lab data for all Vietnamese subcategories
    if (i18n.language === 'vi' && (selectedCategoryFilter?.includes('máu') || selectedCategoryFilter === 'CBC' ||
        selectedCategoryFilter?.includes('Lipid') || selectedCategoryFilter?.includes('Đường huyết') ||
        selectedCategoryFilter?.includes('Gan mật') || selectedCategoryFilter?.includes('Thận') ||
        selectedCategoryFilter?.includes('Điện giải') || selectedCategoryFilter?.includes('Tuyến giáp') ||
        selectedCategoryFilter?.includes('Tim mạch') || selectedCategoryFilter?.includes('Đông máu') ||
        selectedCategoryFilter?.includes('Viêm') || selectedCategoryFilter?.includes('Nước tiểu') ||
        selectedCategoryFilter?.includes('Vitamin') || selectedCategoryFilter === 'Cardiac' ||
        selectedCategoryFilter === 'Coagulation' || selectedCategoryFilter === 'Inflammation' ||
        selectedCategoryFilter === 'Urine' || selectedCategoryFilter === 'Vitamins')) {
      const labType = selectedCategoryFilter;
      const keyPattern = labType === 'CBC' || labType?.includes('máu') ? 'cbc' :
                        labType?.includes('Lipid') ? 'lipid' :
                        labType?.includes('Đường huyết') ? 'glucose' :
                        labType?.includes('Gan mật') ? 'hepatology' :
                        labType?.includes('Thận') ? 'renal' :
                        labType?.includes('Điện giải') ? 'electrolytes' :
                        labType?.includes('Tuyến giáp') ? 'thyroid' :
                        labType?.includes('Tim mạch') || labType === 'Cardiac' ? 'cardiac' :
                        labType?.includes('Đông máu') || labType === 'Coagulation' ? 'coagulation' :
                        labType?.includes('Viêm') || labType === 'Inflammation' ? 'inflammation' :
                        labType?.includes('Nước tiểu') || labType === 'Urine' ? 'urine' :
                        labType?.includes('Vitamin') || labType === 'Vitamins' ? 'vitamins' : 'unknown';
      
      const finalLabRows = finalRowsWithCbcHeaders.filter(row => row.key?.includes(keyPattern));
      
    }
    
    return finalRowsWithCbcHeaders;
  }, [transformedData.parameterRows, selectedCategoryFilter, transformedData.uniqueTimestamps]); 

  const cbcSegments = useMemo(() => {
    // Check for CBC parameters by subcategory instead of category
    const hasCbcData = filteredTableData.some(row => row.subcategory === 'CBC' || row.category === 'CBC');
    
    if (!hasCbcData) { return null; }

    // Define segment boundaries based on the new CBC ordering
    const wbcStartMarker = 'White Blood Cell Count';
    const rbcStartMarker = 'Red Blood Cell Count';
    const plateletStartMarker = 'Platelet Count';

    const wbcData = []; const rbcData = []; const plateletData = [];

    for (const row of filteredTableData) {
      if (row.type === 'CBCheader' || row.subcategory !== 'CBC') continue;
      const paramName = row.parameterName;

      // Use key-based categorization instead of parameterName matching for Vietnamese compatibility
      const key = row.key;
      
      // White Blood Cells section: WBC and differential
      if (key.includes('_wbc') || key.includes('_neut') || key.includes('_lymph') || 
          key.includes('_mono') || key.includes('_eos') || key.includes('_baso') || 
          key.includes('_nlr')) {
        wbcData.push(row);
      }
      // Red Blood Cells section: RBC, HGB, HCT, indices
      else if (key.includes('_rbc') || key.includes('_hgb') || key.includes('_hct') || 
               key.includes('_mcv') || key.includes('_mch') || key.includes('_mchc') || 
               key.includes('_rdw')) {
        rbcData.push(row);
      }
      // Platelets section: PLT and related
      else if (key.includes('_plt') || key.includes('_mpv') || key.includes('_pdw') || 
               key.includes('_pct') || key.includes('_p_lcr')) {
        plateletData.push(row);
      }
    }

    // Sort each segment according to the CBC display order
    const sortByCbcOrder = (a, b) => {
      const indexA = cbcDisplayOrder.indexOf(a.parameterName);
      const indexB = cbcDisplayOrder.indexOf(b.parameterName);
      return indexA - indexB;
    };

    wbcData.sort(sortByCbcOrder);
    rbcData.sort(sortByCbcOrder);
    plateletData.sort(sortByCbcOrder);

    return { wbcData, rbcData, plateletData };
  }, [filteredTableData, cbcDisplayOrder]);

  const emptyColumns = useMemo(() => {
    const emptyTs = new Set(); if (filteredTableData.length > 0) { transformedData.uniqueTimestamps.forEach(ts => { const hasData = filteredTableData.some(row => { const cellData = row[ts]; return cellData && cellData.value !== null && cellData.value !== undefined && cellData.value !== ''; }); if (!hasData) { emptyTs.add(ts); } }); } return emptyTs;
  }, [filteredTableData, transformedData.uniqueTimestamps]);

    const handleDateChange = useCallback((dates) => {
      if (dates) { setDateRange(dates); } else { setDateRange([moment().subtract(1, 'year'), moment()]); }
    }, []);

    const tableColumns = useMemo(() => {
      try {
        const parameterColumn = {
        title: t('tables.parameter', 'Parameter'), dataIndex: 'parameterName', key: 'parameterName', fixed: 'left', width: 350,
        sorter: false, // Disable sorting to preserve our custom CBC order
        render: (text, record) => {
          if (record.type === 'CBCheader') { return <Text strong style={{ padding: '6px 0', display: 'block', color: '#003a8c', backgroundColor: '#e6f4ff' }}>{text}</Text>; }
          if (record.type === 'CognitionHeader') { return <Text strong style={{ color: '#0050b3', backgroundColor: '#e6f7ff', padding: '4px 8px', display: 'block' }}>{text}</Text>; }
          if (record.type === 'Physical Exam Section Header') { return <Text strong style={{ color: '#0050b3' }}>{text}</Text>; }
          if (record.type === 'Physical Exam Summary') { return <Text strong>{record.parameterName || 'Physical Exam Summary'}</Text>; }
          let displayName = text;

          // Use centralized parameter display name utility
          try {
            // Always use the centralized utility for all parameter types
            displayName = getParameterDisplayName(record.key);
            
            // Final debug for Vietnamese lab display
            if (i18n.language === 'vi' && record.key?.startsWith('labs_')) {
              const labType = record.key.includes('cbc') ? 'CBC' :
                            record.key.includes('lipid') ? 'Lipid' :
                            record.key.includes('glucose') ? 'Glucose' :
                            record.key.includes('hepatology') ? 'Hepatology' :
                            record.key.includes('renal') ? 'Renal' :
                            record.key.includes('electrolytes') ? 'Electrolytes' :
                            record.key.includes('thyroid') ? 'Thyroid' :
                            record.key.includes('cardiac') ? 'Cardiac' :
                            record.key.includes('coagulation') ? 'Coagulation' :
                            record.key.includes('inflammation') ? 'Inflammation' :
                            record.key.includes('urine') ? 'Urine' :
                            record.key.includes('vitamins') ? 'Vitamins' : 'Other';
              console.log(`✅ [${labType}-VN-SUCCESS] Resolved display name:`, {
                key: record.key,
                originalText: text,
                vietnameseDisplayName: displayName,
                labType
              });
            }
          } catch (error) {
            // Debug failed parameter resolution for Vietnamese
            if (i18n.language === 'vi') {
              console.error(`⚠️ [VN-ERROR] Parameter not found:`, {
                key: record.key,
                originalText: text,
                error: error.message
              });
              
              // Specific error logging for CBC parameters
              if (record.key?.includes('cbc')) {
                console.error(`❌ [CBC-VN-ERROR] Failed to resolve CBC display name:`, {
                  key: record.key,
                  text: text,
                  error: error.message
                });
              }
            }
            displayName = text; // Display the raw key/text as a last resort
          }

          // Check if this parameter is highlighted from search
          const isHighlighted = highlightedParameterKey === record.key;
          const containerClass = isHighlighted ? 'parameter-search-highlight' : '';
          const containerStyle = {};
          const clinicalRangeInfo = clinicalRanges ? clinicalRanges[record.key] : null;
          const snapshotParam = snapshotData ? snapshotData.find(p => p.key === record.key) : null;

          return (
            <div className={containerClass} style={containerStyle} data-parameter-key={record.key}>
              <Tooltip title={<code>{record.key}</code>} placement="topLeft">
                <Text strong>{displayName}</Text>
              </Tooltip>
              {(record.units || clinicalRangeInfo || record.referenceRange) && (
                <div style={{ fontSize: '0.85em', color: '#666', marginTop: '2px' }}>
                  {record.units && <span>Unit: {record.units}</span>}
                  {clinicalRangeInfo && <span style={{ marginLeft: record.units ? '8px' : '0' }}>Range: {clinicalRangeInfo.range} {clinicalRangeInfo.unit}</span>}
                  {!clinicalRangeInfo && record.referenceRange && (
                    <span style={{ marginLeft: record.units ? '8px' : '0' }}>
                      Range: {record.referenceRange} {record.units || ''}
                    </span>
                  )}
                </div>
              )}
              {snapshotParam && snapshotParam.idealTarget !== undefined && (
                <Text type="secondary" style={{ fontSize: '0.85em', display: 'block', marginTop: '2px', color: '#389e0d', fontWeight: 'bold' }}>
                  Ideal: {snapshotParam.idealTarget.toFixed(1)} {record.units}
                </Text>
              )}
            </div>
          );
        },
      };
      const lastValueColumn = {
        title: 'Most Recent',
        dataIndex: 'lastValue',
        key: 'lastValue',
        fixed: 'left',
        width: 150,
        align: 'center',
        className: 'most-recent-column',
        onHeaderCell: () => ({
          className: 'most-recent-column-header',
        }),
        render: (value, record) => {
          if (value === null || value === undefined) return '-';

          const handleValueClick = () => {
            if (record.trendData && record.trendData.length > 0) {
              setSelectedParameterData(record);
              setIsTrendModalVisible(true);
            }
          };

          const tooltipTitle = record.lastValueTimestamp ? moment(record.lastValueTimestamp).format('YYYY-MM-DD HH:mm') : 'No date available';
          if (record.lastValueType === 'Physical Exam Summary') {
            const examSummaryHtml = value; const summaryTitle = record.parameterName || 'Physical Exam Summary';
            const isFullSummary = summaryTitle.includes('(Full)'); const tagText = isFullSummary ? '[Exam Full]' : '[Exam]';
            const tagColor = isFullSummary ? 'purple' : 'blue';
            const handleTagClick = () => { setExamModalTitle(`Last ${summaryTitle} - ${moment(record.lastValueTimestamp).format('YYYY-MM-DD')}`); setExamModalContent(examSummaryHtml || '<p>No summary available.</p>'); setIsExamModalVisible(true); };
            return ( <Tooltip title={tooltipTitle}> <Tag color={tagColor} style={{ cursor: 'pointer' }} onClick={handleTagClick}> {tagText} </Tag> </Tooltip> ); }
          let displayValue = value;

          // Apply lifestyle parameter formatting to Last Value column
          if (isLifestyleParameter(record.key)) {
              const isMobile = window.innerWidth <= 575; // Mobile breakpoint
              const displayOptions = getDisplayOptionsForScreenSize(isMobile);
              const enhancedValue = formatLifestyleValueForDisplay(record.key, value, displayOptions);

              // Only use enhanced value if it's different from the raw value
              if (enhancedValue !== String(value) && enhancedValue !== 'N/A') {
                  displayValue = enhancedValue;
              }
          } else if (typeof value === 'number') {
              displayValue = Number.isInteger(value) ? value : value.toFixed(1);
          }

          let cellStyle = {};

          // Enhanced lab value color coding based on flags and reference ranges
          if (record.type === 'Lab') {
            const existingFlag = record.flagsByTimestamp && record.lastValueTimestamp ?
              record.flagsByTimestamp[record.lastValueTimestamp] : null;

            // Prioritize clinical reference ranges over lab reference ranges
            const clinicalRangeInfo = clinicalRanges ? clinicalRanges[record.key] : null;
            let referenceRange = record.referenceRange;

            if (clinicalRangeInfo && clinicalRangeInfo.range) {
              referenceRange = `${clinicalRangeInfo.range}`;
            }

            const status = getLabValueStatusWithFlag(value, referenceRange, existingFlag);
            cellStyle = getLabValueStyle(status);
          }
          if (record.key === 'BMI' && value !== null) {
            const bmiValue = parseFloat(value);
            if (bmiValue < 18.5) { cellStyle = { ...cellStyle, color: '#1890ff', fontWeight: 'bold' }; }
            else if (bmiValue >= 18.5 && bmiValue < 25) { cellStyle = { ...cellStyle, color: '#52c41a', fontWeight: 'bold' }; }
            else if (bmiValue >= 25 && bmiValue < 30) { cellStyle = { ...cellStyle, color: '#faad14', fontWeight: 'bold' }; }
            else if (bmiValue >= 30) { cellStyle = { ...cellStyle, color: '#f5222d', fontWeight: 'bold' }; } }
          
          const isClickable = record.trendData && record.trendData.length > 0;
          const spanStyle = { ...cellStyle, cursor: isClickable ? 'pointer' : 'default' };

          return (
            <Tooltip title={tooltipTitle}>
              <span style={spanStyle} onClick={isClickable ? handleValueClick : null}>
                {displayValue}
              </span>
            </Tooltip>
          );
        },
      };
      const allTimestampColumns = transformedData.uniqueTimestamps.map((ts, index) => {
        // Check if this timestamp has any lifestyle parameters
        const hasLifestyleParams = filteredTableData && filteredTableData.some(record => {
          if (isLifestyleParameter(record.key)) {
            const cellData = record[ts];
            return cellData && (cellData.value !== null && cellData.value !== undefined);
          }
          return false;
        });

        return {
          title: (
            <div
              style={{
                textAlign: 'center',
                lineHeight: '1.2',
                cursor: hasLifestyleParams ? 'pointer' : 'default',
                transition: 'all 0.2s'
              }}
              onClick={hasLifestyleParams ? () => handleTimestampColumnClick(ts, filteredTableData) : undefined}
              onMouseEnter={(e) => {
                if (hasLifestyleParams) {
                  e.currentTarget.style.backgroundColor = '#e6f7ff';
                  e.currentTarget.style.borderRadius = '4px';
                }
              }}
              onMouseLeave={(e) => {
                if (hasLifestyleParams) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
              title={hasLifestyleParams ? t('flowsheet.clickToViewLifestyleSnapshot', { defaultValue: 'Click to view lifestyle assessment snapshot' }) : undefined}
            >
              <div>{moment(ts).format('YYYY-MM-DD')}</div>
              <div>{moment(ts).format('HH:mm')}</div>
              {hasLifestyleParams && (
                <div style={{ fontSize: '10px', color: '#1890ff', marginTop: '2px' }}>
                  📋
                </div>
              )}
            </div>
          ),
          dataIndex: ts,
          key: ts,
          width: 110,
          align: 'center',
          render: (cellData, record) => {
          const value = cellData?.value; const notes = cellData?.notes; const flag = cellData?.flag;

          if (value === null || value === undefined) return '-';
          if (record.type === 'Physical Exam Summary') {
            const examSummaryHtml = value; const isFullSummary = record.parameterName?.includes('(Full)');
            const tagText = isFullSummary ? '[Exam Full]' : '[Exam]'; const tagColor = isFullSummary ? 'purple' : 'blue';
            const showExamModal = () => { setExamModalTitle(record.parameterName || 'Physical Exam Summary'); setExamModalContent(examSummaryHtml || '<p>No summary available.</p>'); setIsExamModalVisible(true); };
            return ( <Tag color={tagColor} style={{ cursor: 'pointer' }} onClick={showExamModal}> {tagText} </Tag> ); }
          if (record.type === 'Physical Exam Detail') {
            const displayValue = value;
            const noteIcon = notes ? ( <Tooltip title={notes} overlayStyle={{ maxWidth: '400px' }}> <ProfileOutlined style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'pointer' }} /> </Tooltip> ) : null;
            let detailStyle = {}; const normalValues = ['Normal', 'Absent', 'Intact', 'Negative', '2+', '< 2 sec', 'Midline', 'AC > BC', 'Moist/Pink', 'Clear', 'Symmetric', 'Normoactive', 'Not Palpable', 'Anicteric', 'Present', 'Regular'];
            if (!normalValues.some(norm => value?.startsWith(norm))) { detailStyle = { color: 'orange', fontWeight: 'bold' }; }
            return ( <span style={detailStyle}> {displayValue} {noteIcon} </span> ); }
          if (record.type === 'Questionnaire') {
              // Check if this is a lifestyle parameter and use enhanced display
              let displayValue = value;
              const isLifestyleParam = isLifestyleParameter(record.key);

              if (isLifestyleParam) {
                  // Use the new lifestyle value display utility
                  const isMobile = window.innerWidth <= 575; // Mobile breakpoint
                  const displayOptions = getDisplayOptionsForScreenSize(isMobile);
                  displayValue = formatLifestyleValueForDisplay(record.key, value, displayOptions);
              } else {
                  // Fallback to original logic for non-lifestyle questionnaires
                  const lifestyleParam = getAllLifestyleParameters().find(p => `Lifestyle_${p.key}` === record.key);
                  if (lifestyleParam && lifestyleParam.options) {
                      const selectedOption = lifestyleParam.options.find(option => option.value === value);
                      displayValue = selectedOption ? selectedOption.label : value;
                  }
              }

              const noteIcon = notes ? ( <Tooltip title={notes} overlayStyle={{ maxWidth: '400px' }}> <ProfileOutlined style={{ marginLeft: 4, color: '#8c8c8c', cursor: 'pointer' }} /> </Tooltip> ) : null;

              // Apply styling for lifestyle activity days parameters
              let questionnaireStyle = {};
              if (isLifestyleParam) {
                  const lifestyleParam = getAllLifestyleParameters().find(p => `Lifestyle_${p.key}` === record.key);
                  if (lifestyleParam && lifestyleParam.options === activityDaysOptions) {
                      if (value === 'never' || value === 'rarely') {
                          questionnaireStyle = { backgroundColor: '#fff1f0', color: '#f5222d', padding: '2px 4px', borderRadius: '2px' };
                      }
                      else if (value === 'sometimes') {
                          questionnaireStyle = { backgroundColor: '#fffbe6', color: '#faad14', padding: '2px 4px', borderRadius: '2px' };
                      }
                      else if (value === 'often' || value === 'always') {
                          questionnaireStyle = { backgroundColor: '#f6ffed', color: '#52c41a', padding: '2px 4px', borderRadius: '2px' };
                      }
                  }
                  // Make lifestyle parameters clickable
                  questionnaireStyle = { ...questionnaireStyle, cursor: 'pointer', textDecoration: 'underline' };
              }

              // Handler for lifestyle parameter click
              const handleClick = isLifestyleParam
                ? () => {
                    handleLifestyleParameterClick(record.key, value, ts, notes);
                  }
                : null;

              return (
                <span
                  style={questionnaireStyle}
                  onClick={handleClick}
                  title={isLifestyleParam ? t('flowsheet.clickToViewDetails', { defaultValue: 'Click to view question and answer details' }) : undefined}
                >
                  {displayValue}{noteIcon}
                </span>
              );
          }
          if (record.type === 'BooleanCheckbox' || record.type === 'BooleanCheckboxOption') {
              // Check if this is a lifestyle parameter and use enhanced display
              let displayValue;
              const isLifestyleParam = isLifestyleParameter(record.key);

              // Debug logging
              if (record.key && (record.key.includes('lifestyle') || record.key.includes('Lifestyle'))) {
                console.log('BooleanCheckbox parameter check:', {
                  key: record.key,
                  isLifestyleParam,
                  value
                });
              }

              if (isLifestyleParam) {
                  const isMobile = window.innerWidth <= 575; // Mobile breakpoint
                  const displayOptions = getDisplayOptionsForScreenSize(isMobile);
                  displayValue = formatLifestyleValueForDisplay(record.key, value, displayOptions);
              } else {
                  // Fallback to original logic for non-lifestyle boolean parameters
                  displayValue = value === true ? 'True' : (value === false ? 'False' : '-');
              }

              let cellStyle = value === true ? { color: 'red', fontWeight: 'bold' } : {};

              // Make lifestyle parameters clickable
              if (isLifestyleParam) {
                  cellStyle = { ...cellStyle, cursor: 'pointer', textDecoration: 'underline' };
              }

              // Handler for lifestyle parameter click
              const handleClick = isLifestyleParam
                ? () => {
                    handleLifestyleParameterClick(record.key, value, ts, notes);
                  }
                : null;

              return (
                <span
                  style={cellStyle}
                  onClick={handleClick}
                  title={isLifestyleParam ? t('flowsheet.clickToViewDetails', { defaultValue: 'Click to view question and answer details' }) : undefined}
                >
                  {displayValue}
                </span>
              );
          }
           if (record.category === 'biomeasurement' && (record.key === 'biometrics_biomeasurement_ree' || record.key === 'biometrics_biomeasurement_tee')) {
               const displayValue = value !== null ? parseFloat(value).toFixed(0) : '-'; return <span>{displayValue} {record.units}</span>; }
           if (record.key === 'BMI') {
               const displayValue = value !== null ? parseFloat(value).toFixed(1) : '-'; let bmiStyle = {};
               if (value !== null) {
                   const bmiValue = parseFloat(value);
                   if (bmiValue < 18.5) { bmiStyle = { color: '#1890ff', fontWeight: 'bold' }; }
                   else if (bmiValue >= 18.5 && bmiValue < 25) { bmiStyle = { color: '#52c41a', fontWeight: 'bold' }; }
                   else if (bmiValue >= 25 && bmiValue < 30) { bmiStyle = { color: '#faad14', fontWeight: 'bold' }; }
                   else if (bmiValue >= 30) { bmiStyle = { color: '#f5222d', fontWeight: 'bold' }; } }
               return <span style={bmiStyle}>{displayValue}</span>; }
          let cellStyle = {};

          // Enhanced lab value color coding based on flags and reference ranges
          if (record.type === 'Lab') {
            // Prioritize clinical reference ranges over lab reference ranges
            const clinicalRangeInfo = clinicalRanges ? clinicalRanges[record.key] : null;
            let referenceRange = record.referenceRange;

            if (clinicalRangeInfo && clinicalRangeInfo.range) {
              referenceRange = `${clinicalRangeInfo.range}`;
            }

            const status = getLabValueStatusWithFlag(value, referenceRange, flag);
            cellStyle = getLabValueStyle(status);
          }
          let trendIndicator = null;
          if (record.type === 'Lab' && index === transformedData.uniqueTimestamps.length - 1 && record.trendData && record.trendData.length >= 2) {
              const lastVal = record.trendData[record.trendData.length - 1].value; const secondLastVal = record.trendData[record.trendData.length - 2].value;
              const indicatorStyle = { marginLeft: '4px', fontSize: '0.9em' };
              if (lastVal > secondLastVal) trendIndicator = <Tag color="error" style={indicatorStyle}>▲</Tag>;
              else if (lastVal < secondLastVal) trendIndicator = <Tag color="processing" style={indicatorStyle}>▼</Tag>; }
          if (record.key === 'tempC' && typeof value === 'number') {
              const tempF = (value * 9/5) + 32;
              return ( <span style={cellStyle}> <div>{value.toFixed(1)} °C</div> <div>{tempF.toFixed(1)} °F</div> {trendIndicator} </span> ); }
          let displayValue = value;

          // Check if this is a lifestyle parameter that wasn't caught by the type-specific handlers above
          if (isLifestyleParameter(record.key) && displayValue === value) {
              const isMobile = window.innerWidth <= 575;
              const displayOptions = getDisplayOptionsForScreenSize(isMobile);
              const enhancedValue = formatLifestyleValueForDisplay(record.key, value, displayOptions);

              // Only use enhanced value if it's different from the raw value
              if (enhancedValue !== String(value) && enhancedValue !== 'N/A') {
                  displayValue = enhancedValue;


              }
          }

          if (record.type === 'Score') {
            const safeValue = typeof value === 'object' && value !== null && value.value !== undefined ? value.value : value;
            displayValue = `${safeValue}%`;
          }
          else if (record.type === 'Diagnostic') { displayValue = typeof value === 'string' && value.length > 30 ? value.substring(0, 27) + '...' : value; }
          else if (typeof value === 'number' && displayValue === value) { displayValue = Number.isInteger(value) ? value : value.toFixed(1); }

          // Safety check to ensure displayValue is not an object before rendering
          const safeDisplayValue = typeof displayValue === 'object' && displayValue !== null ?
            (displayValue.value !== undefined ? displayValue.value : JSON.stringify(displayValue)) :
            displayValue;

          return <span style={cellStyle}>{safeDisplayValue}{trendIndicator}</span>;
          }
        };
      });
      const finalTimestampColumns = allTimestampColumns.filter(col => !hideEmptyColumns || !emptyColumns.has(col.key) );
      return [parameterColumn, lastValueColumn, ...finalTimestampColumns];
    } catch (error) { console.error("Error generating table columns:", error); return [{ title: 'Parameter', dataIndex: 'parameterName', key: 'parameterName', fixed: 'left' }]; }
  }, [transformedData.uniqueTimestamps, filteredTableData, hideEmptyColumns, emptyColumns, setIsExamModalVisible, setExamModalContent, setExamModalTitle, highlightedParameterKey, i18n.language, t]);

  useEffect(() => {
    if (tableContainerRef.current) {
      setTimeout(() => { const tableBody = tableContainerRef.current.querySelector('.ant-table-body'); if (tableBody) { tableBody.scrollLeft = tableBody.scrollWidth; } }, 0); }
  }, [tableColumns]);

  useEffect(() => {
    if (!patientId) {
      console.warn("PatientFlowsheet rendered without patientId prop");
      const pathParts = window.location.pathname.split('/');
      const potentialPatientId = pathParts[pathParts.indexOf('patients') + 1];
      if (potentialPatientId) { fetchFlowsheetData(dateRange); } }
  }, [patientId, fetchFlowsheetData, dateRange]);

  // Missing function implementations
  const fetchPendingLabOrders = useCallback(async (patientId) => {
    try {
      // Implementation for fetching pending lab orders

      // This would typically make an API call to get pending orders
    } catch (error) {
      console.error('Error fetching pending lab orders:', error);
    }
  }, []);

  const handleAddDataCancel = useCallback(() => {
    setAddDataMode(null);
    setSelectedTimestamp(moment());
  }, []);

  const renderFlowsheet = () => {
    try {
      return (
        <Layout className="flowsheet-container" style={{ height: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'row', overflow: 'hidden', background: '#fff' }}>
          <Sider width={250} theme="light" style={{ borderRight: '1px solid #f0f0f0', background: '#fff', flexShrink: 0 }}>
            <div style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}> <Text strong>{t('patientChart.flowsheet.categories')}</Text> </div>
            <Menu mode="inline" selectedKeys={[selectedCategoryFilter]} onClick={(e) => setSelectedCategoryFilter(getInternalKey(e.key))} style={{ height: 'calc(100% - 41px)', overflowY: 'auto', borderRight: 0 }} className="flowsheet-category-menu" >
              {transformedData.categories.map(cat => {
                const categoryCount = transformedData.parameterCounts.get(cat.key) || 0;
                const totalAvailable = transformedData.totalAvailableCounts.get(cat.key) || 0;
                
                const categoryTitleElement = (
                  <span>{cat.title}</span>
                );

                if (cat.children && cat.children.length > 0) {
                  return (
                    <SubMenu
                      key={cat.key}
                      title={<Tooltip title={`${categoryCount} Parameters`} placement="right">{categoryTitleElement}</Tooltip>}
                    >
                      {cat.children.map(child => {
                        const childCount = transformedData.parameterCounts.get(child.key) || 0;
                        const childTotalAvailable = transformedData.totalAvailableCounts.get(child.key) || 0;
                        
                        const childTitleElement = (
                          <span>{child.title}</span>
                        );
                        return (
                          <Menu.Item key={child.key}>
                            <Tooltip title={`${childCount} Parameters`} placement="right">{childTitleElement}</Tooltip>
                          </Menu.Item>
                        );
                      })}
                    </SubMenu>
                  );
                }
                return (
                  <Menu.Item key={cat.key}>
                    <Tooltip title={`${categoryCount} Parameters`} placement="right">{categoryTitleElement}</Tooltip>
                  </Menu.Item>
                );
              })}
            </Menu>
          </Sider>
          <Layout style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#fff' }}>
            <div style={{ padding: '16px 16px 0 16px', flexShrink: 0, background: '#fff' }}>
              <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Space>
                  <RangePicker value={dateRange} onChange={handleDateChange} allowClear={false} />
                  <ParameterSearchInput
                    onParameterSelect={handleParameterSelect}
                    onSearchClear={() => setHighlightedParameterKey(null)}
                    style={{ width: '300px' }}
                    placeholder="Search Parameters..."
                  />
                </Space>
                <Space>
                  <Switch checked={hideEmptyColumns} onChange={setHideEmptyColumns} size="small" />
                  <Text style={{ marginLeft: 4, marginRight: 8 }}>Hide Empty Columns</Text>
                  <Button
                    type="primary"
                    style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }} // Green button
                    onClick={() => setIsCarePlanningModalVisible(true)}
                  >
                    {t('patientChart.flowsheet.carePlanningData')}
                  </Button>
                  <Button
                    type="primary"
                    style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }} // Purple button for PDF import
                    onClick={() => setIsLabPdfImportModalVisible(true)}
                  >
                    📄 Import Lab PDF
                  </Button>
                  {/* Category-specific buttons */}
                  {(englishCategoryFilter === 'Vitals' || selectedCategoryFilter === 'Vitals') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                      onClick={() => setIsVitalsAddModalVisible(true)}
                    >
                      + Vitals
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Balance' || selectedCategoryFilter === 'Balance' || selectedCategoryFilter === 'Thăng bằng') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      onClick={() => setIsBalanceAddModalVisible(true)}
                    >
                      + Balance Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Biomeasurement' || selectedCategoryFilter === 'Biomeasurement' || selectedCategoryFilter === 'Đo sinh trắc') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16' }}
                      onClick={() => setIsBiomeasurementAddModalVisible(true)}
                    >
                      + Biomeasurement Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Cognition' || selectedCategoryFilter === 'Cognition' || selectedCategoryFilter === 'Nhận thức') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      onClick={() => setIsCognitionAddModalVisible(true)}
                    >
                      + Cognition Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Endurance' || selectedCategoryFilter === 'Endurance' || selectedCategoryFilter === 'Sức bền') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
                      onClick={() => setIsEnduranceAddModalVisible(true)}
                    >
                      + Endurance Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Flexibility' || selectedCategoryFilter === 'Flexibility' || selectedCategoryFilter === 'Độ linh hoạt') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                      onClick={() => setIsFlexibilityAddModalVisible(true)}
                    >
                      + Flexibility Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Power' || selectedCategoryFilter === 'Power' || selectedCategoryFilter === 'Sức mạnh bùng nổ') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#f5222d', borderColor: '#f5222d' }}
                      onClick={() => setIsPowerAddModalVisible(true)}
                    >
                      + Power Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Pulmonary' || selectedCategoryFilter === 'Pulmonary' || selectedCategoryFilter === 'Phổi') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#13c2c2', borderColor: '#13c2c2' }}
                      onClick={() => setIsPulmonaryAddModalVisible(true)}
                    >
                      + Pulmonary Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Strength' || selectedCategoryFilter === 'Strength' || selectedCategoryFilter === 'Sức mạnh') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#eb2f96', borderColor: '#eb2f96' }}
                      onClick={() => setIsStrengthAddModalVisible(true)}
                    >
                      + Strength Data
                    </Button>
                  )}
                  {(englishCategoryFilter === 'Vision' || selectedCategoryFilter === 'Vision' || selectedCategoryFilter === 'Thị lực') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                      onClick={() => setIsVisionAddModalVisible(true)}
                    >
                      + Vision Data
                    </Button>
                  )}
                  {/* TEMPORARY: Always show button for testing - Remove condition after testing */}
                  {true && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#fa541c', borderColor: '#fa541c' }}
                      onClick={() => {
                        console.log('[ImagingPdfUpload] Button clicked');
                        console.log('[DEBUG] selectedCategoryFilter:', selectedCategoryFilter);
                        console.log('[DEBUG] englishCategoryFilter:', englishCategoryFilter);
                        setIsImagingPdfUploadVisible(true);
                      }}
                    >
                      📄 Upload Imaging PDF (TEST)
                    </Button>
                  )}
                  {/* ORIGINAL CONDITION (commented out for testing):
                  {(englishCategoryFilter === 'Imaging' || selectedCategoryFilter === 'Imaging' || selectedCategoryFilter === 'Hình ảnh') && (
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#fa541c', borderColor: '#fa541c' }}
                      onClick={() => setIsImagingPdfUploadVisible(true)}
                    >
                      📄 Upload Imaging PDF
                    </Button>
                  )}
                  */}
                  <Button type="primary" onClick={() => setIsEnhancedAddDataModalVisible(true)}>+ {t('patientChart.flowsheet.addData')}</Button>
                  {user && user.role === 'Admin' && (
                    <Tooltip title="Manage Flowsheet Data">
                      <Button
                        icon={<SettingOutlined />}
                        onClick={() => setIsManagementPanelVisible(true)}
                      >
                        {t('patientChart.flowsheet.dataManagement')}
                      </Button>
                    </Tooltip>
                  )}
                </Space>
              </Space>
              {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
              {transformedData.uniqueTimestamps.length > 0 && transformedData.uniqueTimestamps.length !== (tableColumns.length - 2) && hideEmptyColumns && ( <div style={{ marginBottom: 8, fontSize: '0.9em', color: '#8c8c8c' }}> Note: {transformedData.uniqueTimestamps.length - (tableColumns.length - 2)} columns with no data are hidden </div> )}
            </div>
            <div ref={tableContainerRef} style={{ flex: 1, padding: '0 16px 16px 16px', overflow: 'auto', background: '#fff' }}>
              {(englishCategoryFilter === 'CBC' || selectedCategoryFilter === 'CBC' || selectedCategoryFilter === 'Công thức máu') && cbcSegments ? (
                <>
                  <Typography.Title level={5} style={{ marginTop: '16px', marginBottom: '8px' }}>{t('medicalTerms.wbc', 'White Blood Cells')} & Differential</Typography.Title>
                  <Table key={`wbc-${i18n.language}`} columns={tableColumns} dataSource={cbcSegments.wbcData.sort((a, b) => {
                    const indexA = cbcDisplayOrder.indexOf(a.parameterName);
                    const indexB = cbcDisplayOrder.indexOf(b.parameterName);
                    return indexA - indexB;
                  })} pagination={false} size="small" scroll={{ x: 'max-content' }} bordered rowKey="key" className="flowsheet-table cbc-segment-table" />
                  <Typography.Title level={5} style={{ marginTop: '24px', marginBottom: '8px' }}>{t('medicalTerms.rbc', 'Red Blood Cells')}</Typography.Title>
                  <Table key={`rbc-${i18n.language}`} columns={tableColumns} dataSource={cbcSegments.rbcData.sort((a, b) => {
                    const indexA = cbcDisplayOrder.indexOf(a.parameterName);
                    const indexB = cbcDisplayOrder.indexOf(b.parameterName);
                    return indexA - indexB;
                  })} pagination={false} size="small" scroll={{ x: 'max-content' }} bordered rowKey="key" className="flowsheet-table cbc-segment-table" />
                  <Typography.Title level={5} style={{ marginTop: '24px', marginBottom: '8px' }}>{t('medicalTerms.platelets', 'Platelets')}</Typography.Title>
                  <Table key={`platelet-${i18n.language}`} columns={tableColumns} dataSource={cbcSegments.plateletData.sort((a, b) => {
                    const indexA = cbcDisplayOrder.indexOf(a.parameterName);
                    const indexB = cbcDisplayOrder.indexOf(b.parameterName);
                    return indexA - indexB;
                  })} pagination={false} size="small" scroll={{ x: 'max-content' }} bordered rowKey="key" className="flowsheet-table cbc-segment-table" />
                </>
              ) : ( <Table key={`main-${i18n.language}`} columns={tableColumns} dataSource={filteredTableData.sort((a, b) => {
                // Force CBC ordering at table render level
                const aIsCBC = cbcDisplayOrder.includes(a.parameterName);
                const bIsCBC = cbcDisplayOrder.includes(b.parameterName);

                if (aIsCBC && bIsCBC) {
                  const indexA = cbcDisplayOrder.indexOf(a.parameterName);
                  const indexB = cbcDisplayOrder.indexOf(b.parameterName);
                  return indexA - indexB;
                }

                if (aIsCBC && !bIsCBC) return -1;
                if (!aIsCBC && bIsCBC) return 1;

                // Force Survey ordering at table render level
                const surveyDisplayOrder = [
                  'Life Changes (6 months)',
                  'New Diagnosis (6 months)',
                  'Hospitalization (6 months)',
                  'Current Medications',
                  'Health Improvement'
                ];

                const aIsSurvey = a.category === 'Surveys' && surveyDisplayOrder.includes(a.parameterName);
                const bIsSurvey = b.category === 'Surveys' && surveyDisplayOrder.includes(b.parameterName);

                if (aIsSurvey && bIsSurvey) {
                  const indexA = surveyDisplayOrder.indexOf(a.parameterName);
                  const indexB = surveyDisplayOrder.indexOf(b.parameterName);
                  return indexA - indexB;
                }

                if (aIsSurvey && !bIsSurvey) return -1;
                if (!aIsSurvey && bIsSurvey) return 1;

                return 0;
              })} pagination={false} size="small" scroll={{ x: 'max-content' }} bordered rowKey="key" className="flowsheet-table" /> )}
            </div>
          </Layout>
        </Layout>
      );
    } catch (error) { console.error("Error rendering flowsheet:", error); setRenderError(error.message || "An error occurred rendering the flowsheet"); return ( <Alert message="Error Rendering Flowsheet" description={`There was a problem displaying the flowsheet data. Please try refreshing the page. Technical details: ${renderError}`} type="error" showIcon /> ); }
  };

  const handleModalClose = useCallback(() => { setIsExamModalVisible(false); setExamModalContent(''); }, []);

  // Enhanced Add Data Modal handlers
  const handleEnhancedDataSave = useCallback(async (dataPayload) => {
    try {
      const { timestamp, parameterData, selectedParameters } = dataPayload;



      // Process each category's data separately
      for (const [category, categoryData] of Object.entries(parameterData)) {
        let endpoint = '';
        let payload = {};
        let successMessage = '';



        switch (category) {
          case 'Vitals':
            endpoint = `/patients/${patientId}/flowsheet/vitals`;
            // Transform vitals data to use standardized parameter keys
            // The backend expects vitals object with standardized keys
            payload = { entryDate: timestamp, vitals: categoryData };
            successMessage = 'Vitals data saved successfully!';
            break;

          case 'Labs':
          case 'Lab Results': // Handle both "Labs" and "Lab Results" category names
            endpoint = `/patients/${patientId}/flowsheet/labs`;
            // Transform lab data to expected format
            // The backend expects labComponents with testName as the standardized key
            const labComponents = Object.entries(categoryData).map(([key, value]) => {
              const param = selectedParameters.find(p => p.key === key);
              const component = {
                testName: key, // Use the standardized parameter key directly
                value: value,
                units: param?.units || '',
                category: param?.subcategory || 'Uncategorized'
              };

              return component;
            });
            payload = { timestamp, formData: { labComponents } };

            successMessage = 'Lab data saved successfully!';
            break;

          case 'Biometrics':
            endpoint = `/patients/${patientId}/flowsheet/biometrics`;
            // Backend expects a flat object with standardized keys
            payload = { entryDate: timestamp, biometrics: categoryData };
            successMessage = 'Biometric data saved successfully!';
            break;

          case 'Physical Exam':
            // Handle physical exam data
            const findingsArray = Object.entries(categoryData).map(([key, value]) => {
              const param = selectedParameters.find(p => p.key === key);
              return {
                parameter: key,
                value: value,
                section: param?.subcategory || 'General'
              };
            });
            endpoint = `/patients/${patientId}/flowsheet/physical-exam-full`;
            payload = { findingsArray, entryDate: timestamp };
            successMessage = 'Physical exam data saved successfully!';
            break;

          case 'Lifestyle':
            endpoint = `/patients/${patientId}/flowsheet/lifestyle`;
            payload = { entryDate: timestamp, formData: categoryData };
            successMessage = 'Lifestyle data saved successfully!';
            break;

          case 'Imaging':
            endpoint = `/patients/${patientId}/flowsheet/imaging`;
            // Backend expects a flat object with standardized keys
            payload = { entryDate: timestamp, findings: categoryData };
            successMessage = 'Imaging data saved successfully!';
            break;

          default:
            console.warn(`Unknown category: ${category}`);
            continue;
        }

        if (endpoint) {
          await axios.post(endpoint, payload);
        }
      }

      // Refresh flowsheet data
      await fetchFlowsheetData(dateRange);
      message.success(`Successfully saved data for ${selectedParameters.length} parameter(s)!`);

    } catch (error) {
      console.error('Error saving enhanced data:', error);
      message.error(`Failed to save data: ${error.response?.data?.message || error.message}`);
      throw error; // Re-throw to let the form handle it
    }
  }, [patientId, fetchFlowsheetData, dateRange]);

  const handleEnhancedModalClose = useCallback(() => {
    setIsEnhancedAddDataModalVisible(false);
  }, []);
  // Removed duplicate handleDateChange that was here. The correct one is defined earlier within the component.

  // Utility function to calculate page layout offsets
  const getPageLayoutOffsets = useCallback(() => {
    // Calculate dynamic offsets based on actual page elements
    const header = document.querySelector('header') || document.querySelector('.ant-layout-header');
    const navigation = document.querySelector('nav') || document.querySelector('.ant-menu');
    const searchSection = document.querySelector('[data-testid="parameter-search"]') ||
                          document.querySelector('.parameter-search') ||
                          document.querySelector('[style*="background"]'); // Search section with background

    let topOffset = 0;
    let bottomOffset = 50; // Default bottom padding

    if (header) {
      topOffset += header.offsetHeight;
    }
    if (navigation && navigation !== header) {
      topOffset += navigation.offsetHeight;
    }
    if (searchSection && searchSection !== header && searchSection !== navigation) {
      topOffset += searchSection.offsetHeight;
    }

    // Add some padding for better visual spacing
    topOffset += 20;


    return { topOffset, bottomOffset };
  }, []);

  // Handle parameter selection from search
  const handleParameterSelect = useCallback((parameter, categoryKey) => {
    // 1. Switch to the correct category filter
    setSelectedCategoryFilter(getInternalKey(categoryKey));
    // 2. Set the key of the parameter to be highlighted
    setHighlightedParameterKey(parameter.key);
  }, []); // Dependencies are removed as direct state setters are stable

  // This effect handles the scrolling and highlighting AFTER the state has updated and the component has re-rendered
  useEffect(() => {
    if (!highlightedParameterKey) return;

    const scrollToParameter = () => {
      try {
        const parameterRow = document.querySelector(`[data-parameter-key="${highlightedParameterKey}"]`);

        if (parameterRow) {
          // Found the element, scroll to it
          // Successfully scrolled to parameter
          parameterRow.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest',
          });
          // The highlighting is now persistent as we are not setting highlightedParameterKey to null.
          // The parent component is responsible for clearing the highlight by changing initialFilterKey.
        } else {
          console.error(`Failed to find parameter element with key: ${highlightedParameterKey}.`);
          message.warning(`Could not scroll to the selected parameter. It may not be visible in the current view.`);
        }
      } catch (error) {
        console.error('Error in scrollToParameter:', error);
        message.error('An error occurred while trying to scroll to the parameter.');
      }
    };

    // We use a short timeout to ensure that the DOM has been updated with the new filter
    // before we try to find and scroll to the element.
    const timer = setTimeout(scrollToParameter, 150); // A slightly longer delay to ensure render completes

    return () => clearTimeout(timer);

  }, [highlightedParameterKey, filteredTableData]); // Rerun this effect when the key or data changes


  const handleSaveData = async (formData, categoryName) => {
    const timestamp = selectedTimestamp.toISOString();
    if (addDataMode === 'labs' && formData.linkedOrderId) {
      try { await axios.patch(`/orders/${formData.linkedOrderId}`, { status: 'Resulted' }); message.success(`Lab order ${formData.linkedOrderId} status updated to Resulted.`); if (patientId) fetchPendingLabOrders(patientId);
      } catch (orderUpdateError) { console.error(`Failed to update status for order ${formData.linkedOrderId}:`, orderUpdateError); message.error(`Failed to update status for linked lab order. Please update manually if needed.`); } }
    try {
      let endpoint = ''; let payload = {}; let successMessageText = '';
      if (addDataMode === 'vitals') { endpoint = `/patients/${patientId}/flowsheet/vitals`; payload = { entryDate: timestamp, vitals: formData }; successMessageText = 'Vitals data saved successfully!';
      } else if (addDataMode === 'imaging-bladder-us') { 
        endpoint = `/patients/${patientId}/flowsheet/imaging-details`;
        const parameterEntries = Object.keys(formData).map(key => {
          let paramKey = ''; let units = ''; let value = formData[key];
          if (key === 'pre_void_volume') { paramKey = 'imaging_bladder_us_pre_void_volume'; units = 'mL'; }
          else if (key === 'pvr_volume') { paramKey = 'imaging_bladder_us_pvr_volume'; units = 'mL'; }
          else if (key === 'wall_thickness') { paramKey = 'imaging_bladder_us_wall_thickness'; units = 'mm'; }
          else if (key === 'calculi_presence') { paramKey = 'imaging_bladder_us_calculi_presence'; }
          else if (key === 'calculi_description') { paramKey = 'imaging_bladder_us_calculi_description'; }
          else if (key === 'masses_presence') { paramKey = 'imaging_bladder_us_masses_presence'; }
          else if (key === 'masses_description') { paramKey = 'imaging_bladder_us_masses_description'; }
          else if (key === 'ureteral_jets') { paramKey = 'imaging_bladder_us_ureteral_jets'; }
          if (paramKey && value !== undefined) { return { name: paramKey, value: value, units: units, entryDate: timestamp, type: 'ImagingDetail', category: 'Imaging', }; }
          return null;
        }).filter(entry => entry !== null);
        payload = { imagingDetails: parameterEntries }; successMessageText = 'Bladder Ultrasound data saved successfully!';
      } else if (addDataMode === 'exam') {
        endpoint = `/patients/${patientId}/flowsheet/physical-exam-full`; 
        payload = { findingsArray: formData.findingsArray, htmlSummary: formData.htmlSummary, entryDate: timestamp }; 
        setLoading(true); 
        await axios.post(endpoint, payload); 
        message.success(`Physical Exam data saved successfully!`); 
        handleAddDataCancel(); 
        fetchFlowsheetData(dateRange);
      } else if (addDataMode === 'biometrics') {
        endpoint = `/patients/${patientId}/flowsheet/biometrics`; payload = { entryDate: timestamp, category: categoryName, biometrics: formData }; successMessageText = 'Biometrics data saved successfully!';
      } else if (addDataMode === 'labs') {
        endpoint = `/patients/${patientId}/flowsheet/labs`; payload = { timestamp: timestamp, formData: formData }; successMessageText = 'Labs data saved successfully!';
      } else if (addDataMode.startsWith('lifestyle-')) {
        endpoint = `/patients/${patientId}/flowsheet/lifestyle`; payload = { entryDate: timestamp, formData: formData }; successMessageText = 'Lifestyle data saved successfully!';
      } else if (addDataMode.startsWith('imaging-')) {
        endpoint = `/patients/${patientId}/flowsheet/imaging`; payload = { entryDate: timestamp, studyType: categoryName, findings: formData }; successMessageText = `${categoryName} data saved successfully!`;
      } else { message.error('Invalid data type selected.'); return; }
      if (endpoint && addDataMode !== 'exam') { setLoading(true); await axios.post(endpoint, payload); message.success(successMessageText); handleAddDataCancel(); fetchFlowsheetData(dateRange); }
    } catch (err) {
      console.error(`Error saving ${addDataMode} data. Full Axios error object:`, err);
      if (err.response) { console.error('Backend response data:', err.response.data); console.error('Backend response status:', err.response.status); console.error('Backend response headers:', err.response.headers); }
      message.error(`Failed to save ${addDataMode} data. ${err.response?.data?.message || err.message || 'Please try again.'}`);
    } finally { if (addDataMode !== 'exam') { setLoading(false); } }
  };

  if (renderError) { return ( <Alert message="Error Rendering Flowsheet" description={`There was a problem displaying the flowsheet data. Please try refreshing the page. Technical details: ${renderError}`} type="error" showIcon /> ); }

  return (
    <>
      <style>
        {`
          .most-recent-column,
          .most-recent-column-header {
            background-color: #fafafa !important;
          }

          @keyframes glow {
            0% {
              box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3), 0 0 0 0 rgba(24, 144, 255, 0.4);
            }
            100% {
              box-shadow: 0 6px 20px rgba(24, 144, 255, 0.5), 0 0 0 4px rgba(24, 144, 255, 0.2);
            }
          }

          .parameter-search-highlight {
            background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%) !important;
            border: 3px solid #1890ff !important;
            border-radius: 6px !important;
            padding: 8px !important;
            animation: glow 2s ease-in-out infinite alternate !important;
            position: relative !important;
            z-index: 10 !important;
          }

          .parameter-search-highlight::before {
            content: "🎯" !important;
            position: absolute !important;
            top: -8px !important;
            right: -8px !important;
            background: #1890ff !important;
            color: white !important;
            border-radius: 50% !important;
            width: 20px !important;
            height: 20px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            font-size: 10px !important;
            z-index: 11 !important;
          }
        `}
      </style>
      {renderFlowsheet()}
      <Modal title={examModalTitle} open={isExamModalVisible} onOk={handleModalClose} onCancel={handleModalClose} width={800} footer={[ <Button key="close" onClick={handleModalClose}> Close </Button>, ]} >
        <div dangerouslySetInnerHTML={{ __html: examModalContent }} style={{ maxHeight: '60vh', overflowY: 'auto' }} />
      </Modal>
      


      {/* Conditionally render the Lab PDF Import Modal */}
      {isLabPdfImportModalVisible && (
        <LabPdfImportModal
          patientId={patientId}
          visible={isLabPdfImportModalVisible}
          onClose={(importedSuccessfully, importedLabCollectionDate) => {
            setIsLabPdfImportModalVisible(false);
            if (importedSuccessfully) {
              // If lab collection date is provided and outside current date range, expand the range
              if (importedLabCollectionDate) {
                const importDate = moment(importedLabCollectionDate);
                const currentStart = dateRange[0];
                const currentEnd = dateRange[1];

                // Check if import date is outside current range
                if (importDate.isBefore(currentStart) || importDate.isAfter(currentEnd)) {
                  const newStart = moment.min(currentStart, importDate);
                  const newEnd = moment.max(currentEnd, importDate);
                  setDateRange([newStart, newEnd]);
                  message.info(`Date range expanded to include imported lab data from ${importDate.format('YYYY-MM-DD')}`);
                  fetchFlowsheetData([newStart, newEnd]);
                } else {
                  fetchFlowsheetData(dateRange); // Refresh data if import was successful
                }
              } else {
                fetchFlowsheetData(dateRange); // Refresh data if import was successful
              }
            }
          }}
        />
      )}

      {patientId && user && user.role === 'Admin' && (
        <FlowsheetDataManagementPanel
          visible={isManagementPanelVisible}
          onClose={() => setIsManagementPanelVisible(false)}
          patientId={patientId}
          // Pass other necessary props like selectedCategory or dateRange if panel needs to be filtered initially
        />
      )}

      {patientId && ( // Render CarePlanningDataModal if patientId exists
        <CarePlanningDataModal
          visible={isCarePlanningModalVisible}
          onClose={() => {
            setIsCarePlanningModalVisible(false);
            fetchFlowsheetData(dateRange); // Refresh flowsheet data after modal closes
          }}
          patientId={patientId}
        />
      )}

      {/* Enhanced Add Data Modal */}
      <EnhancedAddDataModal
        visible={isEnhancedAddDataModalVisible}
        onClose={handleEnhancedModalClose}
        onSave={handleEnhancedDataSave}
        patientId={patientId}
        title="Add Patient Data - Universal Parameter Selection"
        loading={loading}
      />

      <TrendModal
        visible={isTrendModalVisible}
        onCancel={() => setIsTrendModalVisible(false)}
        parameterData={selectedParameterData}
      />

      {/* Lifestyle Parameter Detail Modal */}
      <LifestyleParameterDetailModal
        visible={isLifestyleDetailModalVisible}
        onClose={() => setIsLifestyleDetailModalVisible(false)}
        parameterKey={lifestyleDetailData?.parameterKey}
        value={lifestyleDetailData?.value}
        timestamp={lifestyleDetailData?.timestamp}
        notes={lifestyleDetailData?.notes}
        parameters={lifestyleDetailData?.parameters}
      />

      {/* Vitals Add Modal */}
      <VitalsAddModal
        visible={isVitalsAddModalVisible}
        onClose={() => setIsVitalsAddModalVisible(false)}
        onSave={() => {
          setIsVitalsAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Balance Add Modal */}
      <BalanceAddModal
        visible={isBalanceAddModalVisible}
        onClose={() => setIsBalanceAddModalVisible(false)}
        onSave={() => {
          setIsBalanceAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Biomeasurement Add Modal */}
      <BiomeasurementAddModal
        visible={isBiomeasurementAddModalVisible}
        onClose={() => setIsBiomeasurementAddModalVisible(false)}
        onSave={() => {
          setIsBiomeasurementAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Cognition Add Modal */}
      <CognitionAddModal
        visible={isCognitionAddModalVisible}
        onClose={() => setIsCognitionAddModalVisible(false)}
        onSave={() => {
          setIsCognitionAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Endurance Add Modal */}
      <EnduranceAddModal
        visible={isEnduranceAddModalVisible}
        onClose={() => setIsEnduranceAddModalVisible(false)}
        onSave={() => {
          setIsEnduranceAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Flexibility Add Modal */}
      <FlexibilityAddModal
        visible={isFlexibilityAddModalVisible}
        onClose={() => setIsFlexibilityAddModalVisible(false)}
        onSave={() => {
          setIsFlexibilityAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Power Add Modal */}
      <PowerAddModal
        visible={isPowerAddModalVisible}
        onClose={() => setIsPowerAddModalVisible(false)}
        onSave={() => {
          setIsPowerAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Pulmonary Add Modal */}
      <PulmonaryAddModal
        visible={isPulmonaryAddModalVisible}
        onClose={() => setIsPulmonaryAddModalVisible(false)}
        onSave={() => {
          setIsPulmonaryAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Strength Add Modal */}
      <StrengthAddModal
        visible={isStrengthAddModalVisible}
        onClose={() => setIsStrengthAddModalVisible(false)}
        onSave={() => {
          setIsStrengthAddModalVisible(false);
          fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Vision Add Modal */}
      <VisionAddModal
        visible={isVisionAddModalVisible}
        onClose={() => setIsVisionAddModalVisible(false)}
        onSave={async () => {
          setIsVisionAddModalVisible(false);

          // Add a small delay to ensure the backend has processed the data
          await new Promise(resolve => setTimeout(resolve, 500));

          await fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
        }}
        patientId={patientId}
      />

      {/* Imaging PDF Upload Modal */}
      {isImagingPdfUploadVisible && (
        <Modal
          title="Upload Imaging Report PDF"
          open={isImagingPdfUploadVisible}
          onCancel={() => setIsImagingPdfUploadVisible(false)}
          footer={null}
          width={900}
          destroyOnClose
        >
          <ImagingPdfUpload
            patientId={patientId}
            onSuccess={async () => {
              setIsImagingPdfUploadVisible(false);
              message.success('Imaging data imported successfully!');

              // Add a small delay to ensure the backend has processed the data
              await new Promise(resolve => setTimeout(resolve, 500));

              await fetchFlowsheetData(dateRange); // Refresh flowsheet data after saving
            }}
            onCancel={() => setIsImagingPdfUploadVisible(false)}
          />
        </Modal>
      )}
    </>
  );
};

export default PatientFlowsheet;
